import { AccountOwnerType, MembershipRole, WebhookMethod, WebhookExecutionStatus, validateAllFields, validateFieldValue } from '@explorins/pers-shared';
import { i as isTokenExpired, d as decodeJwtPayload, e as getTokenTimeToLive, E as ErrorUtils, A as AuthenticationError, b as PersApiError } from './index--OssIds0.js';
import { UserService, UserApi } from '../user.js';
import { createUserStatusSDK } from '../user-status.js';
import { a as TokenService, T as TokenApi } from './token-service-Ba9fp95X.js';
import { B as BusinessApi, b as BusinessService, a as BusinessMembershipApi, c as BusinessMembershipService } from './business-membership-service-C2CouLTR.js';
import { CampaignService, CampaignApi } from '../campaign.js';
import { a as RedemptionService, R as RedemptionApi } from './redemption-service-CQ73aUTe.js';
import { a as TransactionService, T as TransactionApi } from './transaction-request.builder-D6VLhaMi.js';
import { a as PaymentService, P as PurchaseApi } from './payment-service-IvM6rryM.js';
import { T as TenantManager } from './tenant-manager-xmYKBFGu.js';
import { b as buildPaginationParams, n as normalizeToPaginated } from './pagination-utils-9vQ-Npkr.js';
import { a as AnalyticsService, A as AnalyticsApi } from './analytics-service-CF7hSwhy.js';
import { DonationService, DonationApi } from '../donation.js';
import { TriggerSourceService, TriggerSourceApi } from '../trigger-source.js';

/**
 * PERS SDK Configuration interfaces and utilities
 *
 * Provides type-safe configuration options for the PERS SDK
 * with sensible defaults for production environments.
 */
/**
 * Default configuration values
 */
const DEFAULT_PERS_CONFIG = {
    environment: 'production',
    apiVersion: 'v2',
    timeout: 30000,
    retries: 3,
    tokenRefreshMargin: 60, // Refresh tokens 60 seconds before expiry
    backgroundRefreshThreshold: 30, // Use background refresh if >30s remaining
    captureWalletEvents: true, // Auto-connect to wallet events after auth
    autoRestoreSession: true // Auto-restore session from stored tokens on init
};
/**
 * Build the API root URL based on config
 *
 * Priority:
 * 1. customApiUrl (if provided)
 * 2. Environment-based URL (staging/production)
 *
 * @internal
 */
function buildApiRoot(environment = 'production', version = 'v2', customApiUrl) {
    // Custom URL takes priority
    if (customApiUrl) {
        return customApiUrl;
    }
    const baseUrls = {
        staging: `https://dev.api.pers.ninja/${version}`,
        production: `https://api.pers.ninja/${version}`
    };
    return baseUrls[environment];
}
/**
 * Build wallet events WebSocket URL based on config
 *
 * @internal
 */
function buildWalletEventsWsUrl(environment = 'production', customWsUrl) {
    if (customWsUrl) {
        return customWsUrl;
    }
    const wsUrls = {
        staging: 'wss://events-staging.pers.ninja',
        production: 'wss://events.pers.ninja'
    };
    return wsUrls[environment];
}
/**
 * Merge user config with defaults
 */
function mergeWithDefaults(config) {
    return {
        ...DEFAULT_PERS_CONFIG,
        ...config,
        environment: config.environment ?? DEFAULT_PERS_CONFIG.environment,
        apiVersion: config.apiVersion ?? DEFAULT_PERS_CONFIG.apiVersion,
        timeout: config.timeout ?? DEFAULT_PERS_CONFIG.timeout,
        retries: config.retries ?? DEFAULT_PERS_CONFIG.retries
    };
}

/**
 * Platform-Agnostic Auth API Client
 * Handles authentication operations using the PERS backend
 *
 * Supports the universal token endpoint:
 * - User authentication (default)
 * - Business authentication with context selection
 * - Admin/Tenant authentication
 */
class AuthApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/auth';
    }
    /**
     * Login tenant admin with JWT
     *
     * Authenticates an admin in a tenant context.
     *
     * @param jwt - Authentication token (Firebase JWT)
     * @param options - Tenant authentication options (tenantId for multi-tenant admins)
     * @returns Session response with tenant context
     * @throws MultipleContextSelectionError when tenantId is required but not provided
     *
     * @example
     * ```typescript
     * // Single tenant - auto-selects
     * const response = await authApi.loginTenantAdmin(jwt);
     *
     * // Multiple tenants - explicit selection
     * const response = await authApi.loginTenantAdmin(jwt, { tenantId: 'tenant-123' });
     * ```
     */
    async loginTenantAdmin(jwt, options) {
        const body = {
            authToken: jwt,
            authType: AccountOwnerType.TENANT,
            context: options?.tenantId ? { tenantId: options.tenantId } : undefined
        };
        return this.apiClient.post(`${this.basePath}/token`, body, { bypassAuth: true });
    }
    /**
     * Login user with JWT
     */
    async loginUser(jwt) {
        const body = {
            authToken: jwt,
            authType: AccountOwnerType.USER
        };
        return this.apiClient.post(`${this.basePath}/token`, body, { bypassAuth: true });
    }
    /**
     * Login as business with JWT
     *
     * Authenticates a user in a business context with role included in JWT.
     *
     * @param jwt - Authentication token (passkey or Firebase JWT)
     * @param options - Business authentication options (businessId for multi-business users, auto-selected if single membership)
     * @returns Session response with business context and role in JWT
     * @throws MultipleContextSelectionError when businessId is required but not provided
     *
     * @example
     * ```typescript
     * // Single business membership - auto-selects
     * const response = await authApi.loginBusiness(jwt);
     *
     * // Multiple memberships - explicit selection
     * const response = await authApi.loginBusiness(jwt, { businessId: 'biz-123' });
     * ```
     */
    async loginBusiness(jwt, options) {
        const body = {
            authToken: jwt,
            authType: AccountOwnerType.BUSINESS,
            context: options?.businessId ? { businessId: options.businessId } : undefined
        };
        return this.apiClient.post(`${this.basePath}/token`, body, { bypassAuth: true });
    }
    /**
     * Login user with raw data (no external authentication)
     */
    async loginUnAuthenticated(rawLoginData) {
        const body = {
            authToken: '',
            authType: AccountOwnerType.USER,
            rawLoginData
        };
        return this.apiClient.post(`${this.basePath}/token`, body, { bypassAuth: true });
    }
    /**
     * Refresh access token
     */
    async refreshAccessToken(refreshToken) {
        return this.apiClient.post(`${this.basePath}/refresh`, { refreshToken }, { bypassAuth: true, isRefreshRequest: true });
    }
    // ==========================================
    // IDENTITY VERIFICATION (for session restore)
    // Note: These duplicate calls in domain APIs (user-api, business-api)
    // but are kept here to avoid circular dependencies during initialization.
    // If endpoints change, update both locations.
    // ==========================================
    /**
     * Get current authenticated user
     */
    async getCurrentUser() {
        return this.apiClient.get('/users/me');
    }
    /**
     * Get current business context
     */
    async getCurrentBusiness() {
        return this.apiClient.get('/businesses/me');
    }
    /**
     * Get current admin context
     */
    async getCurrentAdmin() {
        return this.apiClient.get('/admins/me');
    }
}

/**
 * Authentication status enumeration
 * Represents the current state of the authentication system
 */
var AuthStatus;
(function (AuthStatus) {
    /** User is authenticated with valid tokens */
    AuthStatus["AUTHENTICATED"] = "authenticated";
    /** Access token has expired, refresh may be possible */
    AuthStatus["TOKEN_EXPIRED"] = "token_expired";
    /** Authentication system has failed, manual re-authentication required */
    AuthStatus["AUTH_FAILED"] = "auth_failed";
    /** Currently attempting to refresh tokens */
    AuthStatus["REFRESHING"] = "refreshing";
    /** No authentication present */
    AuthStatus["UNAUTHENTICATED"] = "unauthenticated";
    /** Session restoration completed (fired once on SDK initialization) */
    AuthStatus["SESSION_RESTORED"] = "session_restored";
    /** Session restoration determined no valid session exists */
    AuthStatus["SESSION_RESTORATION_FAILED"] = "session_restoration_failed";
})(AuthStatus || (AuthStatus = {}));

/**
 * Type guard to check if provider implements extended storage methods
 */
function isExtendedProvider(provider) {
    return 'setAuthType' in provider && typeof provider.setAuthType === 'function';
}
/**
 * Fatal auth error codes that require immediate logout.
 * These indicate the session is completely invalid and cannot be recovered.
 * TOKEN_EXPIRED is NOT in this list - it's the normal "please refresh" case.
 *
 * Values validated against CommonErrorCodes from @explorins/pers-shared at compile time.
 *
 * SINGLE SOURCE OF TRUTH - use this constant everywhere, never duplicate these values.
 */
const FATAL_AUTH_CODES = [
    'REFRESH_TOKEN_EXPIRED',
    'REFRESH_TOKEN_REVOKED',
    'INVALID_TOKEN',
    'TOKEN_REVOKED'
];
/**
 * Check if an error message contains any fatal auth error code.
 * Useful for detecting fatal errors in wrapped/stringified errors.
 */
function isFatalAuthErrorInMessage(message) {
    return FATAL_AUTH_CODES.some(code => message.includes(code));
}
/**
 * Platform-agnostic authentication service
 * Handles login, token refresh, and storage operations
 */
class AuthService {
    constructor(authApi, authProvider) {
        this.authApi = authApi;
        this.authProvider = authProvider;
        this.activeRefreshPromise = null;
        this.activeAuthFailurePromise = null;
        this.currentAuthStatus = AuthStatus.UNAUTHENTICATED;
    }
    /**
     * Get extended provider if available, null otherwise
     */
    get extendedProvider() {
        return this.authProvider && isExtendedProvider(this.authProvider) ? this.authProvider : null;
    }
    /**
     * Emit auth status change to the app
     */
    async emitAuthStatus(status) {
        if (this.currentAuthStatus === status)
            return; // No change
        this.currentAuthStatus = status;
        const extended = this.extendedProvider;
        if (extended?.onAuthStatusChange) {
            try {
                await extended.onAuthStatusChange(status);
            }
            catch (error) {
                console.warn('[AuthService] Auth status change callback failed:', error);
            }
        }
    }
    // ==========================================
    // AUTHENTICATION OPERATIONS
    // ==========================================
    /**
     * Login tenant admin with JWT
     *
     * @param jwt - Authentication token (Firebase JWT)
     * @param options - Tenant authentication options (tenantId for multi-tenant admins)
     * @returns Session response with tenant context
     * @throws Error with code MULTIPLE_CONTEXT_SELECTION_REQUIRED when tenantId is required but not provided
     */
    async loginTenantAdmin(jwt, options) {
        const response = await this.authApi.loginTenantAdmin(jwt, options);
        if (this.authProvider && response.accessToken) {
            await this.storeTokens(response.accessToken, response.refreshToken, AccountOwnerType.TENANT, jwt);
        }
        return response;
    }
    /**
     * Login user with JWT
     */
    async loginUser(jwt) {
        const response = await this.authApi.loginUser(jwt);
        if (this.authProvider && response.accessToken) {
            await this.storeTokens(response.accessToken, response.refreshToken, AccountOwnerType.USER, jwt);
        }
        return response;
    }
    /**
     * Login as business with JWT
     *
     * Authenticates a user in a business context. The returned JWT contains
     * the user's role within that business.
     *
     * @param jwt - Authentication token (passkey or Firebase JWT)
     * @param options - Business authentication options (businessId for multi-business users)
     * @returns Session response with business context and role baked into JWT
     * @throws MultipleContextSelectionRequiredError when businessId is required but not provided
     *
     * @example
     * ```typescript
     * // Auto-select if single membership
     * const response = await authService.loginBusiness(jwt);
     *
     * // Explicit business selection
     * const response = await authService.loginBusiness(jwt, { businessId: 'biz-123' });
     * console.log('Business:', response.business?.displayName);
     * ```
     */
    async loginBusiness(jwt, options) {
        const response = await this.authApi.loginBusiness(jwt, options);
        if (this.authProvider && response.accessToken) {
            await this.storeTokens(response.accessToken, response.refreshToken, AccountOwnerType.BUSINESS, jwt);
        }
        return response;
    }
    /**
     * Login user with raw data (no external auth)
     */
    async loginUserWithRawData(rawLoginData) {
        const response = await this.authApi.loginUnAuthenticated(rawLoginData);
        if (this.authProvider && response.accessToken) {
            await this.storeTokens(response.accessToken, response.refreshToken, AccountOwnerType.USER);
        }
        return response;
    }
    /**
     * Refresh access token with race condition protection
     */
    async refreshAccessToken(refreshToken) {
        if (this.currentAuthStatus === AuthStatus.AUTH_FAILED) {
            throw new Error('Auth failure has occurred, cannot refresh token');
        }
        if (this.activeRefreshPromise) {
            return await this.activeRefreshPromise;
        }
        // Create the refresh promise
        this.activeRefreshPromise = this.performRefresh(refreshToken);
        try {
            return await this.activeRefreshPromise;
        }
        finally {
            this.activeRefreshPromise = null;
        }
    }
    /**
     * Performs the actual refresh operation
     */
    async performRefresh(refreshToken) {
        const tokenToUse = refreshToken || (this.authProvider ? await this.authProvider.getRefreshToken() : null);
        if (!tokenToUse) {
            throw new Error('No refresh token available for token refresh');
        }
        // Get the current authType to preserve it after refresh
        const extended = this.extendedProvider;
        const currentAuthType = extended?.getAuthType ? await extended.getAuthType() ?? undefined : undefined;
        const response = await this.authApi.refreshAccessToken(tokenToUse);
        if (this.authProvider && response.accessToken) {
            // Preserve the authType during refresh
            await this.storeTokens(response.accessToken, response.refreshToken, currentAuthType);
        }
        return response;
    }
    /**
     * Clear stored tokens and active refresh operations
     */
    async clearTokens() {
        this.activeRefreshPromise = null;
        if (this.authProvider) {
            await this.authProvider.clearTokens();
        }
    }
    /**
     * Check if an error code indicates a fatal auth failure.
     * Fatal errors require immediate logout - the session cannot be recovered.
     */
    isFatalAuthError(errorCode) {
        return FATAL_AUTH_CODES.includes(errorCode);
    }
    /**
     * Comprehensive check if an error represents a DEFINITIVE auth failure.
     * This is the SINGLE SOURCE OF TRUTH for determining if logout is required.
     *
     * Returns true for:
     * - Known fatal error codes (REFRESH_TOKEN_EXPIRED, TOKEN_REVOKED, etc.)
     * - Fatal error codes in error messages (wrapped errors)
     *
     * Returns false for (retryable/recoverable):
     * - Network errors
     * - Timeouts
     * - 5xx server errors
     * - TOKEN_EXPIRED (normal refresh trigger)
     * - Raw 401/403 without fatal code (should try refresh first)
     *
     * NOTE: We intentionally DO NOT treat raw 401/403 status as fatal.
     * The HTTP client handles 401 by attempting token refresh first.
     * Only if refresh fails WITH a fatal code do we logout.
     */
    isDefinitiveAuthFailure(error) {
        if (!error)
            return false;
        const errorAny = error;
        // Check error code property against canonical list
        if (errorAny?.code && this.isFatalAuthError(errorAny.code)) {
            return true;
        }
        // Check if error message contains fatal keywords (fallback for wrapped errors)
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (isFatalAuthErrorInMessage(errorMessage)) {
            return true;
        }
        // NOTE: We intentionally DO NOT check HTTP status codes here.
        // A raw 401/403 without a fatal error code should trigger a refresh attempt,
        // not an immediate logout. The HTTP client (pers-api-client.ts) handles this:
        // 1. 401 received → try refresh token
        // 2. Refresh succeeds → retry original request
        // 3. Refresh fails with fatal CODE → then logout
        // Everything else is considered retryable
        return false;
    }
    /**
     * Handle authentication failure with provider token recovery.
     *
     * Before logging out, attempts to recover the session using the stored
     * provider token (Firebase JWT, etc.) if it's still valid.
     *
     * Recovery Flow:
     * 1. Check if provider token exists and is not expired
     * 2. Get the auth type (USER, BUSINESS, TENANT) to know which login to use
     * 3. Attempt re-authentication with provider token
     * 4. If successful: Session recovered, stay logged in
     * 5. If failed: Clear tokens and emit AUTH_FAILED
     */
    async handleAuthFailure() {
        // Already failed - nothing to do
        if (this.currentAuthStatus === AuthStatus.AUTH_FAILED) {
            return;
        }
        // If refresh is currently in progress, don't fail - wait for refresh result
        if (this.activeRefreshPromise) {
            return;
        }
        // If auth failure is already in progress, wait for it
        if (this.activeAuthFailurePromise) {
            return await this.activeAuthFailurePromise;
        }
        // Create the failure promise to deduplicate concurrent calls
        this.activeAuthFailurePromise = this.performAuthFailureWithRecovery();
        try {
            await this.activeAuthFailurePromise;
        }
        finally {
            this.activeAuthFailurePromise = null;
        }
    }
    /**
     * Attempts to recover session using provider token before failing.
     */
    async performAuthFailureWithRecovery() {
        // Clear active refresh operations
        this.activeRefreshPromise = null;
        // Try to recover using provider token
        const recovered = await this.attemptProviderTokenRecovery();
        if (recovered) {
            return; // Session recovered, don't fail
        }
        // Recovery failed - perform actual logout
        await this.performAuthFailure();
    }
    /**
     * Attempt to recover session using stored provider token.
     * Extracts context (tenantId/businessId) from the current refresh token to ensure
     * the recovered session maintains the same context.
     *
     * This is a PUBLIC method that can be called by TokenRefreshManager when internal
     * refresh fails, to try external (provider token) recovery before giving up.
     *
     * @returns true if session was successfully recovered
     */
    async attemptProviderTokenRecovery() {
        const extended = this.extendedProvider;
        if (!extended?.getProviderToken || !extended?.getAuthType) {
            return false; // Provider doesn't support recovery
        }
        try {
            const providerToken = await extended.getProviderToken();
            if (!providerToken) {
                return false;
            }
            // Check if provider token is still valid (with small margin)
            if (isTokenExpired(providerToken, 30)) {
                return false;
            }
            const authType = await extended.getAuthType();
            if (!authType) {
                return false;
            }
            // Extract context (tenantId/businessId) from the current refresh token
            // This ensures multi-tenant/multi-business users recover to the same context
            const refreshToken = await this.authProvider?.getRefreshToken?.();
            const contextClaims = refreshToken ? decodeJwtPayload(refreshToken) : null;
            // Re-authenticate using provider token based on auth type
            // Pass the context from the previous session to avoid MULTIPLE_CONTEXT_SELECTION_REQUIRED
            // Note: In AuthJWTPayload, when accountType=BUSINESS, sub contains the businessId
            switch (authType) {
                case AccountOwnerType.USER:
                    await this.loginUser(providerToken);
                    break;
                case AccountOwnerType.BUSINESS:
                    // For BUSINESS auth, sub contains the businessId when accountType is BUSINESS
                    const businessId = contextClaims?.accountType === AccountOwnerType.BUSINESS
                        ? contextClaims.sub
                        : undefined;
                    await this.loginBusiness(providerToken, businessId ? { businessId } : undefined);
                    break;
                case AccountOwnerType.TENANT:
                    await this.loginTenantAdmin(providerToken, contextClaims?.tenantId ? { tenantId: contextClaims.tenantId } : undefined);
                    break;
                default:
                    console.warn(`[AuthService] Unknown auth type for recovery: ${authType}`);
                    return false;
            }
            return true; // Recovery successful
        }
        catch (error) {
            console.warn('[AuthService] Provider token recovery failed:', error);
            return false;
        }
    }
    /**
     * Performs the actual auth failure cleanup
     */
    async performAuthFailure() {
        // Clear active refresh operations to prevent hanging promises
        this.activeRefreshPromise = null;
        // Clear all stored tokens
        await this.clearTokens();
        // Emit auth failure status to notify the app
        await this.emitAuthStatus(AuthStatus.AUTH_FAILED);
    }
    /**
     * Check if we have valid authentication tokens
     */
    async hasValidAuth() {
        if (!this.authProvider)
            return false;
        const token = await this.authProvider.getToken();
        return !!token;
    }
    // ==========================================
    // CONTEXT RETRIEVAL
    // ==========================================
    /**
     * Get current authenticated user
     */
    async getCurrentUser() {
        return this.authApi.getCurrentUser();
    }
    /**
     * Get current business context
     */
    async getCurrentBusiness() {
        return this.authApi.getCurrentBusiness();
    }
    /**
     * Get current admin context
     */
    async getCurrentAdmin() {
        return this.authApi.getCurrentAdmin();
    }
    // ==========================================
    // PRIVATE HELPERS
    // ==========================================
    /**
     * Store tokens using auth provider
     */
    async storeTokens(accessToken, refreshToken, authType, providerToken) {
        if (!this.authProvider)
            return;
        try {
            await this.authProvider.setAccessToken(accessToken);
            if (refreshToken) {
                await this.authProvider.setRefreshToken(refreshToken);
            }
            // Handle optional extended storage methods
            const extended = this.extendedProvider;
            if (extended) {
                if (providerToken && extended.setProviderToken) {
                    await extended.setProviderToken(providerToken);
                }
                if (authType && extended.setAuthType) {
                    await extended.setAuthType(authType);
                }
            }
            else if (authType) {
                console.warn('[AuthService] Auth type not stored - provider does not implement setAuthType');
            }
            // Emit authenticated status on successful token storage
            await this.emitAuthStatus(AuthStatus.AUTHENTICATED);
        }
        catch (error) {
            console.warn('[AuthService] Failed to store tokens:', error);
        }
    }
}

/**
 * Token Refresh Manager
 * Industry-standard token lifecycle management with:
 * - Dynamic refresh margin based on token lifetime
 * - Retry logic for transient failures
 * - Only logout on definitive auth failures (not network errors)
 */
class TokenRefreshManager {
    constructor(authService, authProvider) {
        this.authService = authService;
        this.authProvider = authProvider;
        // Industry standard: refresh when 25% of token lifetime remains
        this.REFRESH_THRESHOLD_PERCENT = 0.25;
        // Minimum margin regardless of token lifetime
        this.MIN_REFRESH_MARGIN_SECONDS = 30;
        // Maximum margin (cap for very long-lived tokens)
        this.MAX_REFRESH_MARGIN_SECONDS = 300; // 5 minutes
        // Validation cache to avoid redundant checks
        this.lastValidationTime = 0;
        this.validationCacheDurationMs = 30000; // 30 seconds
        // Retry configuration for transient failures
        this.MAX_RETRY_ATTEMPTS = 3;
        this.RETRY_DELAY_MS = 1000;
        // Active refresh promise for deduplication
        this.activeRefreshPromise = null;
    }
    /**
     * Ensures the access token is valid, refreshing if needed.
     * Uses dynamic margin based on token lifetime (industry standard).
     */
    async ensureValidToken() {
        const now = Date.now();
        // Skip validation if we checked recently (within cache duration)
        if (now - this.lastValidationTime < this.validationCacheDurationMs) {
            return;
        }
        try {
            const token = await this.authProvider.getToken();
            if (!token) {
                return;
            }
            // Calculate dynamic refresh margin based on token lifetime
            const marginSeconds = this.calculateRefreshMargin(token);
            if (isTokenExpired(token, marginSeconds)) {
                const result = await this.attemptRefreshWithRetry();
                if (!result.success) {
                    if (result.retryable) {
                        // Transient error - don't logout, let next request retry
                        console.warn('[TokenRefreshManager] Refresh failed with retryable error, will retry on next request');
                        return;
                    }
                    // Definitive auth failure - logout required
                    await this.authService.handleAuthFailure();
                }
                else {
                    // Refresh successful
                    this.lastValidationTime = now;
                }
            }
            else {
                // Token is valid
                this.lastValidationTime = now;
            }
        }
        catch (error) {
            // Unexpected error - log but don't logout (conservative approach)
            console.error('[TokenRefreshManager] Unexpected error during token validation:', error);
        }
    }
    /**
     * Calculate refresh margin dynamically based on token lifetime.
     * Industry standard: refresh when 25% of lifetime remains.
     */
    calculateRefreshMargin(token) {
        const ttlSeconds = getTokenTimeToLive(token);
        if (ttlSeconds === null || ttlSeconds <= 0) {
            // Can't determine TTL, use minimum margin
            return this.MIN_REFRESH_MARGIN_SECONDS;
        }
        // Calculate 25% of remaining lifetime
        const dynamicMargin = Math.floor(ttlSeconds * this.REFRESH_THRESHOLD_PERCENT);
        // Clamp between min and max
        return Math.max(this.MIN_REFRESH_MARGIN_SECONDS, Math.min(dynamicMargin, this.MAX_REFRESH_MARGIN_SECONDS));
    }
    /**
     * Attempt token refresh with retry logic for transient failures.
     * Only gives up on definitive auth errors.
     */
    async attemptRefreshWithRetry() {
        let lastError;
        for (let attempt = 1; attempt <= this.MAX_RETRY_ATTEMPTS; attempt++) {
            try {
                await this.authService.refreshAccessToken();
                return { success: true, retryable: false };
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                // Check if this is a definitive auth failure (non-retryable)
                if (this.isDefinitiveAuthFailure(error)) {
                    console.warn(`[TokenRefreshManager] Definitive auth failure: ${lastError.message}`);
                    return { success: false, retryable: false, error: lastError };
                }
                // Retryable error - wait and try again
                if (attempt < this.MAX_RETRY_ATTEMPTS) {
                    await this.delay(this.RETRY_DELAY_MS * attempt); // Exponential backoff
                }
            }
        }
        // All retries exhausted - return as retryable so we don't logout
        console.warn(`[TokenRefreshManager] All ${this.MAX_RETRY_ATTEMPTS} refresh attempts failed`);
        return { success: false, retryable: true, error: lastError };
    }
    /**
     * Determine if an error is a definitive auth failure that requires logout.
     * Network errors, timeouts, 5xx errors are NOT definitive - they're retryable.
     *
     * Delegates to AuthService.isDefinitiveAuthFailure() - the SINGLE SOURCE OF TRUTH.
     */
    isDefinitiveAuthFailure(error) {
        return this.authService.isDefinitiveAuthFailure(error);
    }
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * Attempt token refresh: internal first, then provider token fallback.
     * DEDUPLICATES concurrent calls - all callers share the same promise.
     */
    async attemptInternalRefresh() {
        if (this.activeRefreshPromise) {
            return this.activeRefreshPromise;
        }
        this.activeRefreshPromise = (async () => {
            const result = await this.attemptRefreshWithRetry();
            if (result.success)
                return result;
            // Internal failed - try provider token recovery
            const recovered = await this.authService.attemptProviderTokenRecovery();
            return recovered ? { success: true, retryable: false } : result;
        })();
        try {
            return await this.activeRefreshPromise;
        }
        finally {
            this.activeRefreshPromise = null;
        }
    }
}

/**
 * Token Storage System
 * Simple, focused token storage with clear separation of concerns
 */
const AUTH_STORAGE_KEYS = {
    ACCESS_TOKEN: 'pers_access_token',
    REFRESH_TOKEN: 'pers_refresh_token',
    PROVIDER_TOKEN: 'pers_provider_token',
    AUTH_TYPE: 'pers_auth_type',
};
/**
 * LocalStorage implementation
 * Note: Forces string conversion for compatibility
 */
class LocalStorageTokenStorage {
    constructor() {
        this.supportsObjects = false;
    }
    async get(key) {
        return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    }
    async set(key, value) {
        if (typeof localStorage !== 'undefined') {
            const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
            localStorage.setItem(key, stringValue);
        }
    }
    async remove(key) {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(key);
        }
    }
    async clear() {
        if (typeof localStorage !== 'undefined') {
            Object.values(AUTH_STORAGE_KEYS).forEach(key => {
                localStorage.removeItem(key);
            });
        }
    }
}
/**
 * Memory storage implementation
 */
class MemoryTokenStorage {
    constructor() {
        this.supportsObjects = true;
        this.store = new Map();
    }
    async get(key) {
        return this.store.get(key) || null;
    }
    async set(key, value) {
        this.store.set(key, value);
    }
    async remove(key) {
        this.store.delete(key);
    }
    async clear() {
        this.store.clear();
    }
}
/**
 * Token manager for authentication tokens with memory caching
 */
class AuthTokenManager {
    constructor(storage = new LocalStorageTokenStorage()) {
        this.storage = storage;
        this.cache = {};
    }
    async getAccessToken() {
        // Return cached value if available
        if (this.cache.accessToken !== undefined) {
            return this.cache.accessToken;
        }
        const val = await this.storage.get(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
        // Ensure we return string for tokens
        const token = typeof val === 'string' ? val : null;
        this.cache.accessToken = token;
        return token;
    }
    async setAccessToken(token) {
        this.cache.accessToken = token;
        await this.storage.set(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
    }
    async getRefreshToken() {
        // Return cached value if available
        if (this.cache.refreshToken !== undefined) {
            return this.cache.refreshToken;
        }
        const val = await this.storage.get(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
        const token = typeof val === 'string' ? val : null;
        this.cache.refreshToken = token;
        return token;
    }
    async setRefreshToken(token) {
        this.cache.refreshToken = token;
        await this.storage.set(AUTH_STORAGE_KEYS.REFRESH_TOKEN, token);
    }
    async getProviderToken() {
        // Return cached value if available
        if (this.cache.providerToken !== undefined) {
            return this.cache.providerToken;
        }
        const val = await this.storage.get(AUTH_STORAGE_KEYS.PROVIDER_TOKEN);
        const token = typeof val === 'string' ? val : null;
        this.cache.providerToken = token;
        return token;
    }
    async setProviderToken(token) {
        this.cache.providerToken = token;
        await this.storage.set(AUTH_STORAGE_KEYS.PROVIDER_TOKEN, token);
    }
    async getAuthType() {
        // Return cached value if available
        if (this.cache.authType !== undefined) {
            return this.cache.authType;
        }
        const authType = await this.storage.get(AUTH_STORAGE_KEYS.AUTH_TYPE);
        const type = typeof authType === 'string' ? authType : null;
        this.cache.authType = type;
        return type;
    }
    async setAuthType(authType) {
        this.cache.authType = authType;
        await this.storage.set(AUTH_STORAGE_KEYS.AUTH_TYPE, authType);
    }
    async setTokens(accessToken, refreshToken, providerToken) {
        // Update cache for all tokens
        this.cache.accessToken = accessToken;
        if (refreshToken)
            this.cache.refreshToken = refreshToken;
        if (providerToken)
            this.cache.providerToken = providerToken;
        await this.setAccessToken(accessToken);
        if (refreshToken)
            await this.setRefreshToken(refreshToken);
        if (providerToken)
            await this.setProviderToken(providerToken);
    }
    async clearAllTokens() {
        // Clear cache
        this.cache = {};
        await this.storage.clear();
    }
    /**
     * Clears only auth tokens (access, refresh, provider, authType), preserving DPoP keys.
     * Use this when DPoP keys are regenerated - we want to invalidate auth tokens
     * that were bound to old keys, but keep the newly generated DPoP keys.
     */
    async clearAuthTokens() {
        // Clear cache
        this.cache = {};
        // Remove all auth tokens and auth type, NOT DPoP keys
        await Promise.all([
            this.storage.remove(AUTH_STORAGE_KEYS.ACCESS_TOKEN),
            this.storage.remove(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
            this.storage.remove(AUTH_STORAGE_KEYS.PROVIDER_TOKEN),
            this.storage.remove(AUTH_STORAGE_KEYS.AUTH_TYPE),
        ]);
    }
    async hasAccessToken() {
        // Use cached value if available to avoid storage read
        if (this.cache.accessToken !== undefined) {
            return !!this.cache.accessToken;
        }
        const token = await this.getAccessToken();
        return !!token;
    }
    async hasRefreshToken() {
        // Use cached value if available to avoid storage read
        if (this.cache.refreshToken !== undefined) {
            return !!this.cache.refreshToken;
        }
        const token = await this.getRefreshToken();
        return !!token;
    }
    /**
     * Clears only the refresh token.
     * Used when DPoP keys are regenerated, invalidating the token binding.
     */
    async clearRefreshToken() {
        this.cache.refreshToken = null;
        await this.storage.remove(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    }
}

class IndexedDBTokenStorage {
    constructor() {
        this.supportsObjects = true;
        this.dbPromise = null;
        // IndexedDB availability is checked lazily on first operation
    }
    getDB() {
        if (this.dbPromise) {
            return this.dbPromise;
        }
        this.dbPromise = new Promise((resolve, reject) => {
            if (typeof indexedDB === 'undefined') {
                return reject(new Error('IndexedDB not supported'));
            }
            const request = indexedDB.open(IndexedDBTokenStorage.DB_NAME, IndexedDBTokenStorage.DB_VERSION);
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(IndexedDBTokenStorage.STORE_NAME)) {
                    db.createObjectStore(IndexedDBTokenStorage.STORE_NAME);
                }
            };
            request.onsuccess = (event) => {
                resolve(event.target.result);
            };
            request.onerror = (event) => {
                reject(event.target.error);
            };
        });
        return this.dbPromise;
    }
    async get(key) {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(IndexedDBTokenStorage.STORE_NAME, 'readonly');
                const store = transaction.objectStore(IndexedDBTokenStorage.STORE_NAME);
                const request = store.get(key);
                request.onsuccess = () => {
                    resolve(request.result || null);
                };
                request.onerror = () => {
                    reject(request.error);
                };
            });
        }
        catch (e) {
            console.warn('[IndexedDBTokenStorage] Failed to get key', key, e);
            return null;
        }
    }
    async set(key, value) {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(IndexedDBTokenStorage.STORE_NAME, 'readwrite');
                const store = transaction.objectStore(IndexedDBTokenStorage.STORE_NAME);
                const request = store.put(value, key);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
        catch (e) {
            console.error('[IndexedDBTokenStorage] Failed to set key', key, e);
            throw e;
        }
    }
    async remove(key) {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(IndexedDBTokenStorage.STORE_NAME, 'readwrite');
                const store = transaction.objectStore(IndexedDBTokenStorage.STORE_NAME);
                const request = store.delete(key);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
        catch (e) {
            console.warn('[IndexedDBTokenStorage] Failed to remove key', key, e);
        }
    }
    async clear() {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(IndexedDBTokenStorage.STORE_NAME, 'readwrite');
                const store = transaction.objectStore(IndexedDBTokenStorage.STORE_NAME);
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        }
        catch (e) {
            console.warn('[IndexedDBTokenStorage] Failed to clear storage', e);
        }
    }
}
IndexedDBTokenStorage.DB_NAME = 'pers-sdk-storage';
IndexedDBTokenStorage.STORE_NAME = 'auth-tokens';
IndexedDBTokenStorage.DB_VERSION = 1;

class WebDPoPCryptoProvider {
    get crypto() {
        // Basic environment check
        if (typeof crypto !== 'undefined')
            return crypto;
        if (typeof window !== 'undefined' && window.crypto)
            return window.crypto;
        if (typeof globalThis !== 'undefined' && globalThis.crypto)
            return globalThis.crypto;
        throw new Error('WebCrypto API not found. Please polyfill crypto.subtle or use a different DPoPCryptoProvider.');
    }
    async generateKeyPair(options) {
        const extractable = options?.extractable ?? true;
        const keyPair = await this.crypto.subtle.generateKey({
            name: 'ECDSA',
            namedCurve: 'P-256',
        }, extractable, // Configurable extractability
        ['sign', 'verify']);
        const publicKey = await this.crypto.subtle.exportKey('jwk', keyPair.publicKey);
        // If extractable, export private key as JWK (plain object)
        // If NOT extractable, keep it as CryptoKey (native handle)
        // However, DPoPKeyPair interface currently expects JsonWebKey | CryptoKey
        // We need to check DPoPKeyPair type definition. 
        // Wait, DPoPKeyPair definition in dpop.types.ts says: 
        // publicKey: JsonWebKey; privateKey: JsonWebKey;
        // I need to change that to support CryptoKey.
        // Let's re-read dpop.types.ts
        // I assumed it might be compatible but let's verify.
        // If I change privateKey to unknown or JsonWebKey | CryptoKey, it will be safer.
        let privateKey;
        if (extractable) {
            privateKey = await this.crypto.subtle.exportKey('jwk', keyPair.privateKey);
        }
        else {
            privateKey = keyPair.privateKey;
        }
        return { publicKey, privateKey };
    }
    async signProof(payload, keyPair) {
        // 1. Prepare Header
        const header = {
            typ: 'dpop+jwt',
            alg: 'ES256',
            jwk: keyPair.publicKey
        };
        // 2. Prepare Payload
        // Ensure jti and iat if not present (though Manager usually provides them)
        const finalPayload = {
            ...payload,
            iat: payload.iat || Math.floor(Date.now() / 1000),
            jti: payload.jti || this.generateUUID()
        };
        // 3. Encode segments
        const encodedHeader = this.base64UrlEncode(JSON.stringify(header));
        const encodedPayload = this.base64UrlEncode(JSON.stringify(finalPayload));
        const signingInput = `${encodedHeader}.${encodedPayload}`;
        // 4. Import private key for signing
        let privateKey;
        // Check if it's already a CryptoKey (non-extractable handle)
        // We check for 'algorithm' property which signals a CryptoKey object
        if (keyPair.privateKey.algorithm) {
            privateKey = keyPair.privateKey;
        }
        else {
            // Otherwise import from JWK
            privateKey = await this.crypto.subtle.importKey('jwk', keyPair.privateKey, {
                name: 'ECDSA',
                namedCurve: 'P-256',
            }, false, ['sign']);
        }
        // 5. Sign
        const signatureBuffer = await this.crypto.subtle.sign({
            name: 'ECDSA',
            hash: { name: 'SHA-256' },
        }, privateKey, new TextEncoder().encode(signingInput));
        const encodedSignature = this.base64UrlEncodeBuffer(signatureBuffer);
        return `${signingInput}.${encodedSignature}`;
    }
    async hashAccessToken(accessToken) {
        const encoder = new TextEncoder();
        const data = encoder.encode(accessToken);
        const hashBuffer = await this.crypto.subtle.digest('SHA-256', data);
        return this.base64UrlEncodeBuffer(hashBuffer);
    }
    // --- Helpers ---
    generateUUID() {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback for older envs
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
    base64UrlEncode(str) {
        // Use TextEncoder to handle UTF-8 properly
        const encoder = new TextEncoder();
        return this.base64UrlEncodeBuffer(encoder.encode(str));
    }
    base64UrlEncodeBuffer(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        const len = bytes.byteLength;
        // Chunk processing for large buffers if needed, but proofs are small
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        // btoa is widely available in browsers.
        // In Node (non-globals), we might need Buffer.
        // For universal 'platform agnostic' aiming at standard web-like envs:
        let base64 = '';
        if (typeof btoa === 'function') {
            base64 = btoa(binary);
        }
        else if (typeof Buffer !== 'undefined') {
            base64 = Buffer.from(binary, 'binary').toString('base64');
        }
        else {
            throw new Error('Base64 encoding not supported in this environment');
        }
        return base64
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }
}

const DPOP_STORAGE_KEYS = {
    PUBLIC: 'pers_dpop_public_key',
    PRIVATE: 'pers_dpop_private_key'
};
class DPoPManager {
    constructor(storage, cryptoProvider, callbacks) {
        this.storage = storage;
        this.memoryKeyPair = null;
        /** Pending key pair promise to prevent concurrent generation race conditions */
        this.pendingKeyPair = null;
        this.cryptoProvider = cryptoProvider || new WebDPoPCryptoProvider();
        this.callbacks = callbacks || {};
    }
    /**
     * Checks if DPoP keys exist in storage without generating new ones.
     * Useful for detecting if a key regeneration would be needed.
     */
    async hasStoredKeys() {
        if (this.memoryKeyPair) {
            return true;
        }
        const storedPublic = await this.storage.get(DPOP_STORAGE_KEYS.PUBLIC);
        const storedPrivate = await this.storage.get(DPOP_STORAGE_KEYS.PRIVATE);
        return !!(storedPublic && storedPrivate);
    }
    /**
     * Ensures a DPoP key pair exists, loading from storage or generating new one.
     *
     * IMPORTANT: If keys are regenerated (not loaded from storage), this will
     * invalidate any refresh tokens bound to the old keys. The onKeysRegenerated
     * callback should clear the refresh token to prevent "DPoP proof does not
     * match refresh token binding" errors.
     */
    async ensureKeyPair() {
        // Fast path: already have keys in memory
        if (this.memoryKeyPair) {
            return this.memoryKeyPair;
        }
        // Prevent concurrent key generation race condition:
        // If another call is already generating keys, wait for it instead of starting a new one
        if (this.pendingKeyPair) {
            return this.pendingKeyPair;
        }
        // Create a promise that subsequent callers will wait on
        this.pendingKeyPair = this.loadOrGenerateKeyPair();
        try {
            const keyPair = await this.pendingKeyPair;
            return keyPair;
        }
        finally {
            // Clear pending promise so future calls can proceed
            this.pendingKeyPair = null;
        }
    }
    /**
     * Internal method that actually loads or generates keys.
     * Called only once per concurrent batch of ensureKeyPair() calls.
     */
    async loadOrGenerateKeyPair() {
        // Double-check in case keys were set while waiting
        if (this.memoryKeyPair) {
            return this.memoryKeyPair;
        }
        const storedPublic = await this.storage.get(DPOP_STORAGE_KEYS.PUBLIC);
        const storedPrivate = await this.storage.get(DPOP_STORAGE_KEYS.PRIVATE);
        if (storedPublic && storedPrivate) {
            try {
                // Handle polymorphic storage: String (JSON) or Object (CryptoKey)
                const publicKey = typeof storedPublic === 'string' ? JSON.parse(storedPublic) : storedPublic;
                const privateKey = typeof storedPrivate === 'string' ? JSON.parse(storedPrivate) : storedPrivate;
                this.memoryKeyPair = { publicKey, privateKey };
                return this.memoryKeyPair;
            }
            catch (e) {
                console.warn('[DPoPManager] Corrupted DPoP keys in storage, regenerating');
            }
        }
        // Generate new key pair
        // IMPORTANT: This invalidates any existing refresh tokens bound to the old keys
        // Adaptation: If storage supports raw objects (like Native Keychain on iOS/Android),
        // we can generate Non-Extractable keys for maximum security.
        // If storage is text-only (LocalStorage) OR IndexedDB (CryptoKey doesn't survive reload),
        // we must use Extractable keys to serialize them as JWK.
        // 
        // NOTE: IndexedDB CAN store CryptoKey objects, but they don't survive page reloads -
        // they come back as empty/corrupted objects. Only native secure storage can persist
        // non-extractable keys. Frontend should set supportsObjects: false for web storage.
        const useHighSecurity = !!this.storage.supportsObjects;
        const extractable = !useHighSecurity;
        const keyPair = await this.cryptoProvider.generateKeyPair({
            extractable
        });
        // Save to storage
        await this.storage.set(DPOP_STORAGE_KEYS.PUBLIC, keyPair.publicKey);
        await this.storage.set(DPOP_STORAGE_KEYS.PRIVATE, keyPair.privateKey);
        this.memoryKeyPair = keyPair;
        // Notify that keys were regenerated - refresh tokens bound to old keys are now invalid
        if (this.callbacks.onKeysRegenerated) {
            try {
                this.callbacks.onKeysRegenerated();
            }
            catch (e) {
                console.warn('[DPoPManager] onKeysRegenerated callback failed:', e);
            }
        }
        return keyPair;
    }
    /**
     * Generates the DPoP proof header value.
     * @param method HTTP method
     * @param url Request URL
     * @param accessToken Optional access token to bind the proof to (ath claim)
     */
    async generateProofHeader(method, url, accessToken) {
        const keyPair = await this.ensureKeyPair();
        const payload = {
            htm: method,
            htu: url,
        };
        if (accessToken) {
            payload.ath = await this.cryptoProvider.hashAccessToken(accessToken);
        }
        return this.cryptoProvider.signProof(payload, keyPair);
    }
    /**
     * Generate a short fingerprint of the public key for debugging
     * This helps identify if the same key is being used across operations
     */
    /* private getPublicKeyFingerprint(publicKey: JsonWebKey): string {
      try {
        // Use the 'x' coordinate of the EC key as a fingerprint
        const x = publicKey.x || '';
        const y = publicKey.y || '';
        return `${x.substring(0, 8)}...${y.substring(0, 4)}`;
      } catch {
        return 'unknown';
      }
    } */
    /**
     * Clears DPoP keys (e.g. on logout)
     */
    async clearKeys() {
        this.memoryKeyPair = null;
        await this.storage.remove(DPOP_STORAGE_KEYS.PUBLIC);
        await this.storage.remove(DPOP_STORAGE_KEYS.PRIVATE);
    }
    /**
     * Gets the public key (for debugging/inspection)
     */
    async getPublicKey() {
        try {
            const kp = await this.ensureKeyPair();
            return kp.publicKey;
        }
        catch {
            return null;
        }
    }
}

/**
 * Default Authentication Provider
 * Simple implementation of PERS authentication with token lifecycle management
 */
/**
 * Simple authentication provider with token storage
 */
class DefaultAuthProvider {
    constructor(config = {}) {
        this.config = config;
        this.authTypeCache = null;
        this.authType = config.authType || AccountOwnerType.USER;
        // Determine if DPoP can be enabled:
        // 1. Custom crypto provider is supplied (e.g., React Native), OR
        // 2. IndexedDB is available for browser CryptoKey persistence
        const hasCustomCryptoProvider = !!config.dpop?.cryptoProvider;
        const hasIndexedDB = this.canEnableDPoP();
        const canUseDPoP = hasCustomCryptoProvider || hasIndexedDB;
        this.dpopEnabled = !!(config.dpop?.enabled && canUseDPoP);
        if (config.dpop?.enabled && !canUseDPoP) {
            console.warn('[DefaultAuthProvider] DPoP disabled: IndexedDB not available and no custom crypto provider supplied. CryptoKey persistence requires IndexedDB or a custom provider.');
        }
        const storage = config.storage || this.createStorage();
        this.tokenManager = new AuthTokenManager(storage);
        if (this.dpopEnabled) {
            this.dpopManager = new DPoPManager(storage, config.dpop?.cryptoProvider, {
                // When DPoP keys are regenerated (corrupted/missing), auth tokens
                // bound to the old keys become invalid. Clear only auth tokens, NOT DPoP keys.
                // Access token has cnf claim binding it to the old public key.
                // Refresh token has DPoP binding to the old key pair.
                // NOTE: Do NOT call clearAllTokens() here - it would wipe the freshly saved DPoP keys!
                onKeysRegenerated: () => {
                    // Clear only auth tokens, preserve DPoP keys (they were just regenerated and saved)
                    this.tokenManager.clearAuthTokens().catch(err => {
                        console.warn('[DefaultAuthProvider] Failed to clear tokens after key regeneration:', err);
                    });
                }
            });
        }
    }
    canEnableDPoP() {
        // DPoP requires IndexedDB for proper CryptoKey persistence
        // LocalStorage cannot serialize CryptoKey objects correctly
        return typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
    }
    createStorage() {
        // Server-side: use memory storage
        if (typeof window === 'undefined') {
            return new MemoryTokenStorage();
        }
        // Browser with DPoP enabled: use IndexedDB for CryptoKey persistence
        if (this.dpopEnabled) {
            return new IndexedDBTokenStorage();
        }
        // Browser without DPoP: use LocalStorage
        if (typeof localStorage !== 'undefined') {
            return new LocalStorageTokenStorage();
        }
        // Fallback
        return new MemoryTokenStorage();
    }
    getToken() {
        return this.tokenManager.getAccessToken();
    }
    async getAuthHeaders(token, method, url) {
        const headers = {};
        if (this.dpopManager && method && url) {
            // Generate DPoP Proof
            // Note: For 'bypassAuth' requests (token is null), we still generate proof (bound to public key, no ath)
            // This covers Token Requests (Login) which need DPoP header to bind the issued token.
            const proof = await this.dpopManager.generateProofHeader(method, url, token || undefined);
            headers['DPoP'] = proof;
            if (token) {
                headers['Authorization'] = `DPoP ${token}`;
            }
        }
        else {
            // Standard Bearer
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }
        return headers;
    }
    async getProjectKey() {
        return this.config.projectKey || null;
    }
    async onAuthStatusChange(status) {
        try {
            // Handle provider-level responsibilities first
            if (status === AuthStatus.TOKEN_EXPIRED && this.config.tokenProvider) {
                const newToken = await this.config.tokenProvider();
                if (newToken) {
                    await this.tokenManager.setAccessToken(newToken);
                    // Don't return - still notify the app about the status
                }
            }
            // Then delegate to config callback if available
            if (this.config.onAuthStatusChange) {
                await this.config.onAuthStatusChange(status);
            }
        }
        catch (error) {
            // Re-throw error to let caller (AuthService) handle cleanup
            throw error;
        }
    }
    async setAccessToken(token) {
        await this.tokenManager.setAccessToken(token);
        // Invalidate cache when token changes
        this.authTypeCache = null;
        // Eager DPoP key generation after login
        if (this.dpopManager) {
            // Fire and forget - generate keys in background
            this.dpopManager.ensureKeyPair().catch(err => {
                console.warn('[DefaultAuthProvider] DPoP key generation failed:', err);
            });
        }
    }
    async setRefreshToken(token) {
        await this.tokenManager.setRefreshToken(token);
    }
    async getRefreshToken() {
        return this.tokenManager.getRefreshToken();
    }
    async clearTokens() {
        await this.tokenManager.clearAllTokens();
        // Invalidate cache on clear
        this.authTypeCache = null;
        if (this.dpopManager) {
            await this.dpopManager.clearKeys();
        }
    }
    async setTokens(accessToken, refreshToken, providerToken) {
        await this.tokenManager.setTokens(accessToken, refreshToken, providerToken);
        // Invalidate cache when tokens change
        this.authTypeCache = null;
    }
    async hasValidToken() {
        return this.tokenManager.hasAccessToken();
    }
    async setAuthType(_authType) {
        // No-op: authType is derived from JWT payload via getAuthType()
        // @see getAuthType() - reads accountType directly from JWT
    }
    async getAuthType() {
        // Read from JWT instead of separate storage for single source of truth
        const token = await this.tokenManager.getAccessToken();
        if (!token) {
            this.authTypeCache = null;
            return null;
        }
        // Return cached result if token hasn't changed
        if (this.authTypeCache && this.authTypeCache.token === token) {
            return this.authTypeCache.type;
        }
        try {
            // Decode JWT payload (without verification - we just need to read the claim)
            const parts = token.split('.');
            if (parts.length !== 3) {
                console.warn('[DefaultAuthProvider] Invalid JWT format');
                return null;
            }
            const payloadJson = atob(parts[1]);
            const payload = JSON.parse(payloadJson);
            // Use accountOwnerType from AuthJWTPayload interface
            const authType = payload.accountType || null;
            // Cache result with token signature
            this.authTypeCache = { token, type: authType };
            return authType;
        }
        catch (error) {
            console.warn('[DefaultAuthProvider] Failed to parse auth type from JWT:', error);
            return null;
        }
    }
    /**
     * Store the provider token (Firebase JWT, Auth0 token, etc.)
     * Used for session recovery when refresh tokens expire.
     *
     * NOTE: This is called AUTOMATICALLY by loginWithToken(), so you typically
     * don't need to call this manually. The only reason to call this is if:
     *
     * 1. Your auth provider (Firebase, Auth0) refreshes tokens in the background
     * 2. You want to proactively update the stored token for better recovery chances
     *
     * The stored provider token is used as a fallback when:
     * - PERS refresh token expires (e.g., after 30 days of inactivity)
     * - SDK attempts recovery using the stored provider token
     * - If provider token is still valid → session recovered automatically
     * - If provider token is also expired → user must re-authenticate
     *
     * @example
     * // Optional: Update stored token when Firebase refreshes it
     * firebase.auth().onIdTokenChanged(async (user) => {
     *   if (user) {
     *     const freshToken = await user.getIdToken();
     *     await sdk.auth.setProviderToken(freshToken);
     *   }
     * });
     */
    async setProviderToken(token) {
        await this.tokenManager.setProviderToken(token);
    }
    /**
     * Get the stored provider token (Firebase JWT, Auth0 token, etc.)
     * Used internally for session recovery when refresh tokens expire.
     */
    async getProviderToken() {
        return this.tokenManager.getProviderToken();
    }
}

/**
 * SDK Version Information
 *
 * Exported for consumers who need version info.
 * Used internally for X-SDK-Version header.
 *
 * Version is injected at build time via @rollup/plugin-replace
 *
 * @module @explorins/pers-sdk/core
 */
/** SDK package name */
const SDK_NAME = "@explorins/pers-sdk";
/** SDK version - injected from package.json at build time */
const SDK_VERSION = "2.3.12";
/** Full SDK identifier for headers */
const SDK_USER_AGENT = `${SDK_NAME}/${SDK_VERSION}`;

/**
 * Tracking Manager - Handles data source and platform tracking
 *
 * Manages attribution data for analytics across all user actions.
 *
 * @module @explorins/pers-sdk/core/tracking
 */
/**
 * Manages data source and platform tracking state
 */
class TrackingManager {
    constructor(options) {
        this.configDataSource = options?.dataSource;
        this.configPlatform = options?.platform;
    }
    /**
     * Set data source tracking info (replaces existing)
     *
     * @example
     * ```typescript
     * tracking.setDataSource({
     *   channel: 'mobile',
     *   medium: 'push',
     *   campaign: 'summer_2026',
     * });
     * ```
     */
    setDataSource(dataSource) {
        this._dataSource = dataSource;
    }
    /**
     * Update data source tracking info (merges with existing)
     *
     * @example
     * ```typescript
     * tracking.updateDataSource({
     *   campaign: 'new_campaign',
     * });
     * ```
     */
    updateDataSource(dataSource) {
        const existing = this._dataSource ?? this.configDataSource;
        this._dataSource = { ...existing, ...dataSource };
    }
    /**
     * Set platform info for analytics
     *
     * @example
     * ```typescript
     * tracking.setPlatform({
     *   os: 'iOS',
     *   osVersion: '17.4',
     *   app: 'MyApp',
     *   appVersion: '2.3.1',
     *   deviceType: 'phone',
     * });
     * ```
     */
    setPlatform(platform) {
        this._platform = platform;
    }
    /**
     * Get current data source config
     */
    getDataSource() {
        return this._dataSource ?? this.configDataSource;
    }
    /**
     * Get current platform info
     */
    getPlatform() {
        return this._platform ?? this.configPlatform;
    }
    /**
     * Check if tracking is properly configured
     *
     * Returns warnings for missing recommended tracking data.
     * Useful for development/debugging to ensure proper attribution.
     */
    validate() {
        const warnings = [];
        const dataSource = this.getDataSource();
        const platform = this.getPlatform();
        // Check channel for non-web contexts
        if (!dataSource?.channel) {
            warnings.push('No channel set. Native apps should set channel: "mobile"');
        }
        // Check platform info
        if (!platform || Object.keys(platform).length === 0) {
            warnings.push('No platform info set. Consider calling setPlatform() for better analytics');
        }
        return {
            isValid: warnings.length === 0,
            warnings,
        };
    }
    /**
     * Build HTTP headers for data source tracking
     *
     * @internal - Used by PersApiClient
     */
    buildHeaders() {
        const headers = {};
        const dataSource = this.getDataSource();
        const platform = this.getPlatform();
        if (dataSource) {
            if (dataSource.channel) {
                headers['x-source-channel'] = dataSource.channel;
            }
            if (dataSource.medium) {
                headers['x-source-medium'] = dataSource.medium;
            }
            if (dataSource.campaign) {
                headers['x-source-campaign'] = dataSource.campaign;
            }
            if (dataSource.source) {
                headers['x-source'] = dataSource.source;
            }
            if (dataSource.referrer) {
                headers['Referer'] = dataSource.referrer;
            }
        }
        if (platform && Object.keys(platform).length > 0) {
            headers['x-platform'] = JSON.stringify(platform);
        }
        return headers;
    }
}

// packages/pers-sdk/src/core/pers-api-client.ts
/**
 * PERS API Client - Platform-agnostic HTTP client with authentication
 *
 * Features: automatic token refresh, background validation, configurable settings
 *
 * @example
 * ```typescript
 * const client = new PersApiClient(httpClient, {
 *   environment: 'production',
 *   apiProjectKey: 'your-project-key',
 *   authProvider: new DefaultAuthProvider()
 * });
 *
 * const data = await client.get('/users/me');
 * ```
 */
class PersApiClient {
    /**
     * Creates a new PERS API Client instance
     *
     * @param httpClient - Platform-specific HTTP client implementation
     * @param config - Configuration options for the API client
     */
    constructor(httpClient, config) {
        this.httpClient = httpClient;
        this.config = config;
        this.initializationPromise = null;
        // Merge user config with defaults (production + v2)
        this.mergedConfig = mergeWithDefaults(config);
        // Build API root - customApiUrl takes priority over environment
        this.apiRoot = buildApiRoot(this.mergedConfig.environment, this.mergedConfig.apiVersion, this.mergedConfig.customApiUrl);
        // Initialize auth services for direct authentication
        this.authApi = new AuthApi(this);
        // Auto-create auth provider if none provided
        if (!this.mergedConfig.authProvider) {
            this.mergedConfig.authProvider = new DefaultAuthProvider({
                authType: this.mergedConfig.authType || AccountOwnerType.USER,
                projectKey: this.mergedConfig.apiProjectKey,
                storage: this.mergedConfig.authStorage, // Support custom storage
                dpop: {
                    enabled: this.mergedConfig.dpop?.enabled ?? true, // Enable DPoP by default
                    cryptoProvider: this.mergedConfig.dpop?.cryptoProvider
                }
            });
        }
        this.authService = new AuthService(this.authApi, this.mergedConfig.authProvider);
        this.refreshManager = new TokenRefreshManager(this.authService, this.mergedConfig.authProvider);
        // Initialize tracking manager
        this.tracking = new TrackingManager({
            dataSource: this.mergedConfig.dataSource,
            platform: this.mergedConfig.platform,
        });
        if (this.mergedConfig.authProvider) {
            this.initializationPromise = this.initialize();
        }
    }
    async initialize() {
        try {
            await this.ensureValidToken();
        }
        catch (error) {
            this.initializationPromise = null;
            console.debug('[PersApiClient] Startup token validation failed:', error);
        }
    }
    // ==========================================
    // TOKEN MANAGEMENT & VALIDATION
    // ==========================================
    /**
     * Ensures valid authentication token before making requests
     *
     * Validates the current token and automatically refreshes if expired or near expiration.
     * Uses a 120-second margin - tokens are refreshed if they expire within 2 minutes.
     *
     * This method is called automatically before each request but can also be called
     * manually when needed (e.g., app resume from background).
     *
     * @returns Promise that resolves when token validation is complete
     * @throws Error if token refresh fails
     *
     * @example
     * ```typescript
     * // Validate tokens when app becomes active
     * await apiClient.ensureValidToken();
     * ```
     */
    async ensureValidToken() {
        if (!this.mergedConfig.authProvider) {
            return;
        }
        return this.refreshManager.ensureValidToken();
    }
    async ensureInitialized() {
        if (this.initializationPromise) {
            await this.initializationPromise;
            this.initializationPromise = null;
        }
    }
    /* private async attemptInternalRefresh(): Promise<boolean> {
        return this.refreshManager.attemptInternalRefresh();
    } */
    /**
     * Make a request with proper headers, auth, and error handling
     */
    async request(method, endpoint, body, options) {
        if (!options?.isRefreshRequest) {
            await this.ensureInitialized();
        }
        const { retryCount = 0, responseType = 'json', bypassAuth = false } = options || {};
        const url = `${this.apiRoot}${endpoint}`;
        // TOKEN VALIDATION: Ensure token is valid before request
        // This blocks the request until token validation completes - prevents 401s from expired tokens
        if (!bypassAuth && this.mergedConfig.authProvider && retryCount === 0) {
            try {
                await this.ensureValidToken();
            }
            catch (error) {
                console.debug('[PersApiClient] Token validation failed:', error);
                // Continue with request - it will fail with 401 and trigger retry logic
            }
        }
        const requestOptions = {
            headers: await this.getHeaders(!bypassAuth, method, url),
            timeout: this.mergedConfig.timeout,
            responseType
        };
        try {
            let result;
            switch (method) {
                case 'GET':
                    result = await this.httpClient.get(url, requestOptions);
                    break;
                case 'POST':
                    result = await this.httpClient.post(url, body, requestOptions);
                    break;
                case 'PUT':
                    result = await this.httpClient.put(url, body, requestOptions);
                    break;
                case 'DELETE':
                    result = await this.httpClient.delete(url, requestOptions);
                    break;
                default:
                    throw new Error(`Unsupported HTTP method: ${method}`);
            }
            return result;
        }
        catch (error) {
            const status = ErrorUtils.getStatus(error);
            const errorMessage = ErrorUtils.getMessage(error);
            // Handle 401 Unauthorized errors
            if (status === 401) {
                const backendError = ErrorUtils.extractBackendErrorDetails(error);
                // ===========================================
                // FATAL AUTH ERRORS → Immediate logout
                // ===========================================
                // Fatal codes: REFRESH_TOKEN_EXPIRED, TOKEN_REVOKED, INVALID_REFRESH_TOKEN, etc.
                if (this.authService.isFatalAuthError(backendError.code)) {
                    console.warn(`[PersApiClient] Fatal auth error: ${backendError.code}`);
                    await this.authService.handleAuthFailure();
                    this.emitErrorEvent(backendError, errorMessage, endpoint, method, status, error);
                    throw new AuthenticationError(errorMessage, endpoint, method, backendError);
                }
                // ===========================================
                // REFRESH REQUEST FAILED → Logout (prevents infinite loop)
                // ===========================================
                if (options?.isRefreshRequest) {
                    console.warn('[PersApiClient] Refresh request itself failed with 401');
                    await this.authService.handleAuthFailure();
                    this.emitErrorEvent(backendError, errorMessage, endpoint, method, status, error);
                    throw new AuthenticationError(errorMessage, endpoint, method, backendError);
                }
                // ===========================================
                // RETRYABLE 401 (TOKEN_EXPIRED) → Try refresh, then retry
                // ===========================================
                if (retryCount === 0 && this.mergedConfig.authProvider) {
                    const refreshResult = await this.refreshManager.attemptInternalRefresh();
                    if (refreshResult.success) {
                        // Refresh succeeded → retry request with new token
                        this._events?.emitSuccess({
                            type: 'token_refreshed',
                            domain: 'authentication',
                            userMessage: 'Authentication token refreshed',
                        });
                        return await this.request(method, endpoint, body, { ...options, retryCount: 1 });
                    }
                    // Refresh failed - check if it's a definitive failure
                    if (!refreshResult.retryable) {
                        // Non-retryable failure (e.g., refresh token expired) → logout
                        console.warn('[PersApiClient] Refresh failed with non-retryable error, logging out');
                        await this.authService.handleAuthFailure();
                    }
                    else {
                        // Retryable failure (network error, etc.) → DON'T logout, let app retry later
                        console.warn('[PersApiClient] Refresh failed with retryable error, NOT logging out');
                    }
                    this.emitErrorEvent(backendError, errorMessage, endpoint, method, status, error);
                    throw new AuthenticationError(errorMessage, endpoint, method, backendError);
                }
                // ===========================================
                // RETRY FAILED (retryCount > 0) → Something wrong, but DON'T logout
                // ===========================================
                // We already refreshed successfully, but retry still got 401
                // This is unexpected - don't logout, let app investigate
                console.warn('[PersApiClient] Retry with new token still got 401 - not logging out');
                this.emitErrorEvent(backendError, errorMessage, endpoint, method, status, error);
                throw new AuthenticationError(errorMessage, endpoint, method, backendError);
            }
            // Extract backend error details for non-401 errors
            const backendError = ErrorUtils.extractBackendErrorDetails(error);
            this.emitErrorEvent(backendError, errorMessage, endpoint, method, status, error);
            throw new PersApiError(errorMessage, endpoint, method, status || undefined, backendError);
        }
    }
    /**
     * Emit error event if events emitter is available
     * @internal
     */
    emitErrorEvent(errorDetails, errorMessage, endpoint, method, status, error) {
        /* console.log('[PersApiClient] emitErrorEvent called', {
          hasEvents: !!this._events,
          instanceId: this._events?.instanceId ?? 'none',
          subscriberCount: this._events?.subscriberCount ?? 0,
          domain: errorDetails.domain,
          code: errorDetails.code,
          status: status,
          message: errorDetails.message || errorMessage
        }); */
        this._events?.emitError({
            domain: errorDetails.domain || 'external',
            type: 'api_error', // Event type discriminator (fixed value)
            userMessage: errorDetails.message || errorMessage,
            code: errorDetails.code, // Actual error code from backend
            details: {
                endpoint,
                method,
                status: status || undefined,
                retryable: ErrorUtils.isRetryable(error)
            }
        });
    }
    /**
     * Set event emitter for error event emission
     * @internal
     */
    setEvents(events) {
        this._events = events;
    }
    /**
     * Performs an authenticated GET request
     *
     * @template T - Expected response type
     * @param endpoint - API endpoint path (without base URL)
     * @param responseType - Expected response format
     * @returns Promise resolving to typed response data
     *
     * @example
     * ```typescript
     * const user = await client.get<User>('/users/123');
     * const csvData = await client.get('/export/data', 'blob');
     * ```
     */
    async get(endpoint, responseType) {
        return this.request('GET', endpoint, undefined, { responseType });
    }
    /**
     * Performs an authenticated POST request
     */
    async post(endpoint, body, options) {
        return this.request('POST', endpoint, body, options);
    }
    /**
     * Generic PUT request
     */
    async put(endpoint, body) {
        return this.request('PUT', endpoint, body);
    }
    /**
     * Generic DELETE request
     */
    async delete(endpoint) {
        return this.request('DELETE', endpoint);
    }
    /**
     * Get request headers with optional auth token and project key
     *
     * @param includeAuth - Whether to include the Authorization header (default: true)
     * @param method - HTTP Method (required for DPoP)
     * @param url - Full Request URL (required for DPoP)
     */
    async getHeaders(includeAuth = true, method, url) {
        const headers = {
            'Content-Type': 'application/json',
            'X-SDK-Version': SDK_USER_AGENT,
        };
        if (this.mergedConfig.authProvider) {
            let token = null;
            if (includeAuth) {
                token = await this.mergedConfig.authProvider.getToken();
            }
            // Handle Authentication Headers (Bearer or DPoP)
            if (this.mergedConfig.authProvider.getAuthHeaders) {
                // Provider supports advanced headers (DPoP)
                // We pass token (null if includeAuth=false) so it can generate DPoP proofs for login requests too
                try {
                    const authHeaders = await this.mergedConfig.authProvider.getAuthHeaders(token, method, url);
                    Object.assign(headers, authHeaders);
                }
                catch (e) {
                    console.warn('[PersApiClient] Failed to generate auth headers:', e);
                    // Fallback if token exists but DPoP generation failed? 
                    // Better to fail or let standardized fallback below handle it?
                    // If DPoP fails, falling back to Bearer might be insecure if server enforces DPoP. 
                    // For now, valid token -> Bearer fallback logic mostly for non-DPoP legacy providers.
                }
            }
            // Fallback: If provider didn't give headers but we have token and NO Authorization header yet
            if (includeAuth && token && !headers['Authorization']) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            else if (includeAuth && !token) {
                console.warn('[PersApiClient] No token available from auth provider');
            }
        }
        else if (includeAuth) {
            console.warn('[PersApiClient] No auth provider configured');
        }
        // Add project key
        if (this.mergedConfig.authProvider) {
            const projectKey = await this.mergedConfig.authProvider.getProjectKey();
            if (projectKey) {
                headers['x-project-key'] = projectKey;
            }
        }
        else if (this.mergedConfig.apiProjectKey) {
            headers['x-project-key'] = this.mergedConfig.apiProjectKey;
        }
        // Add tracking headers (data source + platform)
        Object.assign(headers, this.tracking.buildHeaders());
        return headers;
    }
    // ==========================================
    // INTERNAL AUTH METHODS
    // ==========================================
    /**
     * @internal - For AuthManager use only
     */
    getAuthService() {
        return this.authService;
    }
    /**
     * Get current configuration (returns merged config)
     */
    getConfig() {
        return this.mergedConfig;
    }
    /**
     * Get original user configuration
     */
    getOriginalConfig() {
        return this.config;
    }
    // ==========================================
    // DATA SOURCE TRACKING
    // ==========================================
    /** Set data source tracking info (replaces existing) */
    setDataSource(dataSource) {
        this.tracking.setDataSource(dataSource);
    }
    /** Update data source tracking info (merges with existing) */
    updateDataSource(dataSource) {
        this.tracking.updateDataSource(dataSource);
    }
    /** Set platform info for analytics */
    setPlatform(platform) {
        this.tracking.setPlatform(platform);
    }
    /** Get current data source config */
    getDataSource() {
        return this.tracking.getDataSource();
    }
    /** Get current platform info */
    getPlatform() {
        return this.tracking.getPlatform();
    }
    /** Validate tracking configuration (for debugging) */
    validateTracking() {
        return this.tracking.validate();
    }
}

/**
 * Static JWT Authentication Provider
 * For server-side integrations with long-lived JWT tokens
 *
 * Use this provider when:
 * - Running in server environments (Node.js, serverless functions)
 * - Using static/long-lived JWT tokens (not OAuth flow)
 * - No token refresh mechanism needed
 * - JWT rotation handled externally (e.g., environment variables)
 *
 * @example Server-side usage
 * ```typescript
 * const sdk = new PersSdk(httpClient, {
 *   environment: 'production',
 *   apiProjectKey: 'your-project',
 *   authProvider: new StaticJwtAuthProvider(
 *     process.env.PERS_JWT_TOKEN!,
 *     'your-project',
 *     'business' // or 'admin'
 *   )
 * });
 * ```
 */
/**
 * Simple authentication provider for static JWT tokens
 * No token storage, refresh, or DPoP - designed for server-side use
 */
class StaticJwtAuthProvider {
    constructor(configOrJwt, projectKey, authType) {
        if (typeof configOrJwt === 'string') {
            // Legacy constructor: (jwt, projectKey, authType)
            this.jwt = configOrJwt;
            this.projectKey = projectKey;
            this.authType = authType || AccountOwnerType.BUSINESS;
        }
        else {
            // New constructor: (config)
            this.jwt = configOrJwt.jwt;
            this.projectKey = configOrJwt.projectKey;
            this.authType = configOrJwt.authType || AccountOwnerType.BUSINESS;
            this.statusChangeCallback = configOrJwt.onAuthStatusChange;
        }
        if (!this.jwt) {
            throw new Error('StaticJwtAuthProvider: JWT token is required');
        }
        if (!this.projectKey) {
            throw new Error('StaticJwtAuthProvider: Project key is required');
        }
    }
    /**
     * Get the static JWT token
     * @returns The configured JWT token
     */
    async getToken() {
        return this.jwt;
    }
    /**
     * Get project key for API requests
     * @returns The configured project key
     */
    async getProjectKey() {
        return this.projectKey;
    }
    /**
     * Get authentication headers (Bearer token)
     * Note: No DPoP support for server-side static tokens
     *
     * @param token - The access token (ignored, uses configured JWT)
     * @returns Headers with Bearer authorization
     */
    async getAuthHeaders(token) {
        const actualToken = token || this.jwt;
        return actualToken ? { 'Authorization': `Bearer ${actualToken}` } : {};
    }
    /**
     * Handle authentication status changes
     *
     * Note: Static JWTs cannot be refreshed automatically.
     * TOKEN_EXPIRED status indicates manual JWT rotation is needed.
     *
     * @param status - The new authentication status
     */
    async onAuthStatusChange(status) {
        if (status === AuthStatus.TOKEN_EXPIRED) {
            console.warn('[StaticJwtAuthProvider] JWT token expired. Manual rotation required. ' +
                'Update the JWT in your environment configuration.');
        }
        // Delegate to custom callback if provided
        if (this.statusChangeCallback) {
            await this.statusChangeCallback(status);
        }
    }
    /**
     * Clear tokens (no-op for static provider)
     * Static JWTs are immutable and managed externally
     */
    async clearTokens() {
        // No-op: Static JWTs cannot be cleared, they must be rotated externally
        console.warn('[StaticJwtAuthProvider] clearTokens() called but static JWTs cannot be cleared. Rotate JWT externally.');
    }
}

/**
 * PERS SDK Event Emitter
 *
 * Simplified global event stream.
 * Platform-agnostic, zero dependencies.
 *
 * @module @explorins/pers-sdk/events
 */
/**
 * Generates a unique event ID
 */
function generateEventId() {
    return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}
/**
 * Simplified event emitter - single global stream
 *
 * All events flow through one subscription point.
 * Optional filtering at subscription level.
 * Supports replay of last auth event for late subscribers.
 *
 * @example Basic Usage
 * ```typescript
 * // Subscribe to ALL events - one handler
 * const unsubscribe = sdk.events.subscribe((event) => {
 *   showNotification(event.userMessage, event.level);
 * });
 *
 * // With filter - only transaction errors
 * sdk.events.subscribe((event) => {
 *   showErrorToast(event.userMessage);
 * }, { domains: ['transaction'], levels: ['error'] });
 *
 * // Multiple domains (whitelist)
 * sdk.events.subscribe((event) => {
 *   showNotification(event.userMessage);
 * }, { domains: ['transaction', 'campaign', 'redemption'] });
 *
 * // Exclude domains (blacklist)
 * sdk.events.subscribe((event) => {
 *   showNotification(event.userMessage);
 * }, { excludeDomains: ['validation'] });
 *
 * // Subscribe with replay - get last auth event if missed
 * sdk.events.subscribe((event) => {
 *   if (event.type === 'session_restored') {
 *     setUser(event.details.user);
 *   }
 * }, { domains: ['authentication'], replay: true });
 *
 * // Cleanup
 * unsubscribe();
 * ```
 */
let emitterInstanceCounter = 0;
class PersEventEmitter {
    constructor() {
        // Store last auth event for replay to late subscribers
        this.lastAuthEvent = null;
        this.handlers = new Set();
        this._instanceId = ++emitterInstanceCounter;
    }
    get instanceId() {
        return this._instanceId;
    }
    /**
     * Subscribe to events
     *
     * @param handler - Callback for matching events
     * @param filter - Optional filter by domain and/or level. Set `replay: true` to receive last auth event if missed.
     * @returns Unsubscribe function
     *
     * @example All events
     * ```typescript
     * const unsub = sdk.events.subscribe((event) => {
     *   console.log(`[${event.domain}] ${event.type}: ${event.userMessage}`);
     * });
     * ```
     *
     * @example Only errors
     * ```typescript
     * sdk.events.subscribe((event) => {
     *   logToSentry(event);
     * }, { levels: ['error'] });
     * ```
     *
     * @example Only transaction successes
     * ```typescript
     * sdk.events.subscribe((event) => {
     *   playSuccessSound();
     *   confetti();
     * }, { domains: ['transaction'], levels: ['success'] });
     * ```
     *
     * @example Auth events with replay (catches session_restored even if emitted before subscription)
     * ```typescript
     * sdk.events.subscribe((event) => {
     *   if (event.type === 'session_restored') {
     *     const { user } = event.details;
     *     setUser(user);
     *   }
     * }, { domains: ['authentication'], replay: true });
     * ```
     */
    subscribe(handler, filter) {
        const filteredHandler = { handler, filter };
        this.handlers.add(filteredHandler);
        // If replay is requested and we have a cached auth event, replay it immediately
        if (filter?.replay && this.lastAuthEvent) {
            // Check if event matches the filter
            if (!filter || this.matchesFilter(this.lastAuthEvent, filter)) {
                // Use queueMicrotask to ensure handler runs after subscribe returns
                queueMicrotask(() => {
                    try {
                        handler(this.lastAuthEvent);
                    }
                    catch (error) {
                        console.error('[PersEventEmitter] Replay handler error:', error);
                    }
                });
            }
        }
        return () => this.handlers.delete(filteredHandler);
    }
    /**
     * Subscribe and auto-unsubscribe after first matching event
     *
     * @param handler - Callback for the first matching event
     * @param filter - Optional filter by domain and/or level
     * @returns Unsubscribe function
     */
    once(handler, filter) {
        const filteredHandler = {
            handler: (event) => {
                this.handlers.delete(filteredHandler);
                handler(event);
            },
            filter
        };
        this.handlers.add(filteredHandler);
        return () => this.handlers.delete(filteredHandler);
    }
    /**
     * Emit a success event
     *
     * Domain is restricted to business domains only (Domain type).
     * Auth events (login_success, session_restored) are cached for replay to late subscribers.
     *
     * @param event - Success event data (id and timestamp auto-generated)
     * @returns The complete event object
     *
     * @example
     * ```typescript
     * sdk.events.emitSuccess({
     *   domain: 'transaction',  // Only business domains allowed
     *   type: 'transaction_confirmed',
     *   userMessage: 'Transaction confirmed!'
     * });
     * ```
     */
    emitSuccess(event) {
        const fullEvent = {
            ...event,
            level: 'success',
            id: generateEventId(),
            timestamp: Date.now()
        };
        // Cache auth events for replay to late subscribers
        if (event.domain === 'authentication' &&
            (event.type === 'login_success' || event.type === 'session_restored')) {
            this.lastAuthEvent = fullEvent;
        }
        // Clear cached auth on logout
        if (event.domain === 'authentication' && event.type === 'logout_success') {
            this.lastAuthEvent = null;
        }
        this.notifyHandlers(fullEvent);
        return fullEvent;
    }
    /**
     * Emit an error event
     *
     * Domain can be any domain including technical (ErrorDomain type).
     *
     * @param event - Error event data (id and timestamp auto-generated)
     * @returns The complete event object
     *
     * @example
     * ```typescript
     * sdk.events.emitError({
     *   domain: 'validation',  // Technical domains allowed
     *   type: 'validation_failed',
     *   userMessage: 'Please check your input'
     * });
     * ```
     */
    emitError(event) {
        const fullEvent = {
            ...event,
            level: 'error',
            id: generateEventId(),
            timestamp: Date.now()
        };
        // Clear cached auth on auth failure (session invalid)
        if (event.domain === 'authentication' && event.type === 'session_restoration_failed') {
            this.lastAuthEvent = null;
        }
        this.notifyHandlers(fullEvent);
        return fullEvent;
    }
    /**
     * Check if event matches filter
     */
    matchesFilter(event, filter) {
        // Level filtering
        if (filter.levels && !filter.levels.includes(event.level))
            return false;
        // Domain exclusion (blacklist) - checked first
        if (filter.excludeDomains && filter.excludeDomains.includes(event.domain))
            return false;
        // Domain inclusion (whitelist)
        if (filter.domains && !filter.domains.includes(event.domain))
            return false;
        return true;
    }
    /**
     * Notify all handlers of an event (applying filters)
     */
    notifyHandlers(event) {
        for (const { handler, filter } of this.handlers) {
            // Apply filter if present
            if (filter && !this.matchesFilter(event, filter)) {
                continue;
            }
            try {
                const result = handler(event);
                // Catch async handler errors too
                if (result && typeof result.catch === 'function') {
                    result.catch(error => {
                        console.error('[PersEventEmitter] Async handler error:', error);
                    });
                }
            }
            catch (error) {
                console.error('[PersEventEmitter] Handler error:', error);
            }
        }
    }
    /**
     * Remove all handlers
     */
    clear() {
        this.handlers.clear();
    }
    /**
     * Get count of active subscriptions
     */
    get subscriberCount() {
        return this.handlers.size;
    }
}

/**
 * Authentication Manager - Clean, high-level interface for authentication operations
 *
 * Provides a simplified API for common authentication tasks while maintaining
 * access to the underlying API client for advanced use cases.
 *
 * Supports the universal token endpoint (POST /auth/token):
 * - User authentication (default)
 * - Business authentication with role in JWT
 * - Admin/Tenant authentication
 *
 * @group Managers
 * @category Authentication
 *
 * @example Basic Usage
 * ```typescript
 * // Login with external JWT
 * const authResult = await sdk.auth.loginWithToken(firebaseJWT, 'user');
 *
 * // Login as business (with role in JWT)
 * const bizResult = await sdk.auth.loginAsBusiness(jwt, { businessId: 'biz-123' });
 * console.log('Business:', bizResult.business?.displayName);
 *
 * // Check authentication
 * if (await sdk.auth.isAuthenticated()) {
 *   const user = await sdk.auth.getCurrentUser();
 *   console.log('Welcome,', user.name);
 * }
 *
 * // Logout
 * await sdk.auth.clearAuth();
 * ```
 */
class AuthManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
    }
    /**
     * Login with JWT token
     *
     * Authenticates a user or admin using an external JWT token (Firebase, Auth0, etc.).
     * Automatically stores authentication tokens for subsequent API calls.
     *
     * @param jwtToken - JWT token to authenticate with
     * @param userType - Type of user ('user' | 'admin'). Defaults to 'user'
     * @returns Promise resolving to authentication response with user/admin data and tokens
     *
     * @example User Login
     * ```typescript
     * const authResult = await sdk.auth.loginWithToken(firebaseJWT);
     * console.log('User authenticated:', authResult.user.name);
     * ```
     *
     * @example Admin Login
     * ```typescript
     * const authResult = await sdk.auth.loginWithToken(adminJWT, 'admin');
     * console.log('Admin authenticated:', authResult.admin.email);
     * ```
     */
    async loginWithToken(jwtToken, userType = AccountOwnerType.USER) {
        const authService = this.apiClient.getAuthService();
        const result = userType === AccountOwnerType.TENANT
            ? await authService.loginTenantAdmin(jwtToken)
            : await authService.loginUser(jwtToken);
        this.events?.emitSuccess({
            domain: 'authentication',
            type: 'login_success',
            userMessage: 'Successfully logged in',
            details: { user: result.user }
        });
        return result;
    }
    /**
     * Login as business with JWT token
     *
     * Authenticates a user in a business context. The returned JWT contains
     * the user's role (OWNER, ADMIN, EDITOR, VIEWER) within that business.
     *
     * **Auto-Selection Behavior:**
     * - If user has a single business membership, it's auto-selected
     * - If user has multiple memberships and no businessId is provided,
     *   throws `MULTIPLE_CONTEXT_SELECTION_REQUIRED` error with available options
     *
     * @param jwtToken - JWT token from auth provider (passkey, Firebase, etc.)
     * @param options - Business authentication options (businessId for multi-business users)
     * @returns Promise resolving to authentication response with business context and role in JWT
     * @throws Error with code `MULTIPLE_CONTEXT_SELECTION_REQUIRED` when businessId is needed
     *
     * @example Single Business Membership
     * ```typescript
     * // Auto-selects the user's only business
     * const result = await sdk.auth.loginAsBusiness(jwt);
     * console.log('Business:', result.business?.displayName);
     * ```
     *
     * @example Multiple Business Memberships
     * ```typescript
     * try {
     *   const result = await sdk.auth.loginAsBusiness(jwt);
     * } catch (error) {
     *   if (error.code === 'MULTIPLE_CONTEXT_SELECTION_REQUIRED') {
     *     // Show business selector UI
     *     const selectedId = await showBusinessSelector(error.availableOptions);
     *     const result = await sdk.auth.loginAsBusiness(jwt, { businessId: selectedId });
     *   }
     * }
     * ```
     *
     * @example Direct Business Selection
     * ```typescript
     * const result = await sdk.auth.loginAsBusiness(jwt, { businessId: 'biz-123' });
     * console.log('Authenticated as:', result.business?.displayName);
     * ```
     */
    async loginAsBusiness(jwtToken, options) {
        const authService = this.apiClient.getAuthService();
        const result = await authService.loginBusiness(jwtToken, options);
        this.events?.emitSuccess({
            domain: 'authentication',
            type: 'login_success',
            userMessage: 'Successfully logged in as business',
            details: { user: result.user, business: result.business }
        });
        return result;
    }
    /**
     * Login as tenant admin with JWT token
     *
     * Authenticates an admin in a tenant context.
     *
     * **Auto-Selection Behavior:**
     * - If admin has access to a single tenant, it's auto-selected
     * - If admin has access to multiple tenants and no tenantId is provided,
     *   throws `MULTIPLE_CONTEXT_SELECTION_REQUIRED` error with available options
     *
     * @param jwtToken - JWT token from auth provider (Firebase, etc.)
     * @param options - Tenant authentication options (tenantId for multi-tenant admins)
     * @returns Promise resolving to authentication response with tenant context
     * @throws Error with code `MULTIPLE_CONTEXT_SELECTION_REQUIRED` when tenantId is needed
     *
     * @example Single Tenant Access
     * ```typescript
     * // Auto-selects the admin's only tenant
     * const result = await sdk.auth.loginAsTenant(jwt);
     * console.log('Tenant:', result.admin?.tenantId);
     * ```
     *
     * @example Multiple Tenant Access
     * ```typescript
     * try {
     *   const result = await sdk.auth.loginAsTenant(jwt);
     * } catch (error) {
     *   if (error.code === 'MULTIPLE_CONTEXT_SELECTION_REQUIRED') {
     *     // Show tenant selector UI
     *     const selectedId = await showTenantSelector(error.details.availableOptions);
     *     const result = await sdk.auth.loginAsTenant(jwt, { tenantId: selectedId });
     *   }
     * }
     * ```
     *
     * @example Direct Tenant Selection
     * ```typescript
     * const result = await sdk.auth.loginAsTenant(jwt, { tenantId: 'tenant-123' });
     * console.log('Authenticated as admin for tenant:', result.admin?.tenantId);
     * ```
     */
    async loginAsTenant(jwtToken, options) {
        const authService = this.apiClient.getAuthService();
        const result = await authService.loginTenantAdmin(jwtToken, options);
        this.events?.emitSuccess({
            domain: 'authentication',
            type: 'login_success',
            userMessage: 'Successfully logged in as tenant admin',
            details: { admin: result.admin }
        });
        return result;
    }
    /**
     * Get current business context
     *
     * Retrieves the current business context if authenticated as business.
     * Requires prior business authentication via {@link loginAsBusiness}.
     *
     * @returns Promise resolving to current business data
     * @throws {PersApiError} When not authenticated as business
     *
     * @example
     * ```typescript
     * const business = await sdk.auth.getCurrentBusiness();
     * console.log('Current business:', business.displayName);
     * ```
     */
    async getCurrentBusiness() {
        const authService = this.apiClient.getAuthService();
        return authService.getCurrentBusiness();
    }
    /**
     * Get current admin context
     *
     * Retrieves the current admin data if authenticated as tenant admin.
     * Requires prior tenant authentication via {@link loginAsTenant}.
     *
     * @returns Promise resolving to current admin data
     * @throws {PersApiError} When not authenticated as tenant admin
     *
     * @example
     * ```typescript
     * const admin = await sdk.auth.getCurrentAdmin();
     * console.log('Current admin:', admin.email);
     * ```
     */
    async getCurrentAdmin() {
        const authService = this.apiClient.getAuthService();
        return authService.getCurrentAdmin();
    }
    /**
     * Login with raw user data
     *
     * Authenticates using raw user data instead of a JWT token. Useful for
     * direct integration without external authentication providers.
     *
     * @param rawUserData - Raw user data for authentication
     * @returns Promise resolving to authentication response
     *
     * @example
     * ```typescript
     * const authResult = await sdk.auth.loginWithRawData({
     *   email: 'user@example.com',
     *   name: 'John Doe',
     *   externalId: 'custom-user-id'
     * });
     * ```
     */
    async loginWithRawData(rawUserData) {
        const authService = this.apiClient.getAuthService();
        const result = await authService.loginUserWithRawData(rawUserData);
        this.events?.emitSuccess({
            domain: 'authentication',
            type: 'login_success',
            userMessage: 'Successfully logged in',
            details: { user: result.user }
        });
        return result;
    }
    /**
     * Get current authenticated user
     *
     * Retrieves the currently authenticated user's profile information.
     * Requires valid authentication tokens.
     *
     * @returns Promise resolving to current user data
     * @throws {PersApiError} When user is not authenticated or tokens are invalid
     *
     * @example
     * ```typescript
     * try {
     *   const user = await sdk.auth.getCurrentUser();
     *   console.log('Current user:', user.name, user.email);
     * } catch (error) {
     *   console.log('User not authenticated');
     * }
     * ```
     */
    async getCurrentUser() {
        const authService = this.apiClient.getAuthService();
        return authService.getCurrentUser();
    }
    /**
     * Check if user is authenticated
     *
     * Performs an asynchronous check to verify if the user is currently authenticated
     * by attempting to fetch user data. More reliable than {@link hasValidAuth} but slower.
     *
     * @returns Promise resolving to boolean indicating authentication status
     *
     * @example
     * ```typescript
     * if (await sdk.auth.isAuthenticated()) {
     *   // User is authenticated, proceed with authenticated operations
     *   const user = await sdk.auth.getCurrentUser();
     * } else {
     *   // Redirect to login
     *   redirectToLogin();
     * }
     * ```
     *
     * @see {@link hasValidAuth} for synchronous token validation
     */
    async isAuthenticated() {
        try {
            await this.getCurrentUser();
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Refresh access token using stored refresh token
     *
     * Obtains new access tokens using the stored refresh token. This is typically
     * called automatically by the SDK when tokens expire, but can be called manually
     * if needed.
     *
     * @param refreshToken - Optional refresh token, uses stored token if not provided
     * @returns Promise resolving to new auth tokens
     *
     * @example
     * ```typescript
     * try {
     *   const newTokens = await sdk.auth.refreshTokens();
     *   console.log('Tokens refreshed successfully');
     * } catch (error) {
     *   console.log('Refresh failed, need to re-login');
     *   await sdk.auth.clearAuth();
     * }
     * ```
     */
    async refreshTokens(refreshToken) {
        const authService = this.apiClient.getAuthService();
        return authService.refreshAccessToken(refreshToken);
    }
    /**
     * Clear stored authentication tokens
     *
     * Removes all stored authentication tokens and clears the authentication state.
     * Use this for logout functionality.
     *
     * @returns Promise that resolves when tokens are cleared
     *
     * @example Logout
     * ```typescript
     * async function logout() {
     *   await sdk.auth.clearAuth();
     *   console.log('User logged out');
     *   // Redirect to login page
     *   window.location.href = '/login';
     * }
     * ```
     */
    async clearAuth() {
        const authService = this.apiClient.getAuthService();
        await authService.clearTokens();
        this.events?.emitSuccess({
            domain: 'authentication',
            type: 'logout_success',
            userMessage: 'Successfully logged out'
        });
    }
    /**
     * Ensure authentication token is valid
     *
     * Checks if the current token is expired or about to expire and automatically
     * refreshes it if needed. This is useful for ensuring valid authentication
     * before performing critical operations or when app resumes from background.
     *
     * Uses a 120-second margin - tokens are refreshed if they expire within 2 minutes.
     *
     * @returns Promise that resolves when token is validated/refreshed
     *
     * @example App Resume (React Native)
     * ```typescript
     * AppState.addEventListener('change', async (state) => {
     *   if (state === 'active') {
     *     // Validate tokens when app returns from background
     *     await sdk.auth.ensureValidToken().catch(console.error);
     *   }
     * });
     * ```
     *
     * @example Before Critical Operation
     * ```typescript
     * async function processPayment() {
     *   // Ensure token is valid before payment
     *   await sdk.auth.ensureValidToken();
     *   const result = await sdk.purchases.createPurchase(paymentData);
     * }
     * ```
     */
    async ensureValidToken() {
        return this.apiClient.ensureValidToken();
    }
    /**
     * Check if SDK has valid authentication
     *
     * Performs a synchronous check of stored authentication tokens without making
     * an API call. Faster than {@link isAuthenticated} but less reliable as it
     * doesn't verify tokens with the server.
     *
     * @returns Boolean indicating if valid authentication exists
     *
     * @example
     * ```typescript
     * if (sdk.auth.hasValidAuth()) {
     *   // Tokens exist locally, but may still be expired
     *   console.log('Authentication tokens found');
     * } else {
     *   // No tokens found
     *   console.log('No authentication tokens');
     * }
     * ```
     *
     * @see {@link isAuthenticated} for server-verified authentication check
     */
    async hasValidAuth() {
        const authService = this.apiClient.getAuthService();
        return await authService.hasValidAuth();
    }
}

/**
 * User Manager - Clean, high-level interface for user operations
 *
 * Provides a simplified API for common user management tasks including profile
 * management, user lookup, and administrative operations. Maintains access to
 * the full user SDK for advanced use cases.
 *
 * @group Managers
 * @category User Management
 *
 * @example Basic User Operations
 * ```typescript
 * // Get current user
 * const user = await sdk.users.getCurrentUser();
 *
 * // Update current user profile
 * const updated = await sdk.users.updateCurrentUser({
 *   name: 'New Name',
 *   email: 'new@email.com'
 * });
 *
 * // Get user by ID
 * const specificUser = await sdk.users.getUserById('user-123');
 * ```
 *
 * @example Public User Data
 * ```typescript
 * // Get all users' public profiles
 * const publicProfiles = await sdk.users.getAllUsersPublic();
 *
 * // Get users with filter
 * const filteredUsers = await sdk.users.getAllUsersPublic({
 *   key: 'city',
 *   value: 'New York'
 * });
 * ```
 *
 * @example Administrative Operations
 * ```typescript
 * // Admin: Get all users (full data)
 * const allUsers = await sdk.users.getAllUsers();
 *
 * // Admin: Update any user
 * const updated = await sdk.users.updateUser('user-123', {
 *   name: 'Updated Name'
 * });
 *
 * // Admin: Toggle user status
 * await sdk.users.toggleUserStatus(user);
 * ```
 */
class UserManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
        const userApi = new UserApi(apiClient);
        this.userService = new UserService(userApi);
    }
    /**
     * Get current user profile
     *
     * Retrieves the complete profile of the currently authenticated user.
     * Requires valid authentication tokens.
     *
     * @param options - Query options. Use `include` to specify relations: 'status' (user status types), 'balances' (token balances)
     * @returns Promise resolving to current user data with full profile information
     * @throws {PersApiError} When user is not authenticated
     *
     * @example Basic Usage
     * ```typescript
     * try {
     *   const user = await sdk.users.getCurrentUser();
     *   console.log('Current user:', user.firstName, user.email);
     * } catch (error) {
     *   console.log('User not authenticated');
     * }
     * ```
     *
     * @example With Status Types and Balances
     * ```typescript
     * // Include status types and token balances
     * const user = await sdk.users.getCurrentUser({
     *   include: ['status', 'balances']
     * });
     * console.log('Status types:', user.included?.statusTypes);
     * console.log('Token balances:', user.included?.tokenBalances);
     * ```
     */
    async getCurrentUser(options) {
        return this.userService.getRemoteUser(options);
    }
    /**
     * Update current user profile
     *
     * Updates the profile information for the currently authenticated user.
     * Only the provided fields will be updated; omitted fields remain unchanged.
     *
     * @param userData - User data to update (partial update supported)
     * @returns Promise resolving to updated user data
     * @throws {PersApiError} When user is not authenticated or validation fails
     *
     * @example Basic Profile Update
     * ```typescript
     * const updated = await sdk.users.updateCurrentUser({
     *   name: 'John Smith',
     *   email: 'john.smith@example.com'
     * });
     * console.log('Profile updated:', updated.name);
     * ```
     *
     * @example Partial Update
     * ```typescript
     * // Only update the name, keep other fields unchanged
     * const updated = await sdk.users.updateCurrentUser({
     *   name: 'New Display Name'
     * });
     * ```
     */
    async updateCurrentUser(userData) {
        const result = await this.userService.updateRemoteUser(userData);
        this.events?.emitSuccess({
            domain: 'user',
            type: 'profile_updated',
            userMessage: 'Profile updated successfully',
            details: { userId: result.id }
        });
        return result;
    }
    /**
     * Get user by unique identifier
     *
     * Retrieves a user's profile using their unique identifier. This method
     * requires appropriate permissions (admin or specific access rights).
     *
     * @param identifier - Unique identifier for the user (user ID, email, etc.)
     * @param options - Query options. Use `include` to specify relations: 'status' (user status types), 'balances' (token balances)
     * @returns Promise resolving to user data
     * @throws {PersApiError} When user not found or insufficient permissions
     *
     * @example Basic Usage
     * ```typescript
     * try {
     *   const user = await sdk.users.getUserById('user-123');
     *   console.log('Found user:', user.firstName);
     * } catch (error) {
     *   if (error.statusCode === 404) {
     *     console.log('User not found');
     *   }
     * }
     * ```
     *
     * @example With Status Types and Balances
     * ```typescript
     * // Get user with status types and token balances
     * const user = await sdk.users.getUserById('user-123', {
     *   include: ['status', 'balances']
     * });
     * console.log('Status types:', user.included?.statusTypes);
     * console.log('Token balances:', user.included?.tokenBalances);
     * ```
     */
    async getUserById(identifier, options) {
        return this.userService.getUserByUniqueIdentifier(identifier, options);
    }
    /**
     * Get all users public profiles with optional filtering
     *
     * Retrieves public profile information for all users. This data is limited
     * to publicly viewable fields only (name, profile picture, etc.).
     *
     * @param filter - Optional filter criteria to narrow results
     * @param filter.key - Field name to filter by
     * @param filter.value - Value to match for the specified field
     * @returns Promise resolving to array of user public profiles
     *
     * @example Get All Public Profiles
     * ```typescript
     * const publicProfiles = await sdk.users.getAllUsersPublic();
     * console.log(`Found ${publicProfiles.length} users`);
     *
     * publicProfiles.forEach(profile => {
     *   console.log('User:', profile.name);
     *   // Note: Only public fields are available
     * });
     * ```
     *
     * @example Filtered Search
     * ```typescript
     * // Get users from a specific city
     * const cityUsers = await sdk.users.getAllUsersPublic({
     *   key: 'city',
     *   value: 'San Francisco'
     * });
     *
     * // Get users with specific role
     * const roleUsers = await sdk.users.getAllUsersPublic({
     *   key: 'role',
     *   value: 'premium'
     * });
     * ```
     */
    async getAllUsersPublic(filter, options) {
        return this.userService.getAllUsersPublicProfiles(filter || null, options);
    }
    /**
     * Admin: Get all users
     *
     * Retrieves complete user data for all users in the system. This method
     * requires administrator privileges and returns full user profiles including
     * private information.
     *
     * @param options - Pagination options (page, limit, sortBy, sortOrder)
     * @param search - Optional search query to filter users
     * @returns Promise resolving to paginated users with complete data
     * @throws {PersApiError} When not authenticated as admin
     *
     * @example Basic Usage
     * ```typescript
     * // Admin operation - requires admin authentication
     * try {
     *   const result = await sdk.users.getAllUsers();
     *   console.log(`Total users: ${result.pagination.total}`);
     *
     *   result.data.forEach(user => {
     *     console.log(`${user.firstName} - ${user.email} - Active: ${user.isActive}`);
     *   });
     * } catch (error) {
     *   console.log('Admin access required');
     * }
     * ```
     *
     * @example With Sorting
     * ```typescript
     * import { SortOrder } from '@explorins/pers-sdk';
     *
     * // Get users sorted by email ascending
     * const result = await sdk.users.getAllUsers({
     *   page: 1,
     *   limit: 20,
     *   sortBy: 'email',
     *   sortOrder: SortOrder.ASC
     * });
     *
     * // Get users sorted by creation date (newest first)
     * const recent = await sdk.users.getAllUsers({
     *   page: 1,
     *   limit: 10,
     *   sortBy: 'createdAt',
     *   sortOrder: SortOrder.DESC
     * });
     * ```
     */
    async getAllUsers(options, search) {
        return this.userService.getAllRemoteUsers(options, search);
    }
    /**
     * Business/Admin: Create or update a user
     *
     * Creates a new user or updates an existing one. This method requires
     * business authentication (with canManageUsers permission) or admin authentication.
     *
     * @param userData - User data for creation/update
     * @returns Promise resolving to created or updated user
     * @throws {PersApiError} When not authenticated or insufficient permissions
     *
     * @example Create New User
     * ```typescript
     * // Business or Admin operation - create a new user
     * const newUser = await sdk.users.createOrUpdateUser({
     *   identifierEmail: 'newuser@example.com',
     *   firstName: 'John',
     *   lastName: 'Doe',
     *   externalId: 'external-123'
     * });
     * console.log('User created:', newUser.id);
     * ```
     *
     * @example Update Existing User
     * ```typescript
     * // If user with same identifier exists, it will be updated
     * const updated = await sdk.users.createOrUpdateUser({
     *   identifierEmail: 'existing@example.com',
     *   firstName: 'Updated Name'
     * });
     * ```
     */
    async createOrUpdateUser(userData) {
        return this.userService.createOrUpdateUser(userData);
    }
    /**
     * Admin: Bulk create or update users
     *
     * Creates or updates multiple users in a single operation. This method
     * requires admin authentication - business users cannot perform bulk operations.
     *
     * @param users - Array of user data for creation/update
     * @returns Promise resolving to array of created/updated users
     * @throws {PersApiError} When not authenticated as admin
     *
     * @example Bulk User Creation
     * ```typescript
     * // Admin-only operation - bulk create users
     * const users = await sdk.users.createOrUpdateUsers([
     *   { identifierEmail: 'user1@example.com', firstName: 'User', lastName: 'One' },
     *   { identifierEmail: 'user2@example.com', firstName: 'User', lastName: 'Two' },
     *   { identifierEmail: 'user3@example.com', firstName: 'User', lastName: 'Three' }
     * ]);
     * console.log(`Created ${users.length} users`);
     * ```
     */
    async createOrUpdateUsers(users) {
        return this.userService.createOrUpdateUsers(users);
    }
    /**
     * Admin: Update user data
     *
     * Updates profile information for any user in the system. This method
     * requires administrator privileges and can modify any user's profile.
     *
     * @param userId - ID of user to update
     * @param userData - User data to update (partial update supported)
     * @returns Promise resolving to updated user data
     * @throws {PersApiError} When not authenticated as admin or user not found
     *
     * @example
     * ```typescript
     * // Admin operation - update any user's profile
     * try {
     *   const updated = await sdk.users.updateUser('user-123', {
     *     name: 'Updated Name',
     *     isActive: true
     *   });
     *   console.log('User updated:', updated.name);
     * } catch (error) {
     *   console.log('Failed to update user:', error.message);
     * }
     * ```
     */
    async updateUser(userId, userData) {
        return this.userService.updateUserAsAdmin(userId, userData);
    }
    /**
     * Admin: Set or toggle user active status
     *
     * Sets the active/inactive status of a user account explicitly, or toggles
     * if no explicit value is provided. This is typically used for account
     * suspension or reactivation. Requires administrator privileges.
     *
     * @param userId - User ID to update
     * @param isActive - Optional explicit status. If provided, sets to this value. If omitted, toggles current status.
     * @returns Promise resolving to updated user data
     * @throws {PersApiError} When not authenticated as admin
     *
     * @example Explicit Status
     * ```typescript
     * // Admin operation - explicitly activate user
     * const activated = await sdk.users.setUserActiveStatus('user-123', true);
     * console.log('User activated:', activated.isActive); // true
     *
     * // Explicitly deactivate user
     * const deactivated = await sdk.users.setUserActiveStatus('user-123', false);
     * console.log('User deactivated:', deactivated.isActive); // false
     * ```
     *
     * @example Toggle Status
     * ```typescript
     * // Admin operation - toggle current status
     * const toggled = await sdk.users.setUserActiveStatus('user-123');
     * console.log('User status toggled:', toggled.isActive);
     * ```
     */
    async setUserActiveStatus(userId, isActive) {
        return this.userService.setUserActiveStatus(userId, isActive);
    }
    /**
     * Admin: Delete user (soft delete)
     *
     * Soft deletes a user account. The user data is retained for 30 days before
     * GDPR anonymization. Use restoreUser() to restore within the grace period.
     *
     * ⚠️ This operation is irreversible after 30 days. Consider using toggleUserStatus()
     * for temporary deactivation instead.
     *
     * @param identifier - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @returns Promise resolving to success status and message
     * @throws {PersApiError} When not authenticated as admin or user not found
     *
     * @example Delete User
     * ```typescript
     * // Admin operation - soft delete a user
     * try {
     *   const result = await sdk.users.deleteUser('user-123');
     *   console.log(result.message); // "User user-123 has been deleted"
     * } catch (error) {
     *   console.log('Failed to delete user:', error.message);
     * }
     * ```
     */
    async deleteUser(identifier) {
        const result = await this.userService.deleteUser(identifier);
        this.events?.emitSuccess({
            domain: 'user',
            type: 'user_deleted',
            userMessage: 'User deleted successfully',
            details: { identifier }
        });
        return result;
    }
    /**
     * Admin: Restore deleted user
     *
     * Restores a soft-deleted user within the 30-day grace period.
     * After GDPR anonymization (30 days), restoration is not possible.
     *
     * @param identifier - User unique identifier (id, email, externalId, accountAddress, etc.)
     * @returns Promise resolving to restored user data
     * @throws {PersApiError} When not authenticated as admin, user not found, or already anonymized
     *
     * @example Restore Deleted User
     * ```typescript
     * // Admin operation - restore a deleted user
     * try {
     *   const user = await sdk.users.restoreUser('user-123');
     *   console.log('User restored:', user.firstName);
     * } catch (error) {
     *   if (error.message.includes('anonymized')) {
     *     console.log('User cannot be restored - already anonymized');
     *   }
     * }
     * ```
     */
    async restoreUser(identifier) {
        const result = await this.userService.restoreUser(identifier);
        this.events?.emitSuccess({
            domain: 'user',
            type: 'user_restored',
            userMessage: 'User restored successfully',
            details: { identifier, userId: result.id }
        });
        return result;
    }
    /**
     * Admin: Export users as CSV
     *
     * Generates a comprehensive CSV export of all tenant users for
     * external analysis, reporting, or compliance purposes. This operation
     * requires administrator privileges and creates a downloadable file.
     *
     * @param options - Optional date range filters
     * @param options.dateFrom - Start date filter (ISO date string)
     * @param options.dateTo - End date filter (ISO date string)
     * @returns Promise resolving to CSV blob for download
     * @throws {PersApiError} When not authenticated as administrator or export fails
     *
     * @example Export All Users
     * ```typescript
     * const csvBlob = await sdk.users.exportCSV();
     * const downloadUrl = URL.createObjectURL(csvBlob);
     * ```
     *
     * @example Export Users Within Date Range
     * ```typescript
     * const csvBlob = await sdk.users.exportCSV({
     *   dateFrom: '2024-01-01',
     *   dateTo: '2024-12-31'
     * });
     * ```
     */
    async exportCSV(options) {
        const userApi = new UserApi(this.apiClient);
        return userApi.exportCSV(options);
    }
    /**
     * Get the user service for advanced operations
     *
     * Provides access to the complete UserService instance for advanced operations
     * not covered by the high-level manager methods.
     *
     * @returns UserService instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const userService = sdk.users.getUserService();
     *
     * // Access advanced user operations
     * const advancedResult = await userService.someAdvancedMethod();
     * ```
     */
    getUserService() {
        return this.userService;
    }
}

/**
 * User Status Manager - Clean, high-level interface for user status operations
 *
 * Provides a simplified API for common user status management tasks while maintaining
 * access to the full user status SDK for advanced use cases.
 */
class UserStatusManager {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.userStatusSDK = createUserStatusSDK(apiClient);
    }
    /**
     * Get all user status types
     *
     * @returns Promise resolving to paginated user status types
     */
    async getUserStatusTypes(options) {
        return this.userStatusSDK.getRemoteUserStatusTypes(options);
    }
    /**
     * Get earned user status for authenticated user
     *
     * @returns Promise resolving to paginated earned user status
     */
    async getEarnedUserStatus(options) {
        return this.userStatusSDK.getRemoteEarnedUserStatus(options);
    }
    /**
     * Admin: Create new user status type
     *
     * @param userStatusType - User status type data
     * @returns Promise resolving to created user status type
     */
    async createUserStatusType(userStatusType) {
        return this.userStatusSDK.createUserStatusType(userStatusType);
    }
    /**
     * Admin: Update existing user status type
     *
     * @param id - User status type ID
     * @param userStatusType - User status type data
     * @returns Promise resolving to updated user status type
     */
    async updateUserStatusType(id, userStatusType) {
        return this.userStatusSDK.updateUserStatusType(id, userStatusType);
    }
    /**
     * Admin: Delete user status type
     *
     * @param id - User status type ID
     * @returns Promise resolving when deleted
     */
    async deleteUserStatusType(id) {
        return this.userStatusSDK.deleteUserStatusType(id);
    }
    /**
     * Get the full user status SDK for advanced operations
     *
     * @returns UserStatusSDK instance
     */
    getUserStatusSDK() {
        return this.userStatusSDK;
    }
}

/**
 * Domain Token Manager - Clean, high-level interface for business token operations
 *
 * Handles business domain tokens (reward tokens, credit tokens, NFTs, status tokens).
 * This is separate from AuthTokenManager which handles authentication tokens.
 *
 * Provides a simplified API for token management including retrieving token information,
 * managing different types of tokens (credit, reward, status), and administrative operations.
 * Maintains access to the full token SDK for advanced blockchain and token operations.
 *
 * @group Managers
 * @category Token Management
 *
 * @example Basic Token Operations
 * ```typescript
 * // Get all available tokens
 * const tokens = await sdk.tokens.getTokens();
 * console.log(`Found ${tokens.length} tokens`);
 *
 * // Get active credit token
 * const creditToken = await sdk.tokens.getActiveCreditToken();
 * console.log('Credit token:', creditToken.name);
 *
 * // Get reward tokens
 * const rewards = await sdk.tokens.getRewardTokens();
 * ```
 *
 * @example Token Type Management
 * ```typescript
 * // Get all token types
 * const types = await sdk.tokens.getTokenTypes();
 *
 * // Get status tokens for user progression
 * const statusTokens = await sdk.tokens.getStatusTokens();
 *
 * // Get token by blockchain contract
 * const token = await sdk.tokens.getTokenByContract('0x123...', 'token-id');
 * ```
 *
 * @example Administrative Operations
 * ```typescript
 * // Admin: Create new token type
 * const newToken = await sdk.tokens.createToken({
 *   name: 'VIP Points',
 *   symbol: 'VIP',
 *   type: 'REWARD'
 * });
 *
 * // Admin: Toggle token status
 * await sdk.tokens.toggleTokenActive('token-123');
 * ```
 */
class TokenManager {
    constructor(apiClient, eventEmitter) {
        this.apiClient = apiClient;
        const tokenApi = new TokenApi(apiClient);
        this.tokenService = new TokenService(tokenApi);
        this.eventEmitter = eventEmitter;
    }
    /**
     * Get all available tokens
     *
     * Retrieves all tokens available in the system, including credit tokens,
     * reward tokens, and status tokens. This includes both active and inactive tokens.
     *
     * @returns Promise resolving to array of tokens with complete information
     *
     * @example
     * ```typescript
     * const tokens = await sdk.tokens.getTokens();
     *
     * tokens.forEach(token => {
     *   console.log(`${token.name} (${token.symbol})`);
     *   console.log(`Type: ${token.type}`);
     *   console.log(`Active: ${token.isActive}`);
     *   console.log(`Contract: ${token.contractAddress}`);
     * });
     * ```
     */
    async getTokens(options) {
        return this.tokenService.getRemoteTokens(options);
    }
    /**
     * Get all token types
     *
     * Retrieves the available token type definitions and their configurations.
     * This includes metadata about how different token types behave in the system.
     *
     * @returns Promise resolving to token type configurations
     *
     * @example
     * ```typescript
     * const types = await sdk.tokens.getTokenTypes();
     * console.log('Available token types:', types);
     *
     * // Use for token creation or validation
     * const isValidType = types.some(type => type.name === 'REWARD');
     * ```
     */
    async getTokenTypes(options) {
        return this.tokenService.getRemoteTokenTypes(options);
    }
    /**
     * Get active credit token
     *
     * Retrieves the currently active credit token used for the main loyalty currency.
     * There is typically only one active credit token per tenant.
     *
     * @returns Promise resolving to active credit token
     * @throws {PersApiError} When no active credit token is found
     *
     * @example
     * ```typescript
     * try {
     *   const creditToken = await sdk.tokens.getActiveCreditToken();
     *   console.log('Main loyalty currency:', creditToken.name);
     *   console.log('Symbol:', creditToken.symbol);
     *   console.log('Exchange rate:', creditToken.exchangeRate);
     * } catch (error) {
     *   console.log('No active credit token found');
     * }
     * ```
     */
    async getActiveCreditToken() {
        return this.tokenService.getRemoteActiveCreditToken();
    }
    /**
     * Get reward tokens
     *
     * Retrieves all tokens designated as rewards that can be earned through
     * campaigns, challenges, or other loyalty activities.
     *
     * @returns Promise resolving to array of reward tokens
     *
     * @example
     * ```typescript
     * const rewardTokens = await sdk.tokens.getRewardTokens();
     *
     * console.log('Available rewards:');
     * rewardTokens.forEach(token => {
     *   console.log(`- ${token.name}: ${token.description}`);
     * });
     *
     * // Use for campaign rewards or redemption options
     * const discountToken = rewardTokens.find(t => t.name.includes('Discount'));
     * ```
     */
    async getRewardTokens(options) {
        return this.tokenService.getRemoteRewardTokens(options);
    }
    /**
     * Get status tokens
     *
     * Retrieves tokens that represent user status or achievement levels.
     * These are typically used for user progression and tier systems.
     *
     * @returns Promise resolving to array of status tokens
     *
     * @example
     * ```typescript
     * const statusTokens = await sdk.tokens.getStatusTokens();
     *
     * console.log('Status levels available:');
     * statusTokens.forEach(token => {
     *   console.log(`${token.name} - ${token.description}`);
     * });
     *
     * // Use for user tier display or progression tracking
     * const vipToken = statusTokens.find(t => t.name === 'VIP Status');
     * ```
     */
    async getStatusTokens(options) {
        return this.tokenService.getRemoteStatusTokens(options);
    }
    /**
     * Get token by contract address
     *
     * Retrieves a specific token by its blockchain contract address and optional
     * token ID. Useful for blockchain integrations and Web3 operations.
     *
     * @param contractAddress - Blockchain contract address of the token
     * @param contractTokenId - Optional specific token ID within the contract (for NFTs)
     * @returns Promise resolving to matching token
     * @throws {PersApiError} When token with specified contract address is not found
     *
     * @example ERC20 Token
     * ```typescript
     * // Get ERC20 token by contract address
     * const token = await sdk.tokens.getTokenByContract('0x123...');
     * console.log('ERC20 token:', token.name);
     * ```
     *
     * @example NFT Token
     * ```typescript
     * // Get specific NFT by contract and token ID
     * const nft = await sdk.tokens.getTokenByContract(
     *   '0x456...',
     *   'token-123'
     * );
     * console.log('NFT:', nft.name, 'ID:', nft.contractTokenId);
     * ```
     */
    async getTokenByContract(contractAddress, contractTokenId = null) {
        return this.tokenService.getTokenByContractAddress(contractAddress, contractTokenId);
    }
    /**
     * Get all token metadata with filtering, pagination, and include relations
     *
     * Retrieves token metadata (rewards/stamps) with server-side filtering, pagination,
     * and optional include relations for additional data.
     * Useful for displaying rewards (ERC1155) or stamps (ERC721) in admin tables.
     * Non-admin users always get active metadata only.
     *
     * @param options - Filter, pagination, and include options
     * @returns Promise resolving to paginated token metadata
     *
     * @example Get paginated rewards with counts
     * ```typescript
     * const rewards = await sdk.tokens.getTokenMetadata({
     *   tokenType: 'ERC1155',
     *   active: true,
     *   page: 1,
     *   limit: 10,
     *   sortBy: 'name',
     *   sortOrder: 'ASC',
     *   include: ['mintCount', 'burnCount', 'token']
     * });
     *
     * rewards.data.forEach(r => {
     *   console.log(`${r.name}: ${r.mintCount} minted, ${r.burnCount} burned`);
     *   console.log(`Contract: ${r.included?.token?.contractAddress}`);
     * });
     * ```
     *
     * @example Get stamps with owner business
     * ```typescript
     * const stamps = await sdk.tokens.getTokenMetadata({
     *   tokenType: 'ERC721',
     *   search: 'gold',
     *   include: ['ownerBusiness', 'token']
     * });
     *
     * stamps.data.forEach(s => {
     *   console.log(`${s.name} owned by ${s.included?.ownerBusiness?.displayName}`);
     * });
     * ```
     */
    async getTokenMetadata(options) {
        return this.tokenService.getTokenMetadata(options);
    }
    /**
     * Get rewards (ERC1155 token metadata) with filtering, pagination, and include relations
     *
     * Convenience method for fetching reward metadata. Rewards are ERC1155 tokens
     * that represent collectible items, vouchers, or other redeemable rewards.
     *
     * @param options - Filter, pagination, and include options (tokenType is set automatically)
     * @returns Promise resolving to paginated reward metadata
     *
     * @example Get active rewards with mint counts
     * ```typescript
     * const rewards = await sdk.tokens.getRewards({
     *   active: true,
     *   limit: 10,
     *   include: ['mintCount', 'burnCount', 'token', 'ownerBusiness']
     * });
     *
     * rewards.data.forEach(r => {
     *   console.log(`${r.name}: ${r.mintCount ?? 0} minted`);
     *   if (r.included?.token) {
     *     console.log(`  Chain: ${r.included.token.chainId}, Contract: ${r.included.token.contractAddress}`);
     *   }
     * });
     * ```
     */
    async getRewards(options) {
        return this.tokenService.getRewards(options);
    }
    /**
     * Get stamps (ERC721 token metadata) with filtering, pagination, and include relations
     *
     * Convenience method for fetching stamp metadata. Stamps are ERC721 tokens
     * that represent achievements, badges, or collectible status markers.
     *
     * @param options - Filter, pagination, and include options (tokenType is set automatically)
     * @returns Promise resolving to paginated stamp metadata
     *
     * @example Get active stamps with all includes
     * ```typescript
     * const stamps = await sdk.tokens.getStamps({
     *   active: true,
     *   limit: 10,
     *   include: ['mintCount', 'burnCount', 'token', 'ownerBusiness']
     * });
     *
     * stamps.data.forEach(s => {
     *   console.log(`${s.name}: ${s.mintCount ?? 0} minted`);
     *   if (s.included?.ownerBusiness) {
     *     console.log(`  Owner: ${s.included.ownerBusiness.displayName}`);
     *   }
     * });
     * ```
     */
    async getStamps(options) {
        return this.tokenService.getStamps(options);
    }
    /**
     * Admin: Create new token
     *
     * Creates a new token type in the system. This operation requires administrator
     * privileges and is typically used for setting up new loyalty currencies,
     * rewards, or status tokens.
     *
     * @param tokenData - Token creation data including name, symbol, type, and configuration
     * @returns Promise resolving to created token
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example Create Credit Token
     * ```typescript
     * // Admin operation - create new loyalty currency
     * const creditToken = await sdk.tokens.createToken({
     *   name: 'Hotel Points',
     *   symbol: 'HP',
     *   type: 'CREDIT',
     *   isActive: true,
     *   exchangeRate: 1.0,
     *   description: 'Main loyalty currency for hotel rewards'
     * });
     * ```
     *
     * @example Create Reward Token
     * ```typescript
     * // Admin operation - create new reward type
     * const rewardToken = await sdk.tokens.createToken({
     *   name: '10% Discount Coupon',
     *   symbol: 'DISC10',
     *   type: 'REWARD',
     *   isActive: true,
     *   description: '10% discount on next purchase'
     * });
     * ```
     */
    async createToken(tokenData) {
        return this.tokenService.createToken(tokenData);
    }
    /**
     * Admin: Update token
     *
     * Updates an existing token's configuration. This operation requires administrator
     * privileges and can modify token properties such as name, description, or status.
     *
     * @param tokenId - ID of the token to update
     * @param tokenData - Token update data (partial update supported)
     * @returns Promise resolving to updated token
     * @throws {PersApiError} When not authenticated as admin or token not found
     *
     * @example
     * ```typescript
     * // Admin operation - update token configuration
     * const updated = await sdk.tokens.updateToken('token-123', {
     *   description: 'Updated token description',
     *   exchangeRate: 1.25,
     *   isActive: true
     * });
     *
     * console.log('Token updated:', updated.name);
     * ```
     */
    async updateToken(tokenId, tokenData) {
        return this.tokenService.updateToken(tokenId, tokenData);
    }
    /**
     * Admin: Toggle token active status
     *
     * Toggles the active/inactive status of a token. Inactive tokens are not
     * available for new operations but existing holdings remain valid.
     * Requires administrator privileges.
     *
     * @param tokenId - ID of the token to toggle
     * @returns Promise resolving to updated token
     * @throws {PersApiError} When not authenticated as admin or token not found
     *
     * @example
     * ```typescript
     * // Admin operation - disable a token temporarily
     * const updated = await sdk.tokens.toggleTokenActive('seasonal-token-123');
     *
     * if (updated.isActive) {
     *   console.log('Token reactivated');
     * } else {
     *   console.log('Token deactivated');
     * }
     * ```
     */
    async toggleTokenActive(tokenId) {
        return this.tokenService.toggleTokenActive(tokenId);
    }
    /**
     * Admin: Delete token metadata (soft delete)
     *
     * Soft deletes token metadata by setting deletedAt timestamp.
     * Requires administrator privileges. Will fail with 409 Conflict
     * if metadata is currently in use by active campaigns or redemptions.
     *
     * @param metadataId - ID of the token metadata to delete
     * @throws {PersApiError} When not authenticated as admin
     * @throws {PersApiError} 409 Conflict if metadata is in use
     *
     * @example
     * ```typescript
     * try {
     *   await sdk.tokens.deleteTokenMetadata('metadata-123');
     *   console.log('Token metadata deleted');
     * } catch (error) {
     *   if (error.statusCode === 409) {
     *     console.log('Cannot delete: metadata is used by campaigns/redemptions');
     *   }
     * }
     * ```
     */
    async deleteTokenMetadata(metadataId) {
        return this.tokenService.deleteTokenMetadata(metadataId);
    }
    /**
     * Get the full token SDK for advanced operations
     *
     * Provides access to the complete TokenSDK instance for advanced blockchain
     * operations, token balances, transfers, and other operations not covered
     * by the high-level manager methods.
     *
     * @returns TokenSDK instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const tokenSDK = sdk.tokens.getTokenSDK();
     *
     * // Access token balance operations
     * const balances = await tokenSDK.getTokenBalances('user-123');
     *
     * // Access blockchain operations
     * const web3Operations = tokenSDK.getWeb3Operations();
     *
     * // Access token API directly
     * const tokenApi = tokenSDK.api;
     * ```
     */
    getTokenService() {
        return this.tokenService;
    }
    // ==========================================
    // 📊 CSV EXPORT
    // ==========================================
    /**
     * Admin: Export token metadata to CSV
     *
     * Returns a CSV file with all token metadata (rewards/stamps) for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns Promise resolving to CSV blob
     * @throws {PersApiError} When not authenticated as tenant admin
     *
     * @example
     * ```typescript
     * // Export all token metadata
     * const csvBlob = await sdk.tokens.exportMetadataCSV();
     *
     * // Export metadata created in date range
     * const csvBlob = await sdk.tokens.exportMetadataCSV({
     *   dateFrom: '2026-01-01',
     *   dateTo: '2026-06-30'
     * });
     *
     * // Download the CSV
     * const url = URL.createObjectURL(csvBlob);
     * const a = document.createElement('a');
     * a.href = url;
     * a.download = 'token-metadata.csv';
     * a.click();
     * ```
     */
    async exportMetadataCSV(options) {
        return this.tokenService.exportMetadataCSV(options);
    }
}

/**
 * Business Manager - Clean, high-level interface for business operations
 *
 * Provides a simplified API for business directory management including retrieving
 * active businesses, managing business types, and administrative operations for
 * business creation and updates. Maintains access to the full business SDK
 * for advanced partnership and integration features.
 *
 * @group Managers
 * @category Business Management
 *
 * @example Basic Business Operations
 * ```typescript
 * // Get all active businesses in the network
 * const businesses = await sdk.business.getActiveBusinesses();
 * console.log(`Found ${businesses.length} active businesses`);
 *
 * // Get specific business details
 * const business = await sdk.business.getBusinessById('business-123');
 * console.log('Business:', business.displayName);
 *
 * // Search business by account address
 * const partnerBusiness = await sdk.business.getBusinessByAccount('0x123...');
 * ```
 *
 * @example Business Type Management
 * ```typescript
 * // Get all business categories/types
 * const types = await sdk.business.getBusinessTypes();
 *
 * // Filter businesses by type
 * const restaurants = await sdk.business.getBusinessesByType('restaurant-type-id');
 *
 * console.log('Restaurant partners:', restaurants.length);
 * restaurants.forEach(restaurant => {
 *   console.log(`- ${restaurant.displayName}`);
 * });
 * ```
 *
 * @example Administrative Operations
 * ```typescript
 * // Admin: Create new business partner
 * const newBusiness = await sdk.business.createBusiness('New Partner Hotel');
 *
 * // Admin: Update business information
 * await sdk.business.updateBusiness('business-123', {
 *   displayName: 'Updated Business Name',
 *   description: 'New business description',
 *   contactEmail: 'contact@business.com'
 * });
 *
 * // Admin: Toggle business status
 * await sdk.business.toggleBusinessStatus('business-123', { isActive: false });
 * ```
 */
class BusinessManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
        this.businessApi = new BusinessApi(apiClient);
        this.businessService = new BusinessService(this.businessApi);
        this.membershipApi = new BusinessMembershipApi(apiClient);
        this.membershipService = new BusinessMembershipService(this.membershipApi);
        this.userApi = new UserApi(apiClient);
    }
    /**
     * Get all active businesses
     *
     * Retrieves all currently active businesses in the loyalty network.
     * Active businesses are available for partnerships, campaigns, and transactions.
     *
     * @returns Promise resolving to array of active business data
     *
     * @example
     * ```typescript
     * const activeBusinesses = await sdk.business.getActiveBusinesses();
     *
     * console.log('Active Business Partners:');
     * activeBusinesses.forEach(business => {
     *   console.log(`- ${business.displayName}`);
     *   console.log(`  Type: ${business.businessType?.name || 'N/A'}`);
     *   console.log(`  Account: ${business.accountAddress}`);
     * });
     *
     * // Use for partnership displays or campaign targeting
     * const hotelPartners = activeBusinesses.filter(b =>
     *   b.businessType?.name?.toLowerCase().includes('hotel')
     * );
     * ```
     */
    async getActiveBusinesses(options) {
        return this.businessService.getActiveBusinesses(options);
    }
    /**
     * Get business by ID
     *
     * Retrieves detailed information for a specific business using its unique identifier.
     * Includes business profile, contact information, and partnership status.
     *
     * @param businessId - Unique business identifier
     * @returns Promise resolving to business data
     * @throws {PersApiError} When business with specified ID is not found
     *
     * @example
     * ```typescript
     * try {
     *   const business = await sdk.business.getBusinessById('partner-hotel-123');
     *
     *   console.log('Business Details:');
     *   console.log('Name:', business.displayName);
     *   console.log('Description:', business.description);
     *   console.log('Contact:', business.contactEmail);
     *   console.log('Active:', business.isActive);
     *   console.log('Account Address:', business.accountAddress);
     *
     * } catch (error) {
     *   console.log('Business not found:', error.message);
     * }
     * ```
     */
    async getBusinessById(businessId) {
        return this.businessService.getBusinessById(businessId);
    }
    /**
     * Get business by account address
     *
     * Retrieves business information using the blockchain account address.
     * Useful for Web3 integrations and blockchain transaction verification.
     *
     * @param accountAddress - Blockchain account address of the business
     * @returns Promise resolving to business data
     * @throws {PersApiError} When business with specified account address is not found
     *
     * @example
     * ```typescript
     * // Get business associated with blockchain address
     * const business = await sdk.business.getBusinessByAccount('0x742d35Cc6634C0532925a3b8D94B309e32ad75c6');
     *
     * console.log('Business for address:', business.displayName);
     * console.log('Verified partner:', business.isActive);
     *
     * // Use for transaction verification
     * if (business.isActive) {
     *   console.log('Verified business partner');
     * }
     * ```
     */
    async getBusinessByAccount(accountAddress) {
        return this.businessService.getBusinessByAccount(accountAddress);
    }
    /**
     * Get all business types
     *
     * Retrieves all available business type categories and their configurations.
     * Business types help classify partners and determine available features.
     *
     * @returns Promise resolving to array of business type definitions
     *
     * @example
     * ```typescript
     * const businessTypes = await sdk.business.getBusinessTypes();
     *
     * console.log('Available Business Categories:');
     * businessTypes.forEach(type => {
     *   console.log(`- ${type.name}: ${type.description}`);
     * });
     *
     * // Use for business registration forms
     * const typeOptions = businessTypes.map(type => ({
     *   value: type.id,
     *   label: type.name
     * }));
     * ```
     */
    async getBusinessTypes(options) {
        return this.businessService.getBusinessTypes();
    }
    /**
     * Admin: Create a new business type
     *
     * Creates a new business type category in the system.
     * Requires administrator privileges.
     *
     * @param data - Business type data (name, description, code, iconUrl)
     * @returns Promise resolving to the created business type
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * const newType = await sdk.business.createBusinessType({
     *   name: 'Restaurant',
     *   description: 'Food service establishments',
     *   code: 'RESTAURANT',
     *   iconUrl: 'https://example.com/restaurant-icon.png'
     * });
     * ```
     */
    async createBusinessType(data) {
        return this.businessApi.createBusinessType(data);
    }
    /**
     * Admin: Update an existing business type
     *
     * Updates a business type's properties.
     * Requires administrator privileges.
     *
     * @param data - Updated business type data (must include id)
     * @returns Promise resolving to the updated business type
     * @throws {PersApiError} When not authenticated as admin or type not found
     *
     * @example
     * ```typescript
     * const updated = await sdk.business.updateBusinessType({
     *   id: 123,
     *   name: 'Fine Dining Restaurant',
     *   description: 'Upscale dining establishments'
     * });
     * ```
     */
    async updateBusinessType(data) {
        return this.businessApi.updateBusinessType(data);
    }
    /**
     * Admin: Delete a business type
     *
     * Deletes a business type. Will fail if type is in use by businesses.
     * Requires administrator privileges.
     *
     * @param id - Business type ID to delete
     * @returns Promise resolving to true if deleted successfully
     * @throws {PersApiError} When not authenticated as admin or type not found/in use
     *
     * @example
     * ```typescript
     * const success = await sdk.business.deleteBusinessType(123);
     * ```
     */
    async deleteBusinessType(id) {
        return this.businessApi.deleteBusinessType(id);
    }
    /**
     * Get businesses by type
     *
     * Retrieves all businesses that belong to a specific business type category.
     * Useful for creating category-specific partner directories or targeted campaigns.
     *
     * @param typeId - Business type identifier to filter by
     * @returns Promise resolving to array of businesses in the specified type
     *
     * @example
     * ```typescript
     * // Get all restaurant partners
     * const restaurants = await sdk.business.getBusinessesByType('restaurant-type-id');
     *
     * console.log(`Found ${restaurants.length} restaurant partners:`);
     * restaurants.forEach(restaurant => {
     *   console.log(`- ${restaurant.displayName}`);
     *   if (restaurant.description) {
     *     console.log(`  ${restaurant.description}`);
     *   }
     * });
     *
     * // Use for category-specific campaigns
     * const restaurantCampaign = {
     *   targetBusinesses: restaurants.map(r => r.id),
     *   category: 'Dining Rewards'
     * };
     * ```
     */
    async getBusinessesByType(typeId, options) {
        return this.businessService.getBusinessesByType(typeId, options);
    }
    /**
     * Admin: Get all businesses (including inactive)
     *
     * Retrieves all businesses in the system regardless of active status.
     * This operation requires administrator privileges and is used for
     * comprehensive business management and reporting.
     *
     * @returns Promise resolving to array of all businesses
     * @throws {PersApiError} When not authenticated as administrator
     *
     * @example
     * ```typescript
     * // Admin operation - get complete business directory
     * const allBusinesses = await sdk.business.getBusinesses();
     *
     * const activeCount = allBusinesses.filter(b => b.isActive).length;
     * const inactiveCount = allBusinesses.length - activeCount;
     *
     * console.log(`Total businesses: ${allBusinesses.length}`);
     * console.log(`Active: ${activeCount}, Inactive: ${inactiveCount}`);
     *
     * // Generate business status report
     * const statusReport = allBusinesses.map(business => ({
     *   name: business.displayName,
     *   status: business.isActive ? 'Active' : 'Inactive',
     *   type: business.businessType?.name || 'Unspecified'
     * }));
     * ```
     */
    async getBusinesses(options) {
        return this.businessService.getBusinesses(options);
    }
    /**
     * Admin: Create new business
     *
     * Creates a new business partner in the loyalty network. This operation
     * requires administrator privileges and establishes a new business entity
     * with a blockchain account and initial configuration.
     *
     * @param displayName - Display name for the new business
     * @returns Promise resolving to created business data
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * // Admin operation - onboard new business partner
     * const newBusiness = await sdk.business.createBusiness('Grand Palace Hotel');
     *
     * console.log('New business created:');
     * console.log('ID:', newBusiness.id);
     * console.log('Name:', newBusiness.displayName);
     * console.log('Account Address:', newBusiness.accountAddress);
     * console.log('Status:', newBusiness.isActive ? 'Active' : 'Pending');
     *
     * // Follow up with additional configuration
     * await sdk.business.updateBusiness(newBusiness.id, {
     *   description: 'Luxury hotel in downtown area',
     *   contactEmail: 'partnerships@grandpalace.com',
     *   businessTypeId: 'hotel-type-id'
     * });
     * ```
     */
    async createBusiness(displayName) {
        const result = await this.businessService.createBusinessByDisplayName(displayName);
        this.events?.emitSuccess({
            domain: 'business',
            type: 'business_created',
            userMessage: 'Business created successfully',
            details: { businessId: result.id, displayName: result.displayName }
        });
        return result;
    }
    /**
     * Admin: Update business
     *
     * Updates an existing business's information and configuration. This operation
     * requires administrator privileges and can modify business profile data,
     * contact information, and operational settings.
     *
     * @param businessId - ID of the business to update
     * @param businessData - Business update data (partial updates supported)
     * @returns Promise resolving to updated business data
     * @throws {PersApiError} When not authenticated as admin or business not found
     *
     * @example
     * ```typescript
     * // Admin operation - update business profile
     * const updated = await sdk.business.updateBusiness('business-123', {
     *   displayName: 'Grand Palace Hotel & Spa',
     *   description: 'Luxury hotel with spa services and conference facilities',
     *   contactEmail: 'partnerships@grandpalacespa.com',
     *   businessTypeId: 'hotel-spa-type-id'
     * });
     *
     * console.log('Business updated:', updated.displayName);
     * ```
     */
    async updateBusiness(businessId, businessData) {
        const result = await this.businessService.updateBusiness(businessId, businessData);
        this.events?.emitSuccess({
            domain: 'business',
            type: 'business_updated',
            userMessage: 'Business updated successfully',
            details: { businessId: result.id, displayName: result.displayName }
        });
        return result;
    }
    /**
     * Admin: Toggle business active status
     *
     * Toggles the active/inactive status of a business partner. Inactive businesses
     * are not available for new campaigns or transactions but existing relationships
     * are preserved. Requires administrator privileges.
     *
     * @param businessId - ID of the business to toggle
     * @param toggleData - Toggle request data specifying the desired active state
     * @returns Promise resolving to updated business data
     * @throws {PersApiError} When not authenticated as admin or business not found
     *
     * @example
     * ```typescript
     * // Admin operation - deactivate problematic business
     * const updated = await sdk.business.toggleBusinessStatus('business-123', {
     *   isActive: false,
     *   reason: 'Temporary suspension for policy review'
     * });
     *
     * if (updated.isActive) {
     *   console.log('Business reactivated');
     * } else {
     *   console.log('Business temporarily deactivated');
     * }
     * ```
     */
    async toggleBusinessStatus(businessId, toggleData) {
        return this.businessService.toggleBusinessStatus(businessId, toggleData);
    }
    /**
     * Get the full business SDK for advanced operations
     *
     * Provides access to the complete BusinessSDK instance for advanced partnership
     * operations, integration management, and operations not covered by the
     * high-level manager methods.
     *
     * @returns BusinessSDK instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const businessSDK = sdk.business.getBusinessSDK();
     *
     * // Access partnership management
     * const partnerships = await businessSDK.getPartnershipAgreements();
     *
     * // Access integration features
     * const integrationStatus = await businessSDK.getIntegrationStatus('business-123');
     *
     * // Access business API directly
     * const businessApi = businessSDK.api;
     * ```
     */
    getBusinessService() {
        return this.businessService;
    }
    // ==========================================
    // BUSINESS MEMBERSHIP MANAGEMENT
    // ==========================================
    /**
     * Get all members of a business
     *
     * Retrieves all users who have access to the specified business with their roles.
     * Any member of the business can view the member list.
     *
     * @param businessId - The business UUID
     * @param options - Pagination and include options
     * @returns Promise resolving to paginated business memberships
     * @throws {PersApiError} 401 - Not authenticated
     * @throws {PersApiError} 403 - Not a member of this business
     *
     * @example
     * ```typescript
     * // Get members with user data for display
     * const { data: members } = await sdk.business.getMembers('business-123', {
     *   include: ['user']
     * });
     *
     * console.log('Business Members:');
     * members.forEach(member => {
     *   const email = member.included?.user?.email || member.userId;
     *   console.log(`- ${email}: ${member.role}`);
     * });
     * ```
     */
    async getMembers(businessId, options) {
        return this.membershipService.getMembers(businessId, options);
    }
    /**
     * Get members filtered by role
     *
     * @param businessId - The business UUID
     * @param role - The role to filter by
     * @returns Promise resolving to array of memberships with the specified role
     *
     * @example
     * ```typescript
     * const { data: owners } = await sdk.business.getMembersByRole('business-123', 'OWNER');
     * console.log(`Business has ${owners.length} owner(s)`);
     * ```
     */
    async getMembersByRole(businessId, role, options) {
        return this.membershipService.getMembersByRole(businessId, role, options);
    }
    /**
     * Add a new member to a business
     *
     * Adds a user as a member of the business with the specified role.
     * Requires ADMIN role or higher.
     *
     * @param businessId - The business UUID
     * @param userId - The user UUID to add
     * @param role - The role to assign (defaults to VIEWER)
     * @returns Promise resolving to the created membership
     * @throws {PersApiError} 401 - Not authenticated
     * @throws {PersApiError} 403 - Insufficient role (requires ADMIN)
     * @throws {PersApiError} 404 - User not found
     * @throws {PersApiError} 409 - User is already a member
     *
     * @example
     * ```typescript
     * // Add a new editor to the business
     * const newMember = await sdk.business.addMember(
     *   'business-123',
     *   'user-456',
     *   'EDITOR'
     * );
     *
     * console.log(`Added ${newMember.userId} as ${newMember.role}`);
     * ```
     */
    async addMember(businessId, userId, role = MembershipRole.VIEWER) {
        return this.membershipService.addMember(businessId, userId, role);
    }
    /**
     * Update a member's role
     *
     * Changes the role of an existing business member.
     * Requires ADMIN role or higher. Cannot demote the last OWNER.
     * Can only assign roles up to your own level (ADMIN cannot create OWNER).
     *
     * @param businessId - The business UUID
     * @param userId - The user UUID to update
     * @param newRole - The new role to assign
     * @returns Promise resolving to the updated membership
     * @throws {PersApiError} 401 - Not authenticated
     * @throws {PersApiError} 403 - Insufficient role (requires ADMIN)
     * @throws {PersApiError} 404 - Membership not found
     * @throws {PersApiError} 400 - Cannot demote last OWNER
     *
     * @example
     * ```typescript
     * // Promote an editor to admin
     * const updated = await sdk.business.updateMemberRole(
     *   'business-123',
     *   'user-456',
     *   'ADMIN'
     * );
     *
     * console.log(`${updated.userId} is now ${updated.role}`);
     * ```
     */
    async updateMemberRole(businessId, userId, newRole) {
        const result = await this.membershipService.updateMemberRole(businessId, userId, newRole);
        this.events?.emitSuccess({
            domain: 'business',
            type: 'membership_updated',
            userMessage: 'Membership role updated',
            details: { businessId, userId, role: newRole }
        });
        return result;
    }
    /**
     * Remove a member from a business
     *
     * Removes a user's access to the business.
     * Requires ADMIN role or higher. Cannot remove the last OWNER.
     *
     * @param businessId - The business UUID
     * @param userId - The user UUID to remove
     * @returns Promise resolving to success confirmation
     * @throws {PersApiError} 401 - Not authenticated
     * @throws {PersApiError} 403 - Insufficient role (requires ADMIN)
     * @throws {PersApiError} 404 - Membership not found
     * @throws {PersApiError} 400 - Cannot remove last OWNER
     *
     * @example
     * ```typescript
     * const result = await sdk.business.removeMember('business-123', 'user-456');
     *
     * if (result.success) {
     *   console.log('Member removed successfully');
     * }
     * ```
     */
    async removeMember(businessId, userId) {
        return this.membershipService.removeMember(businessId, userId);
    }
    /**
     * Get permission flags for a role
     *
     * Returns an object with boolean flags indicating what actions
     * a user with the given role can perform. Useful for UI rendering.
     *
     * @param role - The membership role (or null if not a member)
     * @returns Object with permission flags
     *
     * @example
     * ```typescript
     * const permissions = sdk.business.getPermissions('EDITOR');
     * // { canViewMembers: true, canManageMembers: false, canEditContent: true, canDeleteBusiness: false }
     *
     * if (permissions.canManageMembers) {
     *   showAddMemberButton();
     * }
     * ```
     */
    getPermissions(role) {
        return this.membershipService.getPermissions(role);
    }
    /**
     * Add a member to a business by email address
     *
     * Convenience method that creates or retrieves a user by email and adds them
     * as a member of the business. If the user doesn't exist, they will be created.
     * Requires ADMIN role or higher.
     *
     * @param businessId - The business UUID
     * @param email - The email address of the user to add
     * @param role - The role to assign (defaults to VIEWER)
     * @returns Promise resolving to the created membership
     * @throws {PersApiError} 401 - Not authenticated
     * @throws {PersApiError} 403 - Insufficient role (requires ADMIN)
     * @throws {PersApiError} 409 - User is already a member
     *
     * @example
     * ```typescript
     * // Add a new editor by email - user is created if they don't exist
     * const membership = await sdk.business.addMemberByEmail(
     *   'business-123',
     *   'newuser@example.com',
     *   'EDITOR'
     * );
     *
     * console.log(`Added user ${membership.userId} as ${membership.role}`);
     * ```
     */
    async addMemberByEmail(businessId, email, role = MembershipRole.VIEWER) {
        // POST /users is an upsert - creates user if not exists, returns existing if found
        const user = await this.userApi.createOrUpdateUser({ email });
        return this.membershipService.addMember(businessId, user.id, role);
    }
    /**
     * Get the membership service for advanced operations
     *
     * @returns BusinessMembershipService instance
     */
    getMembershipService() {
        return this.membershipService;
    }
    // ==========================================
    // 📊 CSV EXPORT
    // ==========================================
    /**
     * Admin: Export businesses to CSV
     *
     * Returns a CSV file with all business data for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns Promise resolving to CSV blob
     * @throws {PersApiError} When not authenticated as tenant admin
     *
     * @example
     * ```typescript
     * // Export all businesses
     * const csvBlob = await sdk.business.exportCSV();
     *
     * // Export businesses created in date range
     * const csvBlob = await sdk.business.exportCSV({
     *   dateFrom: '2026-01-01',
     *   dateTo: '2026-06-30'
     * });
     *
     * // Download the CSV
     * const url = URL.createObjectURL(csvBlob);
     * const a = document.createElement('a');
     * a.href = url;
     * a.download = 'businesses.csv';
     * a.click();
     * ```
     */
    async exportCSV(options) {
        return this.businessApi.exportCSV(options);
    }
}

/**
 * Campaign Manager - Clean, high-level interface for campaign operations
 *
 * Provides a comprehensive API for loyalty campaign management including campaign
 * discovery, reward claiming, business engagement tracking, and administrative
 * campaign lifecycle operations. Campaigns are the core promotional mechanisms
 * that drive user engagement and reward distribution in the loyalty ecosystem.
 *
 * @group Managers
 * @category Campaign Management
 *
 * @example Basic Campaign Operations
 * ```typescript
 * // Get all available campaigns (paginated)
 * const result = await sdk.campaigns.getCampaigns({ page: 1, limit: 20 });
 * console.log(`${result.data.length} of ${result.total} campaigns`);
 *
 * // Get specific campaign details
 * const campaign = await sdk.campaigns.getCampaignById('summer-promo-2024');
 * console.log('Campaign:', campaign.name);
 *
 * // User claims campaign reward
 * const claim = await sdk.campaigns.claimCampaign({
 *   campaignId: 'summer-promo-2024',
 *   businessId: 'partner-hotel-123'
 * });
 * ```
 *
 * @example User Campaign Management
 * ```typescript
 * // Get user's claimed campaigns
 * const userClaims = await sdk.campaigns.getUserClaims();
 *
 * console.log('My Campaign Claims:');
 * userClaims.forEach(claim => {
 *   console.log(`- ${claim.campaign.name}`);
 *   console.log(`  Rewards: ${claim.rewards?.length || 0} tokens`);
 *   console.log(`  Date: ${claim.claimedAt}`);
 * });
 *
 * // Check if user can claim specific campaign
 * const campaigns = await sdk.campaigns.getCampaigns({ active: true });
 * const eligibleCampaigns = campaigns.data.filter(c =>
 *   !userClaims.some(claim => claim.campaignId === c.id)
 * );
 * ```
 *
 * @example Administrative Operations
 * ```typescript
 * // Admin: Create seasonal campaign
 * const newCampaign = await sdk.campaigns.createCampaign({
 *   name: 'Holiday Rewards',
 *   description: 'Special holiday loyalty bonuses',
 *   startDate: '2024-12-01',
 *   endDate: '2024-12-31',
 *   isActive: true
 * });
 *
 * // Admin: Set up campaign rewards
 * await sdk.campaigns.createCampaignTokenUnit(newCampaign.id, {
 *   tokenId: 'loyalty-points',
 *   amount: 500,
 *   type: 'REWARD'
 * });
 * ```
 */
class CampaignManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
        const campaignApi = new CampaignApi(apiClient);
        this.campaignService = new CampaignService(campaignApi);
    }
    /**
     * Get campaign by ID
     *
     * Retrieves detailed information for a specific campaign including rewards,
     * business partnerships, eligibility criteria, and claiming requirements.
     *
     * @param campaignId - Unique campaign identifier
     * @param include - Relations to include: 'triggerSources', 'businesses'
     * @returns Promise resolving to campaign data with complete details
     * @throws {PersApiError} When campaign with specified ID is not found
     *
     * @example
     * ```typescript
     * try {
     *   const campaign = await sdk.campaigns.getCampaignById('summer-promo-2024');
     *
     *   console.log('Campaign Details:');
     *   console.log('Name:', campaign.name);
     *   console.log('Description:', campaign.description);
     *   console.log('Active:', campaign.isActive);
     *   console.log('Period:', `${campaign.startDate} to ${campaign.endDate}`);
     *
     *   console.log('\nRewards:');
     *   campaign.tokenUnits?.forEach(unit => {
     *     console.log(`- ${unit.amount} ${unit.token.symbol} (${unit.token.name})`);
     *   });
     *
     *   console.log('\nPartner Businesses:');
     *   campaign.businessEngagements?.forEach(engagement => {
     *     console.log(`- ${engagement.business.displayName}`);
     *   });
     *
     * } catch (error) {
     *   console.log('Campaign not found:', error.message);
     * }
     * ```
     *
     * @example With Include Relations
     * ```typescript
     * // Get campaign with trigger sources and businesses included
     * const campaign = await sdk.campaigns.getCampaignById('campaign-123', ['triggerSources', 'businesses']);
     * console.log('Trigger sources:', campaign.included?.triggerSources);
     * console.log('Businesses:', campaign.included?.businesses);
     * ```
     */
    async getCampaignById(campaignId, include) {
        return this.campaignService.getCampaignById(campaignId, include);
    }
    /**
     * Claim a campaign reward
     *
     * Claims rewards from a campaign for the authenticated user. This action
     * validates eligibility, processes reward distribution, and creates a permanent
     * claim record. Users can typically claim each campaign only once.
     *
     * @param claimRequest - Campaign claim request with campaign and business context
     * @returns Promise resolving to claim result with reward details
     * @throws {PersApiError} When campaign is not claimable or user is not eligible
     *
     * @example Simple Campaign Claim
     * ```typescript
     * try {
     *   const claim = await sdk.campaigns.claimCampaign({
     *     campaignId: 'welcome-bonus',
     *     businessId: 'partner-hotel-123'
     *   });
     *
     *   console.log('Campaign claimed successfully!');
     *   console.log('Claim ID:', claim.id);
     *   console.log('Claimed at:', claim.claimedAt);
     *
     *   if (claim.rewards?.length) {
     *     console.log('\nRewards received:');
     *     claim.rewards.forEach(reward => {
     *       console.log(`- ${reward.amount} ${reward.token.symbol}`);
     *     });
     *   }
     *
     * } catch (error) {
     *   console.log('Claim failed:', error.message);
     * }
     * ```
     *
     * @example Campaign Claim with Validation
     * ```typescript
     * // Check if campaign is still active
     * const campaign = await sdk.campaigns.getCampaignById('limited-time-offer');
     *
     * if (campaign.isActive && new Date() <= new Date(campaign.endDate)) {
     *   // Check if user already claimed
     *   const userClaims = await sdk.campaigns.getUserClaims();
     *   const alreadyClaimed = userClaims.some(c => c.campaignId === campaign.id);
     *
     *   if (!alreadyClaimed) {
     *     const claim = await sdk.campaigns.claimCampaign({
     *       campaignId: campaign.id,
     *       businessId: 'partner-business-id'
     *     });
     *     console.log('Claim successful:', claim.id);
     *   } else {
     *     console.log('Campaign already claimed');
     *   }
     * }
     * ```
     */
    async claimCampaign(claimRequest) {
        const result = await this.campaignService.claimCampaign(claimRequest);
        this.events?.emitSuccess({
            domain: 'campaign',
            type: 'claim_success',
            userMessage: 'Campaign reward claimed successfully',
            details: { campaignId: claimRequest.campaignId }
        });
        return result;
    }
    /**
     * Get user's campaign claims
     *
     * Retrieves all campaigns that the authenticated user has successfully claimed.
     * Includes claim timestamps, received rewards, and associated business context.
     * Useful for user reward history and campaign participation tracking.
     *
     * @returns Promise resolving to array of user's campaign claims
     *
     * @example
     * ```typescript
     * const userClaims = await sdk.campaigns.getUserClaims();
     *
     * console.log(`Campaign History (${userClaims.length} claims):`);
     *
     * userClaims.forEach((claim, index) => {
     *   console.log(`\n${index + 1}. ${claim.campaign.name}`);
     *   console.log(`   Claimed: ${new Date(claim.claimedAt).toLocaleDateString()}`);
     *   console.log(`   Business: ${claim.business?.displayName || 'N/A'}`);
     *
     *   if (claim.rewards?.length) {
     *     console.log(`   Rewards:`);
     *     claim.rewards.forEach(reward => {
     *       console.log(`      • ${reward.amount} ${reward.token.symbol}`);
     *     });
     *   }
     * });
     *
     * // Calculate total rewards earned
     * const totalRewards = userClaims.reduce((total, claim) => {
     *   return total + (claim.rewards?.length || 0);
     * }, 0);
     *
     * console.log(`\nTotal reward items earned: ${totalRewards}`);
     * ```
     */
    async getUserClaims(options) {
        const { include, ...paginationOptions } = options || {};
        return this.campaignService.getClaimsForLoggedUser(paginationOptions, include);
    }
    /**
     * Get campaigns with pagination support
     *
     * Returns campaigns with pagination metadata for efficient data loading.
     * Intelligent access: Public gets active only, Business gets own campaigns, Admin gets all.
     *
     * @param options - Pagination and filter options (page, limit, sortBy, sortOrder, active, tag, search, businessId, startDate, endDate, include)
     * @returns Promise resolving to paginated campaigns with metadata
     *
     * @example
     * ```typescript
     * // Get first page of campaigns
     * const result = await sdk.campaigns.getCampaigns({ page: 1, limit: 10 });
     * console.log(`Showing ${result.data.length} of ${result.total} campaigns`);
     * console.log(`Has more pages: ${result.hasMore}`);
     *
     * // Get campaigns for a specific business
     * const businessCampaigns = await sdk.campaigns.getCampaigns({
     *   businessId: 'business-123',
     *   page: 1,
     *   limit: 20
     * });
     *
     * // Search campaigns by name or description
     * const searchResults = await sdk.campaigns.getCampaigns({
     *   search: 'summer rewards',
     *   page: 1,
     *   limit: 20
     * });
     *
     * // Filter by tag
     * const taggedCampaigns = await sdk.campaigns.getCampaigns({
     *   tag: 'seasonal',
     *   active: true
     * });
     *
     * // Filter by date range
     * const dateFiltered = await sdk.campaigns.getCampaigns({
     *   startDate: new Date('2026-05-01'),
     *   endDate: new Date('2026-12-31'),
     *   active: true
     * });
     *
     * // Get campaigns with claim count
     * const withCounts = await sdk.campaigns.getCampaigns({
     *   include: ['claimCount']
     * });
     * withCounts.data.forEach(c => console.log(`${c.name}: ${c.claimCount} claims`));
     *
     * // Include related data (trigger sources, businesses, and claim count)
     * const campaignsWithRelations = await sdk.campaigns.getCampaigns({
     *   include: ['triggerSources', 'businesses', 'claimCount']
     * });
     * ```
     */
    async getCampaigns(options) {
        // Convert Date objects to ISO strings for API compatibility
        const normalizedOptions = options ? {
            ...options,
            startDate: options.startDate instanceof Date ? options.startDate.toISOString() : options.startDate,
            endDate: options.endDate instanceof Date ? options.endDate.toISOString() : options.endDate,
        } : undefined;
        return this.campaignService.getCampaigns(normalizedOptions);
    }
    /**
     * Admin: Create new campaign
     *
     * Creates a new loyalty campaign with specified configuration, rewards, and
     * business partnerships. This operation requires administrator privileges
     * and establishes a new promotional mechanism for user engagement.
     *
     * @param campaignData - Campaign configuration including title, dates, and settings
     * @returns Promise resolving to created campaign data
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * // Admin operation - create seasonal campaign
     * const campaign = await sdk.campaigns.createCampaign({
     *   name: 'Summer Travel Rewards',
     *   description: 'Earn bonus points for summer bookings',
     *   startDate: '2024-06-01T00:00:00Z',
     *   endDate: '2024-08-31T23:59:59Z',
     *   isActive: true,
     *   isTestnet: false,
     *   campaignType: 'SEASONAL'
     * });
     *
     * console.log('Campaign created:', campaign.name);
     * console.log('Campaign ID:', campaign.id);
     *
     * // Set up campaign rewards
     * await sdk.campaigns.createCampaignTokenUnit(campaign.id, {
     *   tokenId: 'loyalty-points',
     *   amount: 1000,
     *   type: 'BONUS'
     * });
     * ```
     */
    async createCampaign(campaignData) {
        return this.campaignService.createCampaign(campaignData);
    }
    /**
     * Admin: Update campaign
     *
     * Updates an existing campaign's configuration, dates, or status. This operation
     * requires administrator privileges and can modify most campaign properties
     * while preserving existing claims and relationships.
     *
     * @param campaignId - ID of the campaign to update
     * @param campaignData - Updated campaign configuration
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or campaign not found
     *
     * @example
     * ```typescript
     * // Admin operation - extend campaign duration
     * const updated = await sdk.campaigns.updateCampaign('summer-2024', {
     *   name: 'Extended Summer Travel Rewards',
     *   description: 'Earn bonus points for summer and early fall bookings',
     *   endDate: '2024-09-30T23:59:59Z',  // Extended end date
     *   isActive: true
     * });
     *
     * console.log('Campaign updated:', updated.name);
     * console.log('New end date:', updated.endDate);
     * ```
     */
    async updateCampaign(campaignId, campaignData) {
        return this.campaignService.updateCampaign(campaignId, campaignData);
    }
    /**
     * Admin: Toggle campaign active status
     *
     * Toggles the active/inactive status of a campaign. Inactive campaigns are
     * not available for new claims but existing claims remain valid. Requires
     * administrator privileges.
     *
     * @param campaignId - ID of the campaign to toggle
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or campaign not found
     *
     * @example
     * ```typescript
     * // Admin operation - temporarily disable campaign
     * const updated = await sdk.campaigns.toggleCampaignStatus('problematic-campaign');
     *
     * console.log(`Campaign ${updated.isActive ? 'activated' : 'deactivated'}`);
     * console.log('Status:', updated.isActive ? 'Active' : 'Inactive');
     * ```
     */
    async toggleCampaignStatus(campaignId) {
        return this.campaignService.toggleCampaignActive(campaignId);
    }
    /**
     * Admin: Toggle campaign testnet environment
     *
     * Toggles the testnet/mainnet environment for a campaign. Testnet campaigns
     * operate with test tokens and are used for validation before production deployment.
     * Requires administrator privileges.
     *
     * @param campaignId - ID of the campaign to toggle
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or campaign not found
     *
     * @example
     * ```typescript
     * // Admin operation - move campaign to production
     * const updated = await sdk.campaigns.toggleCampaignTestnet('test-campaign');
     *
     * console.log(`Campaign moved to ${updated.isTestnet ? 'testnet' : 'mainnet'}`);
     * ```
     */
    async toggleCampaignTestnet(campaignId) {
        return this.campaignService.toggleCampaignTestnet(campaignId);
    }
    // ==========================================
    // CAMPAIGN TRIGGER OPERATIONS
    // Rules & limits for campaign activation
    // ==========================================
    /**
     * Admin: Get all campaign triggers (paginated)
     *
     * Retrieves all campaign trigger rules that define rate limits, geo-validation,
     * conditions, and trigger types for campaign activation.
     *
     * @param options - Pagination options
     * @returns Promise resolving to paginated list of campaign triggers
     *
     * @example
     * ```typescript
     * const triggers = await sdk.campaigns.getCampaignTriggers({ page: 1, limit: 20 });
     * console.log(`Found ${triggers.total} triggers`);
     * triggers.data.forEach(trigger => {
     *   console.log(`- ${trigger.name}: max ${trigger.maxPerDayPerUser}/day`);
     * });
     * ```
     */
    async getCampaignTriggers(options) {
        return this.campaignService.getCampaignTriggers(options);
    }
    /**
     * Admin: Get campaign trigger by ID
     *
     * @param triggerId - The campaign trigger ID
     * @returns Promise resolving to the campaign trigger
     *
     * @example
     * ```typescript
     * const trigger = await sdk.campaigns.getCampaignTriggerById('trigger-123');
     * console.log('Max per day:', trigger.maxPerDayPerUser);
     * ```
     */
    async getCampaignTriggerById(triggerId) {
        return this.campaignService.getCampaignTriggerById(triggerId);
    }
    /**
     * Admin: Create a new campaign trigger
     *
     * Creates a trigger rule that defines rate limits, geo-validation, and conditions
     * for campaign activation. Triggers are created independently and then assigned
     * to campaigns via `setCampaignTrigger()`.
     *
     * @param data - Trigger configuration
     * @returns Promise resolving to created trigger
     *
     * @example
     * ```typescript
     * const trigger = await sdk.campaigns.createCampaignTrigger({
     *   name: 'Daily Check-in',
     *   maxPerDayPerUser: 1,
     *   maxPerUser: 100,
     *   minCooldownSeconds: 3600,
     *   maxGeoDistanceInMeters: 50,
     *   triggerType: 'CLAIM_BY_USER'
     * });
     *
     * // Then assign to campaign
     * await sdk.campaigns.setCampaignTrigger(campaignId, trigger.id);
     * ```
     */
    async createCampaignTrigger(data) {
        const result = await this.campaignService.createCampaignTrigger(data);
        this.events?.emitSuccess({
            domain: 'campaign',
            type: 'campaign_trigger_created',
            userMessage: `Trigger "${data.name}" created successfully`,
            details: { triggerId: result.id, name: data.name }
        });
        return result;
    }
    /**
     * Admin: Update an existing campaign trigger
     *
     * @param triggerId - The campaign trigger ID
     * @param data - Updated trigger configuration (partial)
     * @returns Promise resolving to updated trigger
     *
     * @example
     * ```typescript
     * const updated = await sdk.campaigns.updateCampaignTrigger('trigger-123', {
     *   maxPerDayPerUser: 5,
     *   minCooldownSeconds: 1800
     * });
     * ```
     */
    async updateCampaignTrigger(triggerId, data) {
        const result = await this.campaignService.updateCampaignTrigger(triggerId, data);
        this.events?.emitSuccess({
            domain: 'campaign',
            type: 'campaign_trigger_updated',
            userMessage: 'Trigger updated successfully',
            details: { triggerId, updates: Object.keys(data) }
        });
        return result;
    }
    /**
     * Admin: Delete a campaign trigger
     *
     * @param triggerId - The campaign trigger ID
     * @returns Promise resolving to success status
     *
     * @example
     * ```typescript
     * await sdk.campaigns.deleteCampaignTrigger('trigger-123');
     * ```
     */
    async deleteCampaignTrigger(triggerId) {
        const result = await this.campaignService.deleteCampaignTrigger(triggerId);
        this.events?.emitSuccess({
            domain: 'campaign',
            type: 'campaign_trigger_deleted',
            userMessage: 'Trigger deleted successfully',
            details: { triggerId }
        });
        return result;
    }
    /**
     * Admin: Assign a trigger to a campaign
     *
     * Associates a trigger rule with a campaign. A campaign can have only one trigger
     * at a time. Assigning a new trigger replaces any existing trigger.
     *
     * @param campaignId - ID of the campaign
     * @param triggerId - ID of the trigger to associate
     * @returns Promise resolving to updated campaign
     *
     * @example
     * ```typescript
     * const updated = await sdk.campaigns.setCampaignTrigger(
     *   'campaign-123',
     *   'trigger-456'
     * );
     * console.log('Trigger assigned:', updated.trigger?.name);
     * ```
     */
    async setCampaignTrigger(campaignId, triggerId) {
        const result = await this.campaignService.setCampaignTrigger(campaignId, triggerId);
        this.events?.emitSuccess({
            domain: 'campaign',
            type: 'campaign_trigger_assigned',
            userMessage: 'Trigger assigned to campaign',
            details: { campaignId, triggerId }
        });
        return result;
    }
    /**
     * Admin: Remove a trigger from a campaign
     *
     * Removes the trigger rule from a campaign. The trigger itself is not deleted
     * and can be reassigned to other campaigns.
     *
     * @param campaignId - ID of the campaign
     * @param triggerId - ID of the trigger to remove
     * @returns Promise resolving to updated campaign
     *
     * @example
     * ```typescript
     * const updated = await sdk.campaigns.removeCampaignTrigger(
     *   'campaign-123',
     *   'trigger-456'
     * );
     * console.log('Trigger removed:', updated.trigger === null);
     * ```
     */
    async removeCampaignTrigger(campaignId, triggerId) {
        const result = await this.campaignService.removeCampaignTrigger(campaignId, triggerId);
        this.events?.emitSuccess({
            domain: 'campaign',
            type: 'campaign_trigger_removed',
            userMessage: 'Trigger removed from campaign',
            details: { campaignId, triggerId }
        });
        return result;
    }
    // ==========================================
    // TOKEN UNIT OPERATIONS
    // ==========================================
    /**
     * Admin: Create campaign token unit
     *
     * Adds a token reward unit to a campaign, specifying the token type and amount
     * that users will receive when claiming the campaign. Multiple token units
     * can be added to create multi-reward campaigns. Requires administrator privileges.
     *
     * @param campaignId - ID of the campaign
     * @param tokenUnit - Token unit configuration including token type and amount
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * // Admin operation - add loyalty points reward
     * const updated = await sdk.campaigns.createCampaignTokenUnit('summer-promo', {
     *   tokenId: 'loyalty-points',
     *   amount: 500,
     *   type: 'REWARD'
     * });
     *
     * console.log('Token unit added to campaign');
     * console.log('Reward units:', updated.tokenUnits?.length);
     *
     * // Add multiple reward types
     * await sdk.campaigns.createCampaignTokenUnit('summer-promo', {
     *   tokenId: 'bonus-credits',
     *   amount: 100,
     *   type: 'BONUS'
     * });
     * ```
     */
    async createCampaignTokenUnit(campaignId, tokenUnit) {
        return this.campaignService.createCampaignTokenUnit(campaignId, tokenUnit);
    }
    /**
     * Admin: Delete campaign token unit
     *
     * Removes a token reward unit from a campaign. This affects future claims
     * but does not impact rewards that have already been distributed. Requires
     * administrator privileges.
     *
     * @param campaignId - ID of the campaign
     * @param tokenUnitId - ID of the token unit to remove
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or entities not found
     *
     * @example
     * ```typescript
     * // Admin operation - remove token unit from campaign
     * const updated = await sdk.campaigns.deleteCampaignTokenUnit(
     *   'summer-promo',
     *   'token-unit-123'
     * );
     *
     * console.log('Token unit removed from campaign');
     * console.log('Remaining reward units:', updated.tokenUnits?.length);
     * ```
     */
    async deleteCampaignTokenUnit(campaignId, tokenUnitId) {
        return this.campaignService.deleteCampaignTokenUnit(campaignId, tokenUnitId);
    }
    /**
     * Admin: Add business engagement to campaign
     *
     * Associates a business partner with a campaign, enabling the business to
     * participate in the campaign and allowing users to claim rewards through
     * that business. Requires administrator privileges.
     *
     * @param campaignId - ID of the campaign
     * @param engagement - Business engagement configuration
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * // Admin operation - add business partner to campaign
     * const updated = await sdk.campaigns.addBusinessEngagementToCampaign('summer-promo', {
     *   businessId: 'partner-hotel-123',
     *   engagementType: 'PROMOTION_PARTNER',
     *   isActive: true,
     *   startDate: '2024-06-01',
     *   endDate: '2024-08-31'
     * });
     *
     * console.log('Business partner added to campaign');
     * console.log('Partner businesses:', updated.businessEngagements?.length);
     * ```
     */
    async addBusinessEngagementToCampaign(campaignId, engagement) {
        return this.campaignService.addBusinessEngagementToCampaign(campaignId, engagement);
    }
    /**
     * Admin: Update campaign business engagement
     *
     * Updates an existing business engagement within a campaign, modifying
     * participation parameters, dates, or status. Requires administrator privileges.
     *
     * @param campaignId - ID of the campaign
     * @param engagementId - ID of the engagement to update
     * @param engagement - Updated engagement configuration
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or entities not found
     *
     * @example
     * ```typescript
     * // Admin operation - update business engagement terms
     * const updated = await sdk.campaigns.updateCampaignBusinessEngagement(
     *   'summer-promo',
     *   'engagement-456',
     *   {
     *     engagementType: 'PREMIUM_PARTNER',
     *     isActive: true,
     *     endDate: '2024-09-30'  // Extended participation
     *   }
     * );
     *
     * console.log('Business engagement updated');
     * ```
     */
    async updateCampaignBusinessEngagement(campaignId, engagementId, engagement) {
        return this.campaignService.updateCampaignBusinessEngagement(campaignId, engagementId, engagement);
    }
    /**
     * Admin: Delete campaign business engagement
     *
     * Removes a business engagement from a campaign, ending the business's
     * participation in the campaign. Existing claims through this business
     * remain valid. Requires administrator privileges.
     *
     * @param campaignId - ID of the campaign
     * @param engagementId - ID of the engagement to remove
     * @returns Promise resolving to updated campaign data
     * @throws {PersApiError} When not authenticated as admin or entities not found
     *
     * @example
     * ```typescript
     * // Admin operation - remove business from campaign
     * const updated = await sdk.campaigns.deleteCampaignBusinessEngagement(
     *   'summer-promo',
     *   'engagement-456'
     * );
     *
     * console.log('Business engagement removed');
     * console.log('Remaining partners:', updated.businessEngagements?.length);
     * ```
     */
    async deleteCampaignBusinessEngagement(campaignId, engagementId) {
        return this.campaignService.deleteCampaignBusinessEngagement(campaignId, engagementId);
    }
    /**
     * Admin: Get campaign claims with optional filters
     *
     * Retrieves campaign claims with optional filtering by campaign, user, business, or date range.
     * This operation requires administrator privileges and provides system-wide
     * visibility into campaign performance and user engagement.
     *
     * @param filters - Optional filters and pagination options
     * @param filters.campaignId - Filter by specific campaign ID
     * @param filters.userId - Filter by specific user ID
     * @param filters.businessId - Filter by specific business ID
     * @param filters.dateFrom - Filter claims from this date (inclusive)
     * @param filters.dateTo - Filter claims until this date (inclusive)
     * @param filters.page - Page number for pagination
     * @param filters.limit - Items per page
     * @param filters.sortBy - Field to sort by
     * @param filters.sortOrder - Sort direction ('asc' or 'desc')
     * @param include - Optional relations to include for enrichment
     * @returns Promise resolving to paginated campaign claims
     * @throws {PersApiError} When not authenticated as administrator
     *
     * @example Get All Claims
     * ```typescript
     * const { data: allClaims, pagination } = await sdk.campaigns.getCampaignClaims();
     * console.log(`Total: ${pagination.total}`);
     * ```
     *
     * @example Filter by User
     * ```typescript
     * const { data: userClaims } = await sdk.campaigns.getCampaignClaims({ userId: 'user-123' });
     * ```
     *
     * @example Filter by Date Range
     * ```typescript
     * // Get claims within a specific date range
     * const { data: monthClaims } = await sdk.campaigns.getCampaignClaims({
     *   dateFrom: new Date('2024-01-01'),
     *   dateTo: new Date('2024-01-31'),
     *   limit: 100
     * });
     * console.log(`${monthClaims.length} claims in January 2024`);
     * ```
     *
     * @example With Pagination and Campaign Filter
     * ```typescript
     * const { data: page1 } = await sdk.campaigns.getCampaignClaims({
     *   campaignId: 'campaign-456',
     *   page: 1,
     *   limit: 50
     * });
     * ```
     */
    async getCampaignClaims(filters, include) {
        return this.campaignService.getCampaignClaims(filters, include);
    }
    /**
     * Admin: Get campaign claims by user ID
     *
     * Retrieves all campaign claims for a specific user. This operation requires
     * administrator privileges and is used for user support, account analysis,
     * and individual user engagement tracking.
     *
     * @param userId - ID of the user to get claims for
     * @returns Promise resolving to array of user's campaign claims
     * @throws {PersApiError} When not authenticated as admin or user not found
     *
     * @example
     * ```typescript
     * // Admin operation - analyze user's campaign participation
     * const userClaims = await sdk.campaigns.getCampaignClaimsByUserId('user-123');
     *
     * console.log(`User Campaign Activity:`);
     * console.log(`Total campaigns claimed: ${userClaims.length}`);
     *
     * userClaims.forEach((claim, index) => {
     *   console.log(`\n${index + 1}. ${claim.campaign.name}`);
     *   console.log(`   Date: ${claim.claimedAt}`);
     *   console.log(`   Business: ${claim.business?.displayName || 'Direct'}`);
     *   console.log(`   Rewards: ${claim.rewards?.length || 0} items`);
     * });
     *
     * // Calculate user engagement metrics
     * const totalRewards = userClaims.reduce((sum, claim) =>
     *   sum + (claim.rewards?.length || 0), 0
     * );
     * console.log(`\nTotal rewards earned: ${totalRewards}`);
     * ```
     */
    async getCampaignClaimsByUserId(userId, options, include) {
        return this.campaignService.getCampaignClaimsByUserId(userId, options, include);
    }
    /**
     * Admin: Get campaign claims by business ID
     *
     * Retrieves all campaign claims processed through a specific business partner.
     * This operation requires administrator privileges and is used for business
     * partnership analysis and performance tracking.
     *
     * @param businessId - ID of the business to get claims for
     * @returns Promise resolving to array of business's campaign claims
     * @throws {PersApiError} When not authenticated as admin or business not found
     *
     * @example
     * ```typescript
     * // Admin operation - analyze business partnership performance
     * const businessClaims = await sdk.campaigns.getCampaignClaimsByBusinessId('partner-hotel-123');
     *
     * console.log(`Business Partnership Analysis:`);
     * console.log(`Claims processed: ${businessClaims.length}`);
     *
     * // Analyze claims by campaign
     * const claimsByCampaign = businessClaims.reduce((acc, claim) => {
     *   const campaignName = claim.campaign.name;
     *   acc[campaignName] = (acc[campaignName] || 0) + 1;
     *   return acc;
     * }, {});
     *
     * console.log('\nCampaign Performance:');
     * Object.entries(claimsByCampaign).forEach(([campaign, count]) => {
     *   console.log(`${campaign}: ${count} claims`);
     * });
     *
     * // Calculate partnership value
     * const totalRewardsDistributed = businessClaims.reduce((sum, claim) =>
     *   sum + (claim.rewards?.length || 0), 0
     * );
     * console.log(`\nTotal rewards distributed: ${totalRewardsDistributed}`);
     * ```
     */
    async getCampaignClaimsByBusinessId(businessId, options, include) {
        return this.campaignService.getCampaignClaimsByBusinessId(businessId, options, include);
    }
    // ==========================================
    // TRIGGER SOURCE ASSIGNMENT
    // Note: TriggerSource CRUD is in TriggerSourceManager (sdk.triggerSources)
    // ==========================================
    /**
     * Admin: Assign a trigger source to a campaign
     *
     * Associates a trigger source with a campaign, enabling the trigger source
     * to activate campaign rewards when triggered. A campaign can have multiple
     * trigger sources. Requires administrator privileges.
     *
     * Note: To create/update/delete trigger sources, use `sdk.triggerSources`.
     *
     * @param campaignId - Campaign UUID
     * @param triggerSourceId - Trigger source UUID
     * @returns Promise resolving to updated campaign with trigger source assigned
     * @throws {PersApiError} When not authenticated as admin or entities not found
     *
     * @example
     * ```typescript
     * // Create trigger source first
     * const source = await sdk.triggerSources.create({
     *   type: 'QR_CODE',
     *   name: 'Store Entrance QR'
     * });
     *
     * // Then assign to campaign
     * const updated = await sdk.campaigns.assignTriggerSource(
     *   'campaign-123',
     *   source.id
     * );
     *
     * console.log('Trigger source assigned:', updated.triggerSourceIds.length);
     * ```
     */
    async assignTriggerSource(campaignId, triggerSourceId) {
        const result = await this.campaignService.assignTriggerSourceToCampaign(campaignId, triggerSourceId);
        this.events?.emitSuccess({
            domain: 'campaign',
            type: 'trigger_source_assigned',
            userMessage: 'Trigger source assigned to campaign',
            details: { campaignId, triggerSourceId }
        });
        return result;
    }
    /**
     * Admin: Remove a trigger source from a campaign
     *
     * Removes the association between a trigger source and a campaign. The trigger
     * source itself is not deleted and can be reassigned to other campaigns.
     * Requires administrator privileges.
     *
     * @param campaignId - Campaign UUID
     * @param triggerSourceId - Trigger source UUID
     * @returns Promise resolving to updated campaign with trigger source removed
     * @throws {PersApiError} When not authenticated as admin or entities not found
     *
     * @example
     * ```typescript
     * // Remove a trigger source from a campaign
     * const updated = await sdk.campaigns.removeTriggerSource(
     *   'campaign-123',
     *   'source-456'
     * );
     *
     * console.log('Remaining trigger source IDs:', updated.triggerSourceIds.length);
     * ```
     */
    async removeTriggerSource(campaignId, triggerSourceId) {
        return this.campaignService.removeTriggerSourceFromCampaign(campaignId, triggerSourceId);
    }
    /**
     * Admin: Export campaigns as CSV
     *
     * Generates a comprehensive CSV export of all tenant campaigns for
     * external analysis, reporting, or compliance purposes. This operation
     * requires administrator privileges and creates a downloadable file.
     *
     * @param options - Optional date range filters
     * @param options.dateFrom - Start date filter (ISO date string)
     * @param options.dateTo - End date filter (ISO date string)
     * @returns Promise resolving to CSV blob for download
     * @throws {PersApiError} When not authenticated as administrator or export fails
     *
     * @example Export All Campaigns
     * ```typescript
     * const csvBlob = await sdk.campaigns.exportCSV();
     * const downloadUrl = URL.createObjectURL(csvBlob);
     * ```
     *
     * @example Export Campaigns Within Date Range
     * ```typescript
     * const csvBlob = await sdk.campaigns.exportCSV({
     *   dateFrom: '2024-01-01',
     *   dateTo: '2024-12-31'
     * });
     * ```
     */
    async exportCSV(options) {
        const campaignApi = new CampaignApi(this.apiClient);
        return campaignApi.exportCSV(options);
    }
    /**
     * Admin: Export campaign claims as CSV
     *
     * Generates a comprehensive CSV export of all campaign claim records for
     * external analysis, reporting, or compliance purposes. This operation
     * requires administrator privileges and creates a downloadable file.
     *
     * @param options - Optional date range filters
     * @param options.dateFrom - Start date filter (ISO date string)
     * @param options.dateTo - End date filter (ISO date string)
     * @returns Promise resolving to CSV blob for download
     * @throws {PersApiError} When not authenticated as administrator or export fails
     *
     * @example Export All Claims
     * ```typescript
     * const csvBlob = await sdk.campaigns.exportClaimsCSV();
     * const downloadUrl = URL.createObjectURL(csvBlob);
     * ```
     */
    async exportClaimsCSV(options) {
        const campaignApi = new CampaignApi(this.apiClient);
        return campaignApi.exportClaimsCSV(options);
    }
    /**
     * Get the full campaign service for advanced operations
     *
     * Provides access to the complete CampaignService instance for advanced campaign
     * operations, custom triggers, analytics, and operations not covered by the
     * high-level manager methods.
     *
     * @returns CampaignService instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const campaignService = sdk.campaigns.getCampaignService();
     *
     * // Access advanced campaign analytics
     * const analytics = await campaignService.getCampaignAnalytics('campaign-123');
     *
     * // Access custom trigger management
     * const customTriggers = await campaignService.getCustomTriggers();
     *
     * // Access campaign API directly
     * const campaignApi = campaignService.api;
     *
     * // Use advanced claim validation
     * const eligibility = await campaignService.validateClaimEligibility('user-123', 'campaign-456');
     * ```
     */
    getCampaignService() {
        return this.campaignService;
    }
}

/**
 * Redemption Manager - Clean, high-level interface for redemption operations
 *
 * Provides a comprehensive API for loyalty redemption management including discovering
 * available rewards, redeeming offers, tracking redemption history, and administrative
 * redemption lifecycle operations. Redemptions convert loyalty tokens into valuable
 * rewards, discounts, and benefits for users in the loyalty ecosystem.
 *
 * @group Managers
 * @category Redemption Management
 *
 * @example Basic Redemption Operations
 * ```typescript
 * // Browse available redemption offers
 * const { data: offers } = await sdk.redemptions.getRedemptions();
 * console.log(`${offers.length} redemption offers available`);
 *
 * // Redeem a specific offer
 * const redemptionResult = await sdk.redemptions.redeem('discount-10-percent');
 * console.log('Redemption successful:', redemptionResult.success);
 *
 * // Check redemption history
 * const { data: history } = await sdk.redemptions.getUserRedemptions();
 * console.log(`You have redeemed ${history.length} offers`);
 * ```
 *
 * @example Redemption Discovery
 * ```typescript
 * // Get available redemption types/categories
 * const types = await sdk.redemptions.getRedemptionTypes();
 *
 * // Browse offers by category
 * const discounts = offers.filter(offer =>
 *   offer.redemptionType?.name?.includes('Discount')
 * );
 *
 * const products = offers.filter(offer =>
 *   offer.redemptionType?.name?.includes('Product')
 * );
 *
 * console.log(`${discounts.length} discounts, ${products.length} products available`);
 * ```
 *
 * @example Administrative Operations
 * ```typescript
 * // Admin: Create new redemption offer
 * const newOffer = await sdk.redemptions.createRedemption({
 *   title: 'Free Coffee Voucher',
 *   description: 'Redeem for one free coffee at partner cafes',
 *   redemptionTypeId: 'voucher-type-id',
 *   isActive: true
 * });
 *
 * // Admin: Set redemption cost
 * await sdk.redemptions.createRedemptionTokenUnit(newOffer.id, {
 *   tokenId: 'loyalty-points',
 *   amount: 250,
 *   type: 'COST'
 * });
 * ```
 */
class RedemptionManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
        const redemptionApi = new RedemptionApi(apiClient);
        this.redemptionService = new RedemptionService(redemptionApi);
    }
    /**
     * Get redemption offers
     *
     * Retrieves redemption offers based on user permissions and optional filters.
     * The results returned depend on the authenticated user's role:
     *
     * **Regular Users:** See only active redemption offers available for purchase
     * **Administrators:** Can see all redemptions and filter by active/inactive status
     *
     * Active redemptions include discounts, vouchers, products, and services that
     * can be purchased using loyalty tokens. The backend automatically enforces
     * permission-based filtering.
     *
     * @param options - Optional filters and pagination:
     *   - `active`: Filter by active status (true/false). Admins only - regular users always see active offers
     *   - `search`: Free text search across name, description, tags (case-insensitive)
     *   - Pagination options: `page`, `limit`, `sortBy`, `sortOrder`
     * @returns Promise resolving to paginated redemption offers
     *
     * @example Regular User - Browse Active Offers
     * ```typescript
     * // Regular users automatically see only active offers
     * const { data: offers, pagination } = await sdk.redemptions.getRedemptions();
     *
     * console.log('Available Redemption Offers:');
     * console.log(`Showing ${offers.length} of ${pagination.total} offers`);
     *
     * offers.forEach(offer => {
     *   console.log(`\n${offer.name}`);
     *   console.log(`${offer.description}`);
     *   console.log(`Type: ${offer.type?.name || 'General'}`);
     *
     *   if (offer.tokenUnits?.length) {
     *     console.log('Cost:');
     *     offer.tokenUnits.forEach(unit => {
     *       console.log(`   ${unit.amount} ${unit.token.symbol}`);
     *     });
     *   }
     *
     *   if (offer.availableQuantity !== undefined) {
     *     console.log(`Available: ${offer.availableQuantity}`);
     *   }
     * });
     *
     * // Filter offers by affordability
     * const userBalance = 1000;
     * const affordableOffers = offers.filter(offer =>
     *   offer.tokenUnits?.every(unit => unit.amount <= userBalance)
     * );
     *
     * console.log(`\n${affordableOffers.length} offers within your budget`);
     * ```
     *
     * @example Search Redemptions
     * ```typescript
     * // Search by text (matches name, description, tags)
     * const { data: coffeeOffers } = await sdk.redemptions.getRedemptions({
     *   search: 'coffee'
     * });
     *
     * console.log(`Found ${coffeeOffers.length} coffee-related offers`);
     *
     * // Search with sorting
     * const { data: sortedResults } = await sdk.redemptions.getRedemptions({
     *   search: 'discount',
     *   sortBy: 'name',
     *   sortOrder: 'ASC'
     * });
     * ```
     *
     * @example Admin - Manage All Redemptions
     * ```typescript
     * // Admins can see all redemptions and filter by status
     * const { data: allRedemptions } = await sdk.redemptions.getRedemptions();
     * const { data: activeOnly } = await sdk.redemptions.getRedemptions({ active: true });
     * const { data: inactiveOnly } = await sdk.redemptions.getRedemptions({ active: false });
     *
     * console.log('Redemption Inventory:');
     * console.log(`Total offers: ${allRedemptions.length}`);
     * console.log(`Active offers: ${activeOnly.length}`);
     * console.log(`Inactive offers: ${inactiveOnly.length}`);
     *
     * // Search inactive redemptions
     * const { data: inactiveVouchers } = await sdk.redemptions.getRedemptions({
     *   active: false,
     *   search: 'voucher'
     * });
     * ```
     *
     * @example Pagination
     * ```typescript
     * // Load first page
     * const { data: firstPage, pagination } = await sdk.redemptions.getRedemptions({
     *   page: 1,
     *   limit: 10
     * });
     *
     * console.log(`Page ${pagination.page} of ${pagination.totalPages}`);
     * console.log(`${firstPage.length} offers on this page`);
     *
     * // Load next page if available
     * if (pagination.hasNextPage) {
     *   const { data: nextPage } = await sdk.redemptions.getRedemptions({
     *     page: pagination.page + 1,
     *     limit: 10
     *   });
     * }
     * ```
     */
    async getRedemptions(options) {
        return this.redemptionService.getRedemptions(options);
    }
    /**
     * Get a specific redemption by ID
     *
     * Retrieves detailed information about a specific redemption offer.
     * Useful when you already have the redemption ID and need full details.
     *
     * @param id - Redemption UUID
     * @param include - Optional relations to include: 'redeemCount'
     * @returns Promise resolving to the redemption
     *
     * @example Basic usage
     * ```typescript
     * const redemption = await sdk.redemptions.getRedemptionById('7baf4d39-0bd6-45ca-9c66-8c0d655767c3');
     * console.log(redemption.name);
     * console.log(redemption.description);
     * ```
     *
     * @example With redeem count
     * ```typescript
     * const redemption = await sdk.redemptions.getRedemptionById(
     *   'redemption-123',
     *   ['redeemCount']
     * );
     * console.log(`${redemption.name} has been redeemed ${redemption.redeemCount} times`);
     * ```
     *
     * @example Get redemption details for display
     * ```typescript
     * const redemption = await sdk.redemptions.getRedemptionById(id);
     *
     * console.log('Redemption Details:');
     * console.log(`Name: ${redemption.name}`);
     * console.log(`Description: ${redemption.description}`);
     * console.log(`Active: ${redemption.isActive}`);
     *
     * if (redemption.tokenUnits?.length) {
     *   console.log('Cost:');
     *   redemption.tokenUnits.forEach(unit => {
     *     console.log(`  ${unit.amount} ${unit.token.symbol}`);
     *   });
     * }
     * ```
     */
    async getRedemptionById(id, include) {
        return this.redemptionService.getRedemptionById(id, include);
    }
    /**
     * Get available redemption types
     *
     * Retrieves all redemption type categories that classify different kinds of
     * rewards and offers. Types help users navigate and filter redemption options
     * based on their preferences and needs.
     *
     * @returns Promise resolving to paginated redemption type definitions
     *
     * @example
     * ```typescript
     * const { data: redemptionTypes } = await sdk.redemptions.getRedemptionTypes();
     *
     * console.log('Redemption Categories:');
     * redemptionTypes.forEach(type => {
     *   console.log(`- ${type.name}: ${type.description}`);
     * });
     *
     * // Use for filtering UI
     * const typeFilter = redemptionTypes.map(type => ({
     *   id: type.id,
     *   label: type.name,
     *   description: type.description
     * }));
     *
     * // Find specific redemption types
     * const discountType = redemptionTypes.find(t =>
     *   t.name.toLowerCase().includes('discount')
     * );
     * const voucherType = redemptionTypes.find(t =>
     *   t.name.toLowerCase().includes('voucher')
     * );
     * ```
     */
    async getRedemptionTypes(options) {
        return this.redemptionService.getRedemptionTypes(options);
    }
    /**
     * Redeem a redemption offer
     *
     * Executes the redemption of a specific offer for the authenticated user.
     * This action validates eligibility, deducts required tokens, and provides
     * redemption confirmation details. Creates a permanent redemption record
     * and may generate voucher codes or instructions.
     *
     * Supports dynamic context for personalized NFT generation when the redemption
     * issues ERC721 tokens. Context can include validity dates, AI prompt placeholders,
     * and custom metadata.
     *
     * @param redemptionId - ID of the redemption offer to redeem
     * @param context - Optional dynamic context for NFT personalization (validity dates, AI prompts)
     * @returns Promise resolving to redemption result with confirmation details
     * @throws {PersApiError} When redemption is not available or user lacks required tokens
     *
     * @example Basic Redemption
     * ```typescript
     * try {
     *   const result = await sdk.redemptions.redeem('coffee-voucher-123');
     *
     *   if (result.success) {
     *     console.log('Redemption Successful!');
     *     console.log('Confirmation ID:', result.id);
     *     console.log('Redeemed at:', result.redeemedAt);
     *
     *     // Display redemption details
     *     if (result.voucherCode) {
     *       console.log('Voucher Code:', result.voucherCode);
     *       console.log('Present this code to redeem your reward');
     *     }
     *
     *     if (result.instructions) {
     *       console.log('Instructions:', result.instructions);
     *     }
     *
     *     // Show tokens deducted
     *     if (result.tokensUsed?.length) {
     *       console.log('\nTokens Used:');
     *       result.tokensUsed.forEach(token => {
     *         console.log(`- ${token.amount} ${token.symbol}`);
     *       });
     *     }
     *
     *   } else {
     *     console.log('Redemption failed:', result.error);
     *   }
     *
     * } catch (error) {
     *   console.log('Redemption error:', error.message);
     *
     *   // Handle specific error cases
     *   if (error.message.includes('insufficient')) {
     *     console.log('You need more loyalty points for this redemption');
     *   } else if (error.message.includes('unavailable')) {
     *     console.log('This offer is no longer available');
     *   }
     * }
     * ```
     *
     * @example Redemption with Dynamic Context (Personalized NFT)
     * ```typescript
     * // Event ticket with dynamic validity and personalization
     * const result = await sdk.redemptions.redeem('event-ticket-123', {
     *   // Validity - token valid for event duration + 2 hours
     *   validityDate: new Date().toISOString(),
     *   validityDuration: 8,  // 8 hours
     *
     *   // AI prompt context - available as {{context.xxx}} in prompts
     *   attendeeName: 'John Doe',
     *   ticketTier: 'VIP',
     *   seatSection: 'A12',
     * });
     *
     * // Hotel stay NFT with check-in/check-out dates
     * const hotelResult = await sdk.redemptions.redeem('hotel-nft-456', {
     *   validityDate: '2026-04-15T14:00:00Z',      // Check-in
     *   validityEndDate: '2026-04-20T11:00:00Z',   // Check-out
     *   guestName: 'Jane Smith',
     *   roomNumber: '305',
     *   roomType: 'Suite',
     * });
     * ```
     *
     * @example Redemption with Pre-Validation
     * ```typescript
     * // Get offer details first
     * const { data: offers } = await sdk.redemptions.getRedemptions();
     * const targetOffer = offers.find(o => o.id === 'premium-discount');
     *
     * if (targetOffer) {
     *   // Check if user can afford the redemption
     *   const userTokens = await sdk.users.getUserTokenBalances();
     *   const canAfford = targetOffer.tokenUnits?.every(unit => {
     *     const userBalance = userTokens.find(t => t.tokenId === unit.tokenId);
     *     return userBalance && userBalance.amount >= unit.amount;
     *   });
     *
     *   if (canAfford) {
     *     const result = await sdk.redemptions.redeem(targetOffer.id);
     *     console.log('Redemption processed:', result.success);
     *   } else {
     *     console.log('Insufficient tokens for this redemption');
     *   }
     * }
     * ```
     */
    async redeem(redemptionId, context) {
        const request = context
            ? { redemptionId, context }
            : { redemptionId };
        const result = await this.redemptionService.redeemRedemption(request);
        this.events?.emitSuccess({
            domain: 'redemption',
            type: 'redeem_success',
            userMessage: 'Reward redeemed successfully',
            details: { redemptionId, hasContext: !!context }
        });
        return result;
    }
    /**
     * Get user's redemption history
     *
     * Retrieves all redemptions that the authenticated user has successfully
     * completed. Includes redemption details, timestamps, voucher codes, and
     * token usage. Useful for user account history and support purposes.
     *
     * @returns Promise resolving to paginated user redemption records
     *
     * @example
     * ```typescript
     * const { data: userRedemptions, pagination } = await sdk.redemptions.getUserRedemptions();
     *
     * console.log(`Redemption History (${userRedemptions.length} of ${pagination.total} items):`);
     *
     * userRedemptions.forEach((redemption, index) => {
     *   console.log(`\n${index + 1}. ${redemption.redemption.name}`);
     *   console.log(`   Date: ${new Date(redemption.redeemedAt).toLocaleDateString()}`);
     *   console.log(`   Type: ${redemption.redemption.type?.name || 'General'}`);
     *
     *   if (redemption.voucherCode) {
     *     console.log(`   Voucher: ${redemption.voucherCode}`);
     *   }
     *
     *   if (redemption.tokensUsed?.length) {
     *     console.log(`   Cost:`);
     *     redemption.tokensUsed.forEach(token => {
     *       console.log(`      ${token.amount} ${token.symbol}`);
     *     });
     *   }
     *
     *   // Show status if available
     *   if (redemption.status) {
     *     console.log(`   Status: ${redemption.status}`);
     *   }
     * });
     *
     * // Calculate redemption statistics
     * const totalRedemptions = userRedemptions.length;
     * const recentRedemptions = userRedemptions.filter(r =>
     *   new Date(r.redeemedAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
     * ).length;
     *
     * console.log(`\nStatistics:`);
     * console.log(`Total redemptions: ${totalRedemptions}`);
     * console.log(`This month: ${recentRedemptions}`);
     *
     * // Group by redemption type
     * const byType = userRedemptions.reduce((acc, r) => {
     *   const type = r.redemption.redemptionType?.name || 'Other';
     *   acc[type] = (acc[type] || 0) + 1;
     *   return acc;
     * }, {});
     *
     * console.log(`\nBy category:`);
     * Object.entries(byType).forEach(([type, count]) => {
     *   console.log(`${type}: ${count} redemptions`);
     * });
     * ```
     *
     * @example Filter by Status
     * ```typescript
     * import { ProcessRecordStatus } from '@explorins/pers-sdk';
     *
     * // Get only completed redemptions
     * const { data: completed } = await sdk.redemptions.getUserRedemptions(
     *   { status: ProcessRecordStatus.COMPLETED }
     * );
     * ```
     */
    async getUserRedemptions(filters, options, include) {
        return this.redemptionService.getUserRedeems(filters, options, include);
    }
    /**
     * Admin: Get all redemption redeems with filtering and enriched data
     *
     * Retrieves all redemption redeems across the platform with filtering capabilities.
     * This is an admin-level operation that allows monitoring and analytics of redemption
     * activity. Supports filtering by user, redemption offer, date range, and enrichment of related entities.
     *
     * @param filters - Filter options (userId, redemptionId, businessId, status, dateFrom, dateTo, pagination)
     * @param filters.userId - Filter by specific user ID
     * @param filters.redemptionId - Filter by specific redemption offer ID
     * @param filters.businessId - Filter by specific business ID
     * @param filters.status - Filter by redeem status
     * @param filters.dateFrom - Filter redeems from this date (inclusive)
     * @param filters.dateTo - Filter redeems until this date (inclusive)
     * @param filters.page - Page number for pagination
     * @param filters.limit - Items per page
     * @param filters.sortBy - Field to sort by
     * @param filters.sortOrder - Sort direction ('asc' or 'desc')
     * @param include - Optional relations to include for enrichment
     * @returns Promise resolving to paginated list of redemption redeems
     * @throws {PersApiError} When not authenticated as admin
     *
     * @example Get All Redeems
     * ```typescript
     * // Admin operation - get all redemption redeems
     * const { data: allRedeems, pagination } = await sdk.redemptions.getRedemptionRedeems();
     *
     * console.log(`Total redeems: ${pagination.total}`);
     * console.log(`Page ${pagination.page} of ${pagination.pages}`);
     * ```
     *
     * @example Filter by User
     * ```typescript
     * // Get redeems for specific user
     * const { data: userRedeems } = await sdk.redemptions.getRedemptionRedeems({
     *   userId: 'user-123',
     *   limit: 50
     * });
     *
     * console.log(`User has ${userRedeems.length} redemptions`);
     * ```
     *
     * @example Filter by Date Range
     * ```typescript
     * // Get redeems within a specific date range
     * const { data: recentRedeems } = await sdk.redemptions.getRedemptionRedeems({
     *   dateFrom: new Date('2024-01-01'),
     *   dateTo: new Date('2024-01-31'),
     *   limit: 100
     * });
     *
     * console.log(`${recentRedeems.length} redeems in January 2024`);
     * ```
     *
     * @example With Enriched Data
     * ```typescript
     * const { data: redeems } = await sdk.redemptions.getRedemptionRedeems(
     *   { page: 1, limit: 20 },
     *   ['redemption', 'user', 'business']
     * );
     *
     * redeems.forEach(redeem => {
     *   const redemptionName = redeem.included?.redemption?.name || 'Unknown';
     *   const userEmail = redeem.included?.user?.email || redeem.userId;
     *   console.log(`${redemptionName} - ${userEmail}`);
     * });
     * ```
     */
    async getRedemptionRedeems(filters, include) {
        return this.redemptionService.getRedemptionRedeems(filters, include);
    }
    /**
     * Admin: Create new redemption offer
     *
     * Creates a new redemption offer in the loyalty system. This operation requires
     * administrator privileges and establishes a new reward option that users can
     * purchase with their loyalty tokens.
     *
     * @param redemptionData - Redemption configuration including title, description, and settings
     * @returns Promise resolving to created redemption offer
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * // Admin operation - create discount redemption
     * const newRedemption = await sdk.redemptions.createRedemption({
     *   name: '20% Off Weekend Stay',
     *   description: 'Get 20% off your next weekend booking at partner hotels',
     *   redemptionTypeId: 'discount-type-id',
     *   isActive: true,
     *   availableQuantity: 100,
     *   validFrom: '2024-06-01T00:00:00Z',
     *   validUntil: '2024-08-31T23:59:59Z'
     * });
     *
     * console.log('New redemption created:', newRedemption.name);
     * console.log('Redemption ID:', newRedemption.id);
     *
     * // Set redemption cost
     * await sdk.redemptions.createRedemptionTokenUnit(newRedemption.id, {
     *   tokenId: 'loyalty-points',
     *   amount: 750,
     *   type: 'COST'
     * });
     *
     * console.log('Cost set: 750 loyalty points');
     * ```
     */
    async createRedemption(redemptionData) {
        return this.redemptionService.createRedemption(redemptionData);
    }
    /**
     * Admin: Update redemption
     *
     * Updates an existing redemption offer's configuration, availability, or
     * description. This operation requires administrator privileges and can modify
     * most redemption properties while preserving existing redemption history.
     *
     * @param redemptionId - ID of the redemption to update
     * @param redemptionData - Updated redemption configuration
     * @returns Promise resolving to updated redemption offer
     * @throws {PersApiError} When not authenticated as admin or redemption not found
     *
     * @example
     * ```typescript
     * // Admin operation - update redemption details
     * const updated = await sdk.redemptions.updateRedemption('weekend-discount', {
     *   name: '25% Off Weekend Stay - Extended!',
     *   description: 'Enhanced discount - now 25% off weekend bookings',
     *   availableQuantity: 150,  // Increased availability
     *   validUntil: '2024-09-30T23:59:59Z'  // Extended validity
     * });
     *
     * console.log('Redemption updated:', updated.name);
     * console.log('New quantity available:', updated.availableQuantity);
     * ```
     */
    async updateRedemption(redemptionId, redemptionData) {
        return this.redemptionService.updateRedemption(redemptionId, redemptionData);
    }
    /**
     * Admin: Toggle redemption active status
     *
     * Toggles the active/inactive status of a redemption offer. Inactive redemptions
     * are not available for new purchases but existing redemptions remain valid.
     * Requires administrator privileges.
     *
     * @param redemptionId - ID of the redemption to toggle
     * @returns Promise resolving to updated redemption offer
     * @throws {PersApiError} When not authenticated as admin or redemption not found
     *
     * @example
     * ```typescript
     * // Admin operation - temporarily disable redemption
     * const updated = await sdk.redemptions.toggleRedemptionStatus('seasonal-offer');
     *
     * console.log(`Redemption ${updated.isActive ? 'activated' : 'deactivated'}`);
     * console.log('Status:', updated.isActive ? 'Available' : 'Unavailable');
     *
     * // Reactivate when ready
     * if (!updated.isActive) {
     *   console.log('Use the same method to reactivate when ready');
     * }
     * ```
     */
    async toggleRedemptionStatus(redemptionId) {
        return this.redemptionService.toggleRedemptionActive(redemptionId);
    }
    /**
     * Admin: Create redemption token unit
     *
     * Adds a token cost requirement to a redemption offer, specifying the type
     * and amount of loyalty tokens needed to redeem the offer. Multiple token
     * units can be added to create complex pricing structures. Requires
     * administrator privileges.
     *
     * @param redemptionId - ID of the redemption
     * @param tokenUnit - Token unit configuration including token type and amount
     * @returns Promise resolving to updated redemption offer
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * // Admin operation - set redemption cost
     * const updated = await sdk.redemptions.createRedemptionTokenUnit('premium-voucher', {
     *   tokenId: 'loyalty-points',
     *   amount: 1500,
     *   type: 'COST'
     * });
     *
     * console.log('Token cost added to redemption');
     * console.log('Cost: 1500 loyalty points');
     * console.log('Total cost units:', updated.tokenUnits?.length);
     *
     * // Add additional cost type (e.g., premium currency)
     * await sdk.redemptions.createRedemptionTokenUnit('premium-voucher', {
     *   tokenId: 'premium-credits',
     *   amount: 50,
     *   type: 'ADDITIONAL_COST'
     * });
     * ```
     */
    async createRedemptionTokenUnit(redemptionId, tokenUnit) {
        return this.redemptionService.createRedemptionTokenUnit(redemptionId, tokenUnit);
    }
    /**
     * Admin: Delete redemption token unit
     *
     * Removes a token cost requirement from a redemption offer. This affects
     * the pricing of future redemptions but does not impact already completed
     * redemptions. Requires administrator privileges.
     *
     * @param redemptionId - ID of the redemption
     * @param tokenUnitId - ID of the token unit to remove
     * @returns Promise resolving to updated redemption offer
     * @throws {PersApiError} When not authenticated as admin or entities not found
     *
     * @example
     * ```typescript
     * // Admin operation - remove token cost from redemption
     * const updated = await sdk.redemptions.deleteRedemptionTokenUnit(
     *   'special-offer',
     *   'token-unit-123'
     * );
     *
     * console.log('Token cost removed from redemption');
     * console.log('Remaining cost units:', updated.tokenUnits?.length);
     * ```
     */
    async deleteRedemptionTokenUnit(redemptionId, tokenUnitId) {
        return this.redemptionService.deleteRedemptionTokenUnit(redemptionId, tokenUnitId);
    }
    // ==========================================
    // REDEMPTION TYPE MANAGEMENT (Admin)
    // ==========================================
    /**
     * Admin: Create redemption type
     *
     * Creates a new redemption type category for organizing redemption offers.
     * Requires administrator privileges.
     *
     * @param data - Redemption type data (name, description, imageUrl)
     * @returns Promise resolving to created redemption type
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * const newType = await sdk.redemptions.createRedemptionType({
     *   name: 'Premium Rewards',
     *   description: 'High-value exclusive rewards',
     *   imageUrl: 'https://example.com/premium-icon.png'
     * });
     *
     * console.log('Created type:', newType.name);
     * ```
     */
    async createRedemptionType(data) {
        return this.redemptionService.createRedemptionType(data);
    }
    /**
     * Admin: Update redemption type
     *
     * Updates an existing redemption type's properties.
     * Requires administrator privileges.
     *
     * @param data - Updated redemption type data (must include id)
     * @returns Promise resolving to updated redemption type
     * @throws {PersApiError} When not authenticated as admin or type not found
     *
     * @example
     * ```typescript
     * const updated = await sdk.redemptions.updateRedemptionType({
     *   id: 123,
     *   name: 'VIP Rewards',
     *   description: 'Updated description',
     *   imageUrl: 'https://example.com/vip-icon.png'
     * });
     *
     * console.log('Updated type:', updated.name);
     * ```
     */
    async updateRedemptionType(data) {
        return this.redemptionService.updateRedemptionType(data);
    }
    /**
     * Admin: Delete redemption type
     *
     * Deletes a redemption type. Will fail if type is in use by redemptions.
     * Requires administrator privileges.
     *
     * @param id - Redemption type ID to delete
     * @returns Promise resolving to true if deleted successfully
     * @throws {PersApiError} When not authenticated as admin or type not found/in use
     *
     * @example
     * ```typescript
     * const success = await sdk.redemptions.deleteRedemptionType(123);
     * console.log('Deleted:', success);
     * ```
     */
    async deleteRedemptionType(id) {
        return this.redemptionService.deleteRedemptionType(id);
    }
    /**
     * Delete a redemption (soft delete)
     *
     * @param id - Redemption ID to delete
     * @returns Promise resolving to true if deleted successfully
     *
     * @example
     * ```typescript
     * const success = await sdk.redemptions.deleteRedemption('redemption-123');
     * console.log('Deleted:', success);
     * ```
     */
    async deleteRedemption(id) {
        return this.redemptionService.deleteRedemption(id);
    }
    /**
     * Admin: Export redemptions as CSV
     *
     * Generates a comprehensive CSV export of all tenant redemptions for
     * external analysis, reporting, or compliance purposes. This operation
     * requires administrator privileges and creates a downloadable file.
     *
     * @param options - Optional date range filters
     * @param options.dateFrom - Start date filter (ISO date string)
     * @param options.dateTo - End date filter (ISO date string)
     * @returns Promise resolving to CSV blob for download
     * @throws {PersApiError} When not authenticated as administrator or export fails
     *
     * @example Export All Redemptions
     * ```typescript
     * const csvBlob = await sdk.redemptions.exportCSV();
     * const downloadUrl = URL.createObjectURL(csvBlob);
     * ```
     *
     * @example Export Redemptions Within Date Range
     * ```typescript
     * const csvBlob = await sdk.redemptions.exportCSV({
     *   dateFrom: '2024-01-01',
     *   dateTo: '2024-12-31'
     * });
     * ```
     */
    async exportCSV(options) {
        const redemptionApi = new RedemptionApi(this.apiClient);
        return redemptionApi.exportCSV(options);
    }
    /**
     * Admin: Export redemption redeems as CSV
     *
     * Generates a comprehensive CSV export of all redemption redeem records for
     * external analysis, reporting, or compliance purposes. This operation
     * requires administrator privileges and creates a downloadable file.
     *
     * @param options - Optional date range filters
     * @param options.dateFrom - Start date filter (ISO date string)
     * @param options.dateTo - End date filter (ISO date string)
     * @returns Promise resolving to CSV blob for download
     * @throws {PersApiError} When not authenticated as administrator or export fails
     *
     * @example Export All Redeems
     * ```typescript
     * const csvBlob = await sdk.redemptions.exportRedeemsCSV();
     * const downloadUrl = URL.createObjectURL(csvBlob);
     * ```
     */
    async exportRedeemsCSV(options) {
        const redemptionApi = new RedemptionApi(this.apiClient);
        return redemptionApi.exportRedeemsCSV(options);
    }
    /**
     * Get the full redemption service for advanced operations
     *
     * Provides access to the complete RedemptionService instance for advanced
     * redemption operations, custom validation, analytics, and operations not
     * covered by the high-level manager methods.
     *
     * @returns RedemptionService instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const redemptionService = sdk.redemptions.getRedemptionService();
     *
     * // Access advanced redemption analytics
     * const analytics = await redemptionService.getRedemptionAnalytics();
     *
     * // Access custom validation rules
     * const eligibility = await redemptionService.validateRedemptionEligibility(
     *   'user-123',
     *   'redemption-456'
     * );
     *
     * // Access redemption API directly
     * const redemptionApi = redemptionService.api;
     *
     * // Use advanced inventory management
     * const inventory = await redemptionService.getInventoryStatus();
     * ```
     */
    getRedemptionService() {
        return this.redemptionService;
    }
}

/**
 * Transaction Manager - Clean, high-level interface for transaction operations
 *
 * Provides a comprehensive API for loyalty transaction management including transaction
 * creation, history tracking, reporting, and administrative oversight. Transactions
 * are the core financial operations that track token movements, purchases, rewards,
 * and all value exchanges within the loyalty ecosystem.
 *
 * @group Managers
 * @category Transaction Management
 *
 * @example Basic Transaction Operations
 * ```typescript
 * // Create a purchase transaction
 * const transaction = await sdk.transactions.createTransaction({
 *   type: 'PURCHASE',
 *   amount: 100.00,
 *   currency: 'USD',
 *   businessId: 'partner-store-123',
 *   description: 'Coffee shop purchase'
 * });
 *
 * // Get transaction details
 * const details = await sdk.transactions.getTransactionById(transaction.id);
 * console.log('Transaction:', details.description, details.status);
 *
 * // View transaction history
 * const history = await sdk.transactions.getUserTransactionHistory('ALL');
 * console.log(`Found ${history.length} transactions`);
 * ```
 *
 * @example Transaction History Analysis
 * ```typescript
 * // Get different types of transactions
 * const purchases = await sdk.transactions.getUserTransactionHistory('PURCHASE');
 * const rewards = await sdk.transactions.getUserTransactionHistory('REWARD');
 * const redemptions = await sdk.transactions.getUserTransactionHistory('REDEMPTION');
 *
 * console.log('Transaction Summary:');
 * console.log(`Purchases: ${purchases.length}`);
 * console.log(`Rewards: ${rewards.length}`);
 * console.log(`Redemptions: ${redemptions.length}`);
 * ```
 *
 * @example Administrative Reporting
 * ```typescript
 * // Admin: Get paginated transactions for analysis
 * const allTransactions = await sdk.transactions.getPaginatedTransactions({ page: 1, limit: 100 });
 *
 * // Export transaction data
 * const csvBlob = await sdk.transactions.exportTransactionsCSV();
 * const downloadUrl = URL.createObjectURL(csvBlob);
 * console.log('Download transaction report:', downloadUrl);
 * ```
 */
class TransactionManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
        const transactionApi = new TransactionApi(apiClient);
        this.transactionService = new TransactionService(transactionApi);
    }
    /**
     * Get transaction by ID
     *
     * Retrieves detailed information for a specific transaction including status,
     * amounts, participants, token transfers, and blockchain confirmations.
     * Provides complete transaction audit trail and verification data.
     *
     * @param transactionId - Unique transaction identifier
     * @param include - Optional relations to include (sender, recipient, business) for enriched entity data
     * @returns Promise resolving to complete transaction data
     * @throws {PersApiError} When transaction with specified ID is not found or access denied
     *
     * @example
     * ```typescript
     * try {
     *   // Get transaction with enriched sender/recipient data
     *   const transaction = await sdk.transactions.getTransactionById(
     *     'txn-abc123',
     *     ['sender', 'recipient', 'business']
     *   );
     *
     *   console.log('Transaction Details:');
     *   console.log('ID:', transaction.id);
     *   console.log('Type:', transaction.type);
     *   console.log('Status:', transaction.status);
     *   console.log('Amount:', transaction.amount, transaction.currency);
     *   console.log('Date:', new Date(transaction.createdAt).toLocaleString());
     *
     *   if (transaction.description) {
     *     console.log('Description:', transaction.description);
     *   }
     *
     *   // Access enriched sender data
     *   if (transaction.included?.sender) {
     *     console.log('Sender:', transaction.included.sender);
     *   }
     *
     *   // Access enriched recipient data
     *   if (transaction.included?.recipient) {
     *     console.log('Recipient:', transaction.included.recipient);
     *   }
     *
     *   // Access enriched business data
     *   if (transaction.included?.engagedBusiness) {
     *     console.log('Business:', transaction.included.engagedBusiness.displayName);
     *   }
     *
     *   // Show blockchain confirmation
     *   if (transaction.blockchainHash) {
     *     console.log('Blockchain:', transaction.blockchainHash);
     *   }
     *
     * } catch (error) {
     *   console.log('Transaction not found or access denied:', error.message);
     * }
     * ```
     */
    async getTransactionById(transactionId, include) {
        return this.transactionService.getTransactionById(transactionId, include);
    }
    /**
     * Create a new transaction
     *
     * Creates a new transaction in the loyalty system, processing token transfers,
     * applying rewards, and updating user balances. Supports various transaction
     * types including purchases, rewards, redemptions, and transfers.
     *
     * @param transactionData - Transaction configuration including type, amounts, and context
     * @returns Promise resolving to transaction creation response
     * @throws {PersApiError} When validation fails or insufficient balance
     *
     * @example Purchase Transaction
     * ```typescript
     * try {
     *   const purchase = await sdk.transactions.createTransaction({
     *     type: 'PURCHASE',
     *     amount: 25.50,
     *     currency: 'USD',
     *     businessId: 'coffee-shop-123',
     *     description: 'Morning coffee and pastry',
     *     metadata: {
     *       items: ['Latte', 'Croissant'],
     *       location: 'Downtown Store'
     *     }
     *   });
     *
     *   console.log('Purchase completed!');
     *   console.log('Transaction ID:', purchase.id);
     *   console.log('Status:', purchase.status);
     *
     *   if (purchase.tokensEarned?.length) {
     *     console.log('\nLoyalty rewards earned:');
     *     purchase.tokensEarned.forEach(reward => {
     *       console.log(`${reward.amount} ${reward.token.symbol}`);
     *     });
     *   }
     *
     * } catch (error) {
     *   console.log('Transaction failed:', error.message);
     * }
     * ```
     *
     * @example Reward Transaction
     * ```typescript
     * // Create reward transaction (typically triggered by campaigns)
     * const reward = await sdk.transactions.createTransaction({
     *   type: 'REWARD',
     *   businessId: 'partner-hotel-456',
     *   description: 'Welcome bonus for new customer',
     *   tokenRewards: [
     *     {
     *       tokenId: 'loyalty-points',
     *       amount: 500
     *     }
     *   ]
     * });
     *
     * console.log('Reward transaction created:', reward.id);
     * ```
     *
     * @example Transfer Transaction
     * ```typescript
     * // Create token transfer between users
     * const transfer = await sdk.transactions.createTransaction({
     *   type: 'TRANSFER',
     *   recipientUserId: 'user-789',
     *   description: 'Gift to friend',
     *   tokenTransfers: [
     *     {
     *       tokenId: 'loyalty-points',
     *       amount: 100,
     *       direction: 'OUTBOUND'
     *     }
     *   ]
     * });
     *
     * console.log('Transfer initiated:', transfer.id);
     * ```
     */
    async createTransaction(transactionData) {
        const result = await this.transactionService.createTransaction(transactionData);
        this.events?.emitSuccess({
            domain: 'transaction',
            type: 'transaction_created',
            userMessage: 'Transaction created successfully',
            details: { transactionId: result.transaction?.id }
        });
        return result;
    }
    /**
     * Get user's transaction history
     *
     * Retrieves transaction history for the authenticated user with comprehensive
     * filtering options. Provides chronological view of all user's loyalty
     * activities including purchases, rewards, redemptions, and transfers.
     * Optionally enrich with related entities (sender, recipient, business).
     *
     * @param options - Query options including filters, pagination, and include relations
     * @returns Promise resolving to paginated transaction data with optional included entities
     *
     * @example All Transactions
     * ```typescript
     * const result = await sdk.transactions.getUserTransactionHistory();
     *
     * console.log(`Transaction History (${result.data.length} of ${result.pagination.total} transactions)`);
     *
     * result.data.forEach((transaction, index) => {
     *   const date = new Date(transaction.createdAt).toLocaleDateString();
     *   console.log(`\n${index + 1}. ${transaction.type} - ${date}`);
     *   console.log(`   ${transaction.description || 'No description'}`);
     *   console.log(`   Status: ${transaction.status}`);
     * });
     * ```
     *
     * @example Filter by Role and Status
     * ```typescript
     * // Get only completed sent transactions
     * const sent = await sdk.transactions.getUserTransactionHistory({
     *   role: TransactionRole.SENDER,
     *   status: TransactionStatus.COMPLETED,
     *   include: ['recipient', 'business'],
     *   page: 1,
     *   limit: 50
     * });
     *
     * sent.data.forEach(tx => {
     *   console.log(`Sent to: ${tx.included?.recipient?.displayName || tx.recipientAddress}`);
     * });
     * ```
     *
     * @example Filter by Trigger Process (Campaign/Redemption)
     * ```typescript
     * // Get all transactions triggered by a specific campaign claim
     * const claimTxs = await sdk.transactions.getUserTransactionHistory({
     *   triggerProcessId: 'claim-abc123',
     *   include: ['sender', 'recipient', 'business']
     * });
     *
     * claimTxs.data.forEach(tx => {
     *   console.log('Transaction from claim:', tx.id);
     *   if (tx.included?.engagedBusiness) {
     *     console.log('Business:', tx.included.engagedBusiness.displayName);
     *   }
     * });
     * ```
     */
    async getUserTransactionHistory(options) {
        return this.transactionService.getUserTransactionHistory(options);
    }
    /**
     * Admin: Get paginated transactions
     *
     * Retrieves transactions with pagination support for efficient large dataset
     * handling. This operation requires administrator privileges and is used for
     * detailed transaction analysis and reporting interfaces.
     *
     * @param options - Pagination, filtering parameters, and optional include relations
     * @returns Promise resolving to paginated transaction results with metadata
     * @throws {PersApiError} When not authenticated as administrator
     *
     * @example Basic Pagination
     * ```typescript
     * // Admin operation - paginated transaction retrieval
     * const result = await sdk.transactions.getPaginatedTransactions({
     *   page: 1,
     *   limit: 50,
     *   sortBy: 'createdAt',
     *   sortOrder: 'DESC'
     * });
     *
     * console.log(`� Page ${result.page} of ${result.totalPages}`);
     * console.log(`Showing ${result.data.length} of ${result.total} transactions`);
     *
     * result.data.forEach((transaction, index) => {
     *   console.log(`${index + 1}. ${transaction.type} - ${transaction.status}`);
     *   console.log(`   ${new Date(transaction.createdAt).toLocaleDateString()}`);
     * });
     *
     * // Continue pagination if needed
     * if (result.hasNextPage) {
     *   const nextPage = await sdk.transactions.getPaginatedTransactions({
     *     page: result.page + 1,
     *     limit: 50
     *   });
     * }
     * ```
     *
     * @example Filtered Pagination
     * ```typescript
     * // Admin operation - filter transactions by criteria
     * const filteredResult = await sdk.transactions.getPaginatedTransactions({
     *   page: 1,
     *   limit: 25,
     *   filters: {
     *     type: 'PURCHASE',
     *     status: 'COMPLETED',
     *     businessId: 'partner-store-123',
     *     dateFrom: '2024-01-01',
     *     dateTo: '2024-12-31'
     *   },
     *   sortBy: 'amount',
     *   sortOrder: 'DESC'
     * });
     *
     * console.log('Filtered Results:');
     * console.log(`Found ${filteredResult.total} matching transactions`);
     *
     * filteredResult.data.forEach(transaction => {
     *   console.log(`$${transaction.amount} - ${transaction.business?.displayName}`);
     * });
     * ```
     *
     * @example With Included Relations
     * ```typescript
     * // Include sender and recipient entities
     * const result = await sdk.transactions.getPaginatedTransactions({
     *   page: 1,
     *   limit: 50,
     *   include: ['sender', 'recipient', 'business']
     * });
     *
     * result.data.forEach(tx => {
     *   if (tx.included?.sender) console.log('From:', tx.included.sender);
     *   if (tx.included?.recipient) console.log('To:', tx.included.recipient);
     * });
     * ```
     */
    async getPaginatedTransactions(options) {
        return this.transactionService.getPaginatedTransactions(options);
    }
    /**
     * Admin: Export transactions as CSV
     *
     * Generates a comprehensive CSV export of all tenant transactions for
     * external analysis, reporting, or compliance purposes. This operation
     * requires administrator privileges and creates a downloadable file.
     *
     * @param options - Optional date range filters
     * @param options.dateFrom - Start date filter (ISO date string)
     * @param options.dateTo - End date filter (ISO date string)
     * @returns Promise resolving to CSV blob for download
     * @throws {PersApiError} When not authenticated as administrator or export fails
     *
     * @example Export All Transactions
     * ```typescript
     * const csvBlob = await sdk.transactions.exportCSV();
     * const downloadUrl = URL.createObjectURL(csvBlob);
     * ```
     *
     * @example Export Transactions Within Date Range
     * ```typescript
     * const csvBlob = await sdk.transactions.exportCSV({
     *   dateFrom: '2024-01-01',
     *   dateTo: '2024-12-31'
     * });
     * ```
     */
    async exportCSV(options) {
        return this.transactionService.exportTransactionsCSV(options);
    }
    /**
     * Admin: Export transactions as CSV
     *
     * @deprecated Use `exportCSV()` instead for consistent naming across all managers
     */
    async exportTransactionsCSV(options) {
        return this.exportCSV(options);
    }
    /**
     * Prepare existing transaction for client-side signing
     *
     * Retrieves the necessary data to sign an existing transaction on the client side.
     * This is typically used when a transaction has been created but requires user signature.
     *
     * @param transactionId - The ID of the transaction to prepare
     * @returns Promise resolving to the transaction preparation data
     */
    async prepareExistingTransaction(transactionId) {
        return this.transactionService.prepareExistingTransaction(transactionId);
    }
    /**
     * Prepare a new transaction for client-side signing
     *
     * Creates a new transaction and immediately returns the data needed for client-side signing.
     * This combines creation and preparation into a single flow for client-signed operations.
     *
     * @param request - The transaction request details
     * @returns Promise resolving to the transaction preparation data
     */
    async prepareClientSignedTransaction(request) {
        return this.transactionService.prepareClientSignedTransaction(request);
    }
    /**
     * Submit a signed transaction
     *
     * Submits a transaction that has been signed on the client side to the backend for processing.
     *
     * @param signedTxData - The signed transaction data
     * @returns Promise resolving to the submission result
     */
    async submitSignedTransaction(signedTxData) {
        const result = await this.transactionService.submitSignedTransaction(signedTxData);
        this.events?.emitSuccess({
            domain: 'transaction',
            type: 'transaction_submitted',
            userMessage: 'Transaction submitted successfully',
            details: { transactionId: result.transaction?.id }
        });
        return result;
    }
    /**
     * Query transactions by sender
     *
     * Retrieves transactions where the specified account is the sender.
     *
     * @param accountSelector - Selector for the sender account
     * @returns Promise resolving to paginated transactions
     */
    async queryTransactionsBySender(accountSelector) {
        return this.transactionService.queryTransactionsBySender(accountSelector);
    }
    /**
     * Query transactions by recipient
     *
     * Retrieves transactions where the specified account is the recipient.
     *
     * @param accountSelector - Selector for the recipient account
     * @returns Promise resolving to paginated transactions
     */
    async queryTransactionsByRecipient(accountSelector) {
        return this.transactionService.queryTransactionsByRecipient(accountSelector);
    }
    /**
     * Get transaction analytics
     *
     * Retrieves analytics data for transactions based on the provided request criteria.
     * Useful for generating reports and dashboards.
     *
     * @param analyticsRequest - The analytics query parameters
     * @returns Promise resolving to the analytics data
     */
    async getTransactionAnalytics(analyticsRequest) {
        return this.transactionService.getTransactionAnalytics(analyticsRequest);
    }
    /**
     * Get the full transaction service for advanced operations
     *
     * Provides access to the complete TransactionService instance for advanced
     * transaction operations, custom analytics, blockchain interactions, and
     * operations not covered by the high-level manager methods.
     *
     * @returns TransactionService instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const transactionService = sdk.transactions.getTransactionService();
     *
     * // Access advanced transaction analytics
     * const analytics = await transactionService.getTransactionAnalytics({
     *   timeframe: 'last-30-days',
     *   groupBy: 'business'
     * });
     *
     * // Access blockchain transaction monitoring
     * const blockchainStatus = await transactionService.getBlockchainTransactionStatus('txn-123');
     *
     * // Access transaction API directly
     * const transactionApi = transactionService.api;
     *
     * // Use advanced validation and verification
     * const verification = await transactionService.verifyTransactionIntegrity('txn-456');
     *
     * // Access real-time transaction streaming
     * const stream = transactionService.subscribeToTransactionUpdates();
     * stream.on('transaction', (transaction) => {
     *   console.log('New transaction:', transaction.id);
     * });
     * ```
     */
    getTransactionService() {
        return this.transactionService;
    }
}

/**
 * Purchase Manager - Clean, high-level interface for purchase operations
 *
 * Provides a comprehensive API for purchase and payment management including payment
 * intent creation, purchase token handling, user purchase history, and integration
 * with payment processors. Handles both fiat currency payments and loyalty token
 * purchases within the ecosystem.
 *
 * @group Managers
 * @category Purchase Management
 *
 * @example Basic Purchase Operations
 * ```typescript
 * // Create payment intent for fiat purchase
 * const paymentIntent = await sdk.purchases.createPaymentIntent(
 *   50.00,
 *   'USD',
 *   'customer@example.com',
 *   'Loyalty token bundle purchase'
 * );
 * console.log('Payment intent created:', paymentIntent.id);
 *
 * // Get available purchase tokens
 * const tokens = await sdk.purchases.getActivePurchaseTokens();
 * console.log(`${tokens.length} token packages available`);
 *
 * // View purchase history
 * const purchases = await sdk.purchases.getAllUserPurchases();
 * console.log(`User has made ${purchases.length} purchases`);
 * ```
 *
 * @example Purchase Token Discovery
 * ```typescript
 * // Browse available token packages
 * const availableTokens = await sdk.purchases.getActivePurchaseTokens(true);
 *
 * console.log('Available Token Packages:');
 * availableTokens.forEach(token => {
 *   console.log(`\n${token.name}`);
 *   console.log(`Price: $${token.price} ${token.currency}`);
 *   console.log(`Tokens: ${token.tokenAmount} ${token.tokenSymbol}`);
 *   console.log(`Description: ${token.description}`);
 * });
 * ```
 *
 * @example Payment Processing
 * ```typescript
 * // Create payment for specific token package
 * const selectedPackage = availableTokens[0];
 * const paymentIntent = await sdk.purchases.createPaymentIntent(
 *   selectedPackage.price,
 *   selectedPackage.currency,
 *   'user@example.com',
 *   `Purchase: ${selectedPackage.name}`
 * );
 *
 * // Use payment intent client secret for frontend payment processing
 * console.log('Payment client secret:', paymentIntent.clientSecret);
 * ```
 */
class PurchaseManager {
    constructor(apiClient) {
        this.apiClient = apiClient;
        const paymentApi = new PurchaseApi(apiClient);
        this.paymentService = new PaymentService(paymentApi);
    }
    /**
     * Create a payment intent
     *
     * Creates a payment intent for processing fiat currency payments through
     * integrated payment processors (e.g., Stripe). Payment intents are used to
     * securely handle payment processing on the frontend while maintaining
     * server-side validation and security.
     *
     * @param amount - Payment amount in the specified currency's smallest unit (e.g., cents for USD)
     * @param currency - Payment currency code (e.g., 'USD', 'EUR', 'GBP')
     * @param receiptEmail - Email address for sending payment receipt
     * @param description - Human-readable description of the payment
     * @returns Promise resolving to payment intent with client secret
     * @throws {PersApiError} When payment processing is unavailable or validation fails
     *
     * @example Simple Payment Intent
     * ```typescript
     * try {
     *   const paymentIntent = await sdk.purchases.createPaymentIntent(
     *     2500,  // $25.00 in cents
     *     'USD',
     *     'customer@example.com',
     *     '1000 Loyalty Points Package'
     *   );
     *
     *   console.log('Payment Intent Created:');
     *   console.log('ID:', paymentIntent.id);
     *   console.log('Amount:', paymentIntent.amount / 100, paymentIntent.currency);
     *   console.log('Status:', paymentIntent.status);
     *   console.log('Client Secret:', paymentIntent.clientSecret);
     *
     *   // Use client secret in frontend payment processing
     *   // Example with Stripe Elements:
     *   // const { confirmPayment } = useStripe();
     *   // await confirmPayment({
     *   //   elements,
     *   //   confirmParams: {
     *   //     return_url: 'https://yourapp.com/payment-success'
     *   //   }
     *   // });
     *
     * } catch (error) {
     *   console.log('Payment intent creation failed:', error.message);
     * }
     * ```
     *
     * @example Token Package Purchase
     * ```typescript
     * // Get available token packages first
     * const packages = await sdk.purchases.getActivePurchaseTokens(true);
     * const selectedPackage = packages.find(p => p.name === 'Starter Package');
     *
     * if (selectedPackage) {
     *   const paymentIntent = await sdk.purchases.createPaymentIntent(
     *     selectedPackage.price * 100,  // Convert to cents
     *     selectedPackage.currency,
     *     'user@example.com',
     *     `Purchase: ${selectedPackage.name} - ${selectedPackage.tokenAmount} ${selectedPackage.tokenSymbol}`
     *   );
     *
     *   console.log(`Payment setup for ${selectedPackage.name}`);
     *   console.log(`Will receive: ${selectedPackage.tokenAmount} ${selectedPackage.tokenSymbol}`);
     *   console.log(`Payment amount: $${selectedPackage.price}`);
     *
     *   // Store payment intent for frontend processing
     *   localStorage.setItem('paymentIntentId', paymentIntent.id);
     *   localStorage.setItem('expectedTokens', selectedPackage.tokenAmount.toString());
     * }
     * ```
     *
     * @example Subscription Payment
     * ```typescript
     * // Create payment intent for recurring subscription
     * const subscriptionPayment = await sdk.purchases.createPaymentIntent(
     *   999,  // $9.99/month
     *   'USD',
     *   'subscriber@example.com',
     *   'Premium Loyalty Membership - Monthly'
     * );
     *
     * console.log('Subscription payment intent:', subscriptionPayment.id);
     * // Payment processor will handle recurring billing setup
     * ```
     */
    async createPaymentIntent(amount, currency, receiptEmail, description) {
        return this.paymentService.createPaymentIntent(amount, currency, receiptEmail, description);
    }
    /**
     * Get active purchase tokens
     *
     * Retrieves available token packages that users can purchase with fiat currency.
     * These packages represent bundles of loyalty tokens at various price points,
     * often with bonus tokens for larger purchases.
     *
     * @param active - Optional filter to show only active packages (default: all packages)
     * @returns Promise resolving to array of purchase token packages
     *
     * @example
     * ```typescript
     * const allPackages = await sdk.purchases.getActivePurchaseTokens();
     * const activeOnly = await sdk.purchases.getActivePurchaseTokens(true);
     *
     * console.log('Token Package Catalog:');
     *
     * activeOnly.forEach(package => {
     *   console.log(`\n${package.name}`);
     *   console.log(`Price: $${package.price} ${package.currency}`);
     *   console.log(`🪙  Tokens: ${package.tokenAmount} ${package.tokenSymbol}`);
     *   console.log(`${package.description}`);
     *
     *   // Calculate value per token
     *   const costPerToken = package.price / package.tokenAmount;
     *   console.log(`Value: $${costPerToken.toFixed(4)} per token`);
     *
     *   if (package.bonusTokens && package.bonusTokens > 0) {
     *     console.log(`Bonus: +${package.bonusTokens} extra tokens`);
     *     const totalTokens = package.tokenAmount + package.bonusTokens;
     *     const actualCostPerToken = package.price / totalTokens;
     *     console.log(`With bonus: $${actualCostPerToken.toFixed(4)} per token`);
     *   }
     *
     *   if (package.validUntil) {
     *     console.log(`⏰ Valid until: ${new Date(package.validUntil).toLocaleDateString()}`);
     *   }
     * });
     *
     * // Find best value package
     * const bestValue = activeOnly.reduce((best, current) => {
     *   const currentValue = current.price / (current.tokenAmount + (current.bonusTokens || 0));
     *   const bestValue = best.price / (best.tokenAmount + (best.bonusTokens || 0));
     *   return currentValue < bestValue ? current : best;
     * });
     *
     * console.log(`\n� Best Value: ${bestValue.name}`);
     * ```
     *
     * @example Filter by Currency and Price
     * ```typescript
     * const packages = await sdk.purchases.getActivePurchaseTokens(true);
     *
     * // Filter by currency
     * const usdPackages = packages.filter(p => p.currency === 'USD');
     * const eurPackages = packages.filter(p => p.currency === 'EUR');
     *
     * console.log(`USD packages: ${usdPackages.length}`);
     * console.log(`EUR packages: ${eurPackages.length}`);
     *
     * // Filter by price range
     * const budgetPackages = packages.filter(p => p.price <= 25);
     * const premiumPackages = packages.filter(p => p.price > 100);
     *
     * console.log(`Budget options (≤$25): ${budgetPackages.length}`);
     * console.log(`Premium options (>$100): ${premiumPackages.length}`);
     *
     * // Sort by token amount
     * const sortedByTokens = [...packages].sort((a, b) =>
     *   (b.tokenAmount + (b.bonusTokens || 0)) - (a.tokenAmount + (a.bonusTokens || 0))
     * );
     *
     * console.log('\nTop 3 by token amount:');
     * sortedByTokens.slice(0, 3).forEach((pkg, index) => {
     *   const totalTokens = pkg.tokenAmount + (pkg.bonusTokens || 0);
     *   console.log(`${index + 1}. ${pkg.name}: ${totalTokens} tokens`);
     * });
     * ```
     */
    async getActivePurchaseTokens(active, options) {
        return this.paymentService.getActivePurchaseTokens(active, options);
    }
    /**
     * Get all user purchases
     *
     * Retrieves the complete purchase history for the authenticated user,
     * including both successful and failed purchases. Provides insights into
     * user spending patterns, token acquisition, and payment history.
     *
     * @returns Promise resolving to array of user's purchase records
     *
     * @example
     * ```typescript
     * const userPurchases = await sdk.purchases.getAllUserPurchases();
     *
     * console.log(`Purchase History (${userPurchases.length} purchases):`);
     *
     * userPurchases.forEach((purchase, index) => {
     *   console.log(`\n${index + 1}. Purchase #${purchase.id}`);
     *   console.log(`Date: ${new Date(purchase.createdAt).toLocaleDateString()}`);
     *   console.log(`Amount: $${purchase.amount} ${purchase.currency}`);
     *   console.log(`Status: ${purchase.status}`);
     *
     *   if (purchase.description) {
     *     console.log(`Description: ${purchase.description}`);
     *   }
     *
     *   if (purchase.tokensReceived) {
     *     console.log(`🪙  Tokens received: ${purchase.tokensReceived}`);
     *   }
     *
     *   if (purchase.paymentMethod) {
     *     console.log(`Payment method: ${purchase.paymentMethod}`);
     *   }
     *
     *   if (purchase.receiptEmail) {
     *     console.log(`� Receipt sent to: ${purchase.receiptEmail}`);
     *   }
     * });
     *
     * // Calculate purchase statistics
     * const successfulPurchases = userPurchases.filter(p => p.status === 'COMPLETED');
     * const totalSpent = successfulPurchases.reduce((sum, p) => sum + p.amount, 0);
     * const totalTokens = successfulPurchases.reduce((sum, p) => sum + (p.tokensReceived || 0), 0);
     *
     * console.log('\nPurchase Statistics:');
     * console.log(`Total purchases: ${userPurchases.length}`);
     * console.log(`Successful purchases: ${successfulPurchases.length}`);
     * console.log(`Total spent: $${totalSpent.toFixed(2)}`);
     * console.log(`Total tokens acquired: ${totalTokens}`);
     *
     * if (totalTokens > 0) {
     *   const avgCostPerToken = totalSpent / totalTokens;
     *   console.log(`Average cost per token: $${avgCostPerToken.toFixed(4)}`);
     * }
     * ```
     *
     * @example Purchase Analysis
     * ```typescript
     * const purchases = await sdk.purchases.getAllUserPurchases();
     *
     * // Analyze by time period
     * const last30Days = purchases.filter(p =>
     *   new Date(p.createdAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
     * );
     *
     * const last6Months = purchases.filter(p =>
     *   new Date(p.createdAt) > new Date(Date.now() - 180 * 24 * 60 * 60 * 1000)
     * );
     *
     * console.log('Purchase Timeline:');
     * console.log(`Last 30 days: ${last30Days.length} purchases`);
     * console.log(`Last 6 months: ${last6Months.length} purchases`);
     *
     * // Analyze by status
     * const statusCounts = purchases.reduce((acc, p) => {
     *   acc[p.status] = (acc[p.status] || 0) + 1;
     *   return acc;
     * }, {});
     *
     * console.log('\nBy status:');
     * Object.entries(statusCounts).forEach(([status, count]) => {
     *   console.log(`${status}: ${count} purchases`);
     * });
     *
     * // Find largest and most recent purchases
     * const largestPurchase = purchases.reduce((max, current) =>
     *   current.amount > max.amount ? current : max, purchases[0]
     * );
     *
     * const mostRecent = purchases.reduce((newest, current) =>
     *   new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest, purchases[0]
     * );
     *
     * if (largestPurchase) {
     *   console.log(`\nLargest purchase: $${largestPurchase.amount} on ${new Date(largestPurchase.createdAt).toLocaleDateString()}`);
     * }
     *
     * if (mostRecent) {
     *   console.log(`⏰ Most recent: $${mostRecent.amount} on ${new Date(mostRecent.createdAt).toLocaleDateString()}`);
     * }
     * ```
     */
    async getAllUserPurchases(options) {
        return this.paymentService.getAllUserPurchases(options);
    }
    /**
     * Get the full purchase service for advanced operations
     *
     * Provides access to the complete PaymentService instance for advanced purchase
     * operations, payment processor integrations, subscription management, and
     * operations not covered by the high-level manager methods.
     *
     * @returns PaymentService instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const paymentService = sdk.purchases.getPurchaseService();
     *
     * // Access advanced payment analytics
     * const analytics = await paymentService.getPaymentAnalytics();
     *
     * // Access subscription management
     * const subscriptions = await paymentService.getUserSubscriptions();
     *
     * // Access payment method management
     * const paymentMethods = await paymentService.getUserPaymentMethods();
     *
     * // Access payment API directly
     * const paymentApi = paymentService.api;
     *
     * // Use advanced purchase validation
     * const validation = await paymentService.validatePurchaseEligibility('token-package-123');
     *
     * // Access payment processor webhooks
     * const webhookHandler = paymentService.createWebhookHandler();
     * ```
     */
    getPurchaseService() {
        return this.paymentService;
    }
}

/**
 * Platform-Agnostic File API Client
 *
 * Handles file operations using the PERS backend's RESTful endpoints.
 */
class FileApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/files';
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get signed URL for file operations
     *
     * @param request - Signed URL request parameters
     * @returns Promise resolving to signed URL response
     */
    async getSignedUrl(request) {
        return this.apiClient.post(`${this.basePath}/entity-storage-url`, request);
    }
    /**
     * PUBLIC: Get signed GET URL for file access
     *
     * @param url - File URL
     * @param expireSeconds - Optional expiration time in seconds
     * @returns Promise resolving to signed URL response
     */
    async getSignedGetUrl(url, expireSeconds) {
        const searchParams = new URLSearchParams({ url });
        if (expireSeconds) {
            searchParams.set('expireSeconds', expireSeconds.toString());
        }
        return this.apiClient.get(`${this.basePath}/signed-url?${searchParams.toString()}`);
    }
    /**
     * PUBLIC: Optimize media from URL
     *
     * @param request - File optimization request
     * @returns Promise resolving to optimized file URL
     */
    async optimizeMediaFromUrl(request) {
        const searchParams = new URLSearchParams({ url: request.url });
        if (request.width)
            searchParams.set('width', request.width.toString());
        if (request.height)
            searchParams.set('height', request.height.toString());
        return this.apiClient.get(`/file/optimize-url?${searchParams.toString()}`);
    }
}

/**
 * File Service - High-level file operations
 *
 * Provides convenient methods for file upload, signed URLs, and media optimization.
 */
class FileService {
    constructor(apiClient) {
        this.fileApi = new FileApi(apiClient);
    }
    /**
     * Get signed PUT URL for file upload
     *
     * @param entityId - Entity ID to associate the file with
     * @param entityType - Type of entity (token, campaign, etc.)
     * @param fileExtension - File extension (e.g., 'jpg', 'png')
     * @returns Promise resolving to signed upload URL
     */
    async getSignedPutUrl(entityId, entityType, fileExtension) {
        const request = {
            entityId,
            entityType,
            signedUrlType: 'PUT',
            fileExtension
        };
        const response = await this.fileApi.getSignedUrl(request);
        return response.signedUrl;
    }
    /**
     * Get signed GET URL for file access
     *
     * @param entityId - Entity ID
     * @param entityType - Type of entity
     * @param expireSeconds - Optional expiration time in seconds
     * @returns Promise resolving to signed access URL
     */
    async getSignedGetUrl(entityId, entityType, expireSeconds) {
        const request = {
            entityId,
            entityType,
            signedUrlType: 'GET'
        };
        const response = await this.fileApi.getSignedUrl(request);
        return response.signedUrl;
    }
    /**
     * Get signed URL for any file operation
     *
     * @param request - Signed URL request
     * @returns Promise resolving to signed URL
     */
    async getSignedUrl(request) {
        const response = await this.fileApi.getSignedUrl(request);
        return response.signedUrl;
    }
    /**
     * Optimize media from URL
     *
     * @param url - Original file URL
     * @param width - Optional target width
     * @param height - Optional target height
     * @returns Promise resolving to optimized file URL
     */
    async optimizeMedia(url, width, height) {
        const request = { url, width, height };
        const response = await this.fileApi.optimizeMediaFromUrl(request);
        return response.optimizedUrl;
    }
    /**
     * Get the file API instance for advanced operations
     *
     * @returns FileApi instance
     */
    getFileApi() {
        return this.fileApi;
    }
}

/**
 * File Manager - Clean, high-level interface for file operations
 *
 * Provides a comprehensive API for file management including secure file uploads,
 * download URLs, media optimization, and cloud storage integration. Handles file
 * operations for various entity types including tokens, campaigns, businesses,
 * and user profiles with automatic security and optimization features.
 *
 * @group Managers
 * @category File Management
 *
 * @example Basic File Upload
 * ```typescript
 * // Get signed URL for uploading a campaign image
 * const uploadUrl = await sdk.files.getSignedPutUrl(
 *   'campaign-123',
 *   'campaign',
 *   'jpg'
 * );
 *
 * // Upload file using the signed URL
 * const file = document.getElementById('fileInput').files[0];
 * const response = await fetch(uploadUrl, {
 *   method: 'PUT',
 *   body: file,
 *   headers: { 'Content-Type': file.type }
 * });
 *
 * console.log('File uploaded successfully:', response.ok);
 * ```
 *
 * @example File Access and Optimization
 * ```typescript
 * // Get secure download URL
 * const downloadUrl = await sdk.files.getSignedGetUrl(
 *   'token-456',
 *   'token',
 *   3600  // 1 hour expiry
 * );
 *
 * // Get optimized thumbnail
 * const thumbnail = await sdk.files.optimizeMedia(
 *   downloadUrl,
 *   300,  // width
 *   300   // height
 * );
 *
 * console.log('Thumbnail URL:', thumbnail);
 * ```
 *
 * @example Advanced File Operations
 * ```typescript
 * // Use flexible signed URL API
 * const customUrl = await sdk.files.getSignedUrl({
 *   operation: 'GET',
 *   entityId: 'business-789',
 *   entityType: 'business',
 *   expireSeconds: 7200,
 *   contentType: 'image/png'
 * });
 * ```
 */
class FileManager {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.fileService = new FileService(apiClient);
    }
    /**
     * Get signed URL for file upload
     *
     * Generates a secure, time-limited URL for uploading files to cloud storage.
     * The URL provides direct upload access while maintaining security and
     * associating files with specific entities in the loyalty system.
     *
     * @param entityId - Unique identifier of the entity to associate the file with
     * @param entityType - Type of entity ('token', 'campaign', 'business', 'user', etc.)
     * @param fileExtension - File extension without dot (e.g., 'jpg', 'png', 'pdf')
     * @returns Promise resolving to signed upload URL
     * @throws {PersApiError} When entity not found or upload not authorized
     *
     * @example Campaign Image Upload
     * ```typescript
     * try {
     *   // Get signed upload URL for campaign banner
     *   const uploadUrl = await sdk.files.getSignedPutUrl(
     *     'summer-campaign-2024',
     *     'campaign',
     *     'png'
     *   );
     *
     *   console.log('Upload URL generated:', uploadUrl);
     *
     *   // Upload file from input element
     *   const fileInput = document.getElementById('campaignBanner');
     *   const file = fileInput.files[0];
     *
     *   if (file) {
     *     const uploadResponse = await fetch(uploadUrl, {
     *       method: 'PUT',
     *       body: file,
     *       headers: {
     *         'Content-Type': file.type
     *       }
     *     });
     *
     *     if (uploadResponse.ok) {
     *       console.log('Campaign banner uploaded successfully');
     *     } else {
     *       console.log('Upload failed:', uploadResponse.status);
     *     }
     *   }
     *
     * } catch (error) {
     *   console.log('Upload URL generation failed:', error.message);
     * }
     * ```
     *
     * @example Token Logo Upload
     * ```typescript
     * // Upload logo for loyalty token
     * const tokenLogoUrl = await sdk.files.getSignedPutUrl(
     *   'loyalty-points-token',
     *   'token',
     *   'svg'
     * );
     *
     * // Upload SVG file
     * const svgFile = new File([svgContent], 'token-logo.svg', { type: 'image/svg+xml' });
     * await fetch(tokenLogoUrl, {
     *   method: 'PUT',
     *   body: svgFile,
     *   headers: { 'Content-Type': 'image/svg+xml' }
     * });
     *
     * console.log('Token logo uploaded');
     * ```
     *
     * @example Business Profile Image
     * ```typescript
     * // Upload business profile image
     * const businessImageUrl = await sdk.files.getSignedPutUrl(
     *   'partner-hotel-123',
     *   'business',
     *   'jpg'
     * );
     *
     * // Handle file upload with progress tracking
     * const file = selectedFile;
     * const xhr = new XMLHttpRequest();
     *
     * xhr.upload.addEventListener('progress', (e) => {
     *   if (e.lengthComputable) {
     *     const percentComplete = (e.loaded / e.total) * 100;
     *     console.log(`Upload progress: ${percentComplete}%`);
     *   }
     * });
     *
     * xhr.open('PUT', businessImageUrl);
     * xhr.send(file);
     * ```
     */
    async getSignedPutUrl(entityId, entityType, fileExtension) {
        return this.fileService.getSignedPutUrl(entityId, entityType, fileExtension);
    }
    /**
     * Get signed URL for file access
     *
     * Generates a secure, time-limited URL for accessing/downloading files from
     * cloud storage. URLs automatically expire for security and can be used
     * directly in image tags, download links, or API responses.
     *
     * @param entityId - Unique identifier of the entity the file is associated with
     * @param entityType - Type of entity ('token', 'campaign', 'business', 'user', etc.)
     * @param expireSeconds - Optional expiration time in seconds (default: 1 hour)
     * @returns Promise resolving to signed access URL
     * @throws {PersApiError} When entity or file not found or access denied
     *
     * @example Image Display
     * ```typescript
     * try {
     *   // Get URL for displaying token logo
     *   const logoUrl = await sdk.files.getSignedGetUrl(
     *     'vip-status-token',
     *     'token',
     *     3600  // 1 hour expiry
     *   );
     *
     *   // Use in image element
     *   const img = document.createElement('img');
     *   img.src = logoUrl;
     *   img.alt = 'VIP Status Token';
     *   img.style.width = '64px';
     *   img.style.height = '64px';
     *
     *   document.getElementById('tokenContainer').appendChild(img);
     *
     *   console.log('Token logo displayed');
     *
     * } catch (error) {
     *   console.log('Failed to load token logo:', error.message);
     *   // Show fallback image
     *   img.src = '/images/default-token.png';
     * }
     * ```
     *
     * @example File Download
     * ```typescript
     * // Get download URL for campaign assets
     * const downloadUrl = await sdk.files.getSignedGetUrl(
     *   'holiday-campaign',
     *   'campaign',
     *   7200  // 2 hours for download
     * );
     *
     * // Create download link
     * const link = document.createElement('a');
     * link.href = downloadUrl;
     * link.download = 'holiday-campaign-assets.zip';
     * link.textContent = 'Download Campaign Assets';
     *
     * document.body.appendChild(link);
     *
     * // Or trigger immediate download
     * link.click();
     * ```
     *
     * @example Gallery Display
     * ```typescript
     * // Display business gallery images
     * const businessImages = ['image1', 'image2', 'image3'];
     * const gallery = document.getElementById('businessGallery');
     *
     * for (const imageId of businessImages) {
     *   try {
     *     const imageUrl = await sdk.files.getSignedGetUrl(
     *       `business-gallery-${imageId}`,
     *       'business',
     *       1800  // 30 minutes
     *     );
     *
     *     const img = document.createElement('img');
     *     img.src = imageUrl;
     *     img.className = 'gallery-image';
     *     img.loading = 'lazy';
     *
     *     gallery.appendChild(img);
     *
     *   } catch (error) {
     *     console.log(`Failed to load image ${imageId}:`, error.message);
     *   }
     * }
     * ```
     */
    async getSignedGetUrl(entityId, entityType, expireSeconds) {
        return this.fileService.getSignedGetUrl(entityId, entityType, expireSeconds);
    }
    /**
     * Get signed URL for any file operation
     *
     * Flexible method for generating signed URLs with custom parameters for
     * various file operations including uploads, downloads, and deletions.
     * Provides fine-grained control over URL generation and file access permissions.
     *
     * @param request - Signed URL request configuration
     * @returns Promise resolving to signed URL
     * @throws {PersApiError} When request parameters are invalid or access denied
     *
     * @example Advanced Upload Configuration
     * ```typescript
     * // Create upload URL with specific content type and metadata
     * const advancedUploadUrl = await sdk.files.getSignedUrl({
     *   operation: 'PUT',
     *   entityId: 'premium-nft-123',
     *   entityType: 'token',
     *   fileExtension: 'png',
     *   contentType: 'image/png',
     *   expireSeconds: 300,  // 5 minutes for security
     *   metadata: {
     *     creator: 'artist-456',
     *     collection: 'premium-series',
     *     rarity: 'legendary'
     *   }
     * });
     *
     * console.log('Advanced upload URL created:', advancedUploadUrl);
     * ```
     *
     * @example Temporary Access URL
     * ```typescript
     * // Create very short-lived URL for sensitive document
     * const secureUrl = await sdk.files.getSignedUrl({
     *   operation: 'GET',
     *   entityId: 'compliance-document-789',
     *   entityType: 'business',
     *   expireSeconds: 60,  // 1 minute only
     *   accessLevel: 'admin-only'
     * });
     *
     * // Use immediately
     * window.open(secureUrl, '_blank');
     * ```
     *
     * @example Batch Operations
     * ```typescript
     * // Generate multiple URLs for batch file operations
     * const fileOperations = [
     *   { operation: 'GET', entityId: 'doc1', entityType: 'campaign' },
     *   { operation: 'GET', entityId: 'doc2', entityType: 'campaign' },
     *   { operation: 'PUT', entityId: 'doc3', entityType: 'campaign', fileExtension: 'pdf' }
     * ];
     *
     * const urls = await Promise.all(
     *   fileOperations.map(op => sdk.files.getSignedUrl(op))
     * );
     *
     * console.log('Generated batch URLs:', urls);
     * ```
     */
    async getSignedUrl(request) {
        return this.fileService.getSignedUrl(request);
    }
    /**
     * Optimize media file
     *
     * Creates optimized versions of media files with automatic resizing, format
     * conversion, and compression. Perfect for generating thumbnails, responsive
     * images, and bandwidth-optimized content while maintaining visual quality.
     *
     * @param url - Original file URL (can be signed or public URL)
     * @param width - Optional target width in pixels
     * @param height - Optional target height in pixels
     * @returns Promise resolving to optimized file URL
     * @throws {PersApiError} When optimization fails or source file is invalid
     *
     * @example Responsive Image Sizes
     * ```typescript
     * // Get original campaign banner URL
     * const originalUrl = await sdk.files.getSignedGetUrl('campaign-123', 'campaign');
     *
     * // Generate multiple optimized sizes
     * const thumbnail = await sdk.files.optimizeMedia(originalUrl, 150, 150);
     * const medium = await sdk.files.optimizeMedia(originalUrl, 400, 300);
     * const large = await sdk.files.optimizeMedia(originalUrl, 800, 600);
     *
     * // Use in responsive image setup
     * const img = document.createElement('img');
     * img.src = medium;  // Default size
     * img.srcset = `
     *   ${thumbnail} 150w,
     *   ${medium} 400w,
     *   ${large} 800w
     * `;
     * img.sizes = '(max-width: 400px) 150px, (max-width: 800px) 400px, 800px';
     *
     * console.log('Responsive image configured');
     * ```
     *
     * @example Avatar Generation
     * ```typescript
     * // Get user profile image and create avatars
     * const profileUrl = await sdk.files.getSignedGetUrl('user-456', 'user');
     *
     * // Generate different avatar sizes
     * const smallAvatar = await sdk.files.optimizeMedia(profileUrl, 32, 32);   // Nav bar
     * const mediumAvatar = await sdk.files.optimizeMedia(profileUrl, 64, 64);  // Comments
     * const largeAvatar = await sdk.files.optimizeMedia(profileUrl, 128, 128); // Profile page
     *
     * // Cache avatar URLs
     * const avatars = {
     *   small: smallAvatar,
     *   medium: mediumAvatar,
     *   large: largeAvatar
     * };
     *
     * localStorage.setItem('userAvatars', JSON.stringify(avatars));
     * console.log('User avatars generated and cached');
     * ```
     *
     * @example Product Gallery Thumbnails
     * ```typescript
     * // Generate thumbnails for business product gallery
     * const productImages = await getBusinessProductImages('business-789');
     * const thumbnails = [];
     *
     * for (const productImage of productImages) {
     *   try {
     *     const originalUrl = await sdk.files.getSignedGetUrl(
     *       productImage.id,
     *       'business',
     *       3600
     *     );
     *
     *     const thumbnail = await sdk.files.optimizeMedia(originalUrl, 200, 200);
     *
     *     thumbnails.push({
     *       id: productImage.id,
     *       original: originalUrl,
     *       thumbnail: thumbnail,
     *       title: productImage.title
     *     });
     *
     *   } catch (error) {
     *     console.log(`Failed to optimize ${productImage.id}:`, error.message);
     *   }
     * }
     *
     * console.log(`Generated ${thumbnails.length} product thumbnails`);
     *
     * // Display thumbnail gallery
     * thumbnails.forEach(item => {
     *   const img = document.createElement('img');
     *   img.src = item.thumbnail;
     *   img.alt = item.title;
     *   img.onclick = () => showLightbox(item.original);
     *   gallery.appendChild(img);
     * });
     * ```
     *
     * @example Automatic Format Optimization
     * ```typescript
     * // Optimize with automatic format selection
     * const tokenIcon = await sdk.files.getSignedGetUrl('token-123', 'token');
     *
     * // System automatically chooses best format (WebP, AVIF, etc.)
     * const optimizedIcon = await sdk.files.optimizeMedia(tokenIcon, 48, 48);
     *
     * console.log('Optimized token icon (auto format):', optimizedIcon);
     *
     * // Use in token display
     * document.querySelector('.token-icon').src = optimizedIcon;
     * ```
     */
    async optimizeMedia(url, width, height) {
        return this.fileService.optimizeMedia(url, width, height);
    }
    /**
     * Get the full file service for advanced operations
     *
     * Provides access to the complete FileService instance for advanced file
     * operations, batch processing, storage analytics, and operations not
     * covered by the high-level manager methods.
     *
     * @returns FileService instance with full API access
     *
     * @example Advanced Operations
     * ```typescript
     * const fileService = sdk.files.getFileService();
     *
     * // Access storage analytics
     * const storageStats = await fileService.getStorageAnalytics();
     * console.log('Storage usage:', storageStats.totalSize);
     *
     * // Access batch file operations
     * const batchResult = await fileService.batchDeleteFiles(['file1', 'file2', 'file3']);
     *
     * // Access file metadata management
     * const metadata = await fileService.getFileMetadata('entity-123', 'campaign');
     *
     * // Access advanced optimization options
     * const advancedOptimization = await fileService.optimizeMediaAdvanced(url, {
     *   width: 400,
     *   height: 300,
     *   quality: 85,
     *   format: 'webp',
     *   progressive: true,
     *   watermark: 'brand-logo'
     * });
     *
     * // Access file upload monitoring
     * const uploadProgress = fileService.monitorUploadProgress('upload-session-123');
     * uploadProgress.on('progress', (percent) => {
     *   console.log(`Upload: ${percent}%`);
     * });
     * ```
     */
    getFileService() {
        return this.fileService;
    }
}

/**
 * Platform-Agnostic API Key API Client
 *
 * Handles API key management operations for tenant admins.
 * All operations require admin authentication and operate within tenant context.
 */
class ApiKeyApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/api-keys';
    }
    /**
     * ADMIN: Get all tenant API keys
     *
     * @param type Optional filter by integration API key type (database-stored tokens that can be revoked)
     * @param includeRevoked Include revoked API keys in results (default: false)
     * @param options Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with API keys for the current tenant
     */
    async getTenantApiKeys(type, includeRevoked = false, options) {
        const params = buildPaginationParams(options);
        if (type)
            params.append('type', type);
        if (includeRevoked)
            params.append('includeRevoked', 'true');
        const response = await this.apiClient.get(`${this.basePath}?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * ADMIN: Create new API key
     *
     * @param req API key creation request containing type, name, and optional configuration
     * @returns Created API key with the actual key (store securely - only returned once)
     */
    async createApiKey(req) {
        return this.apiClient.post(this.basePath, req);
    }
    /**
     * ADMIN: Revoke API key
     *
     * @param apiKeyId Unique identifier of the API key to revoke
     * @returns API key verification details with revocation status
     */
    async revokeApiKey(apiKeyId) {
        return this.apiClient.delete(`${this.basePath}/${apiKeyId}`);
    }
}

/**
 * API Key Manager
 *
 * Provides high-level API key management operations for tenant admins.
 * Handles JWT token creation, API key listing, and revocation with proper error handling.
 *
 * **Admin Access Required**: All operations require tenant admin authentication
 *
 * @example
 * ```typescript
 * // Create JWT token for tenant
 * const jwtToken = await sdk.apiKeys.createJwtToken('Frontend App');
 * console.log('Store this JWT securely:', jwtToken.key);
 *
 * // List all tenant API keys
 * const apiKeys = await sdk.apiKeys.listApiKeys();
 * apiKeys.forEach(key => {
 *   console.log(`${key.name} (${key.type}) - Active: ${key.isActive}`);
 * });
 *
 * // Revoke an API key
 * await sdk.apiKeys.revokeApiKey('key-id-to-revoke');
 * ```
 */
class ApiKeyManager {
    constructor(apiClient) {
        this.apiKeyApi = new ApiKeyApi(apiClient);
    }
    // ==========================================
    // TENANT API KEY OPERATIONS (Admin Only)
    // ==========================================
    /**
     * Get all API keys for the current tenant with pagination
     *
     * **Admin Only**: Requires tenant admin authentication
     *
     * @param type Optional filter by integration API key type (database-stored tokens)
     * @param includeRevoked Include revoked API keys in results (default: false)
     * @returns Paginated API keys with metadata (excludes actual key values)
     *
     * @example
     * ```typescript
     * // Get all active API keys
     * const { data: allKeys } = await sdk.apiKeys.listApiKeys();
     *
     * // Get only active JWT tokens
     * const { data: jwtKeys } = await sdk.apiKeys.listApiKeys('TENANT_SYSTEM_JWT');
     *
     * // Get all JWT tokens including revoked ones
     * const { data: allJwtKeys } = await sdk.apiKeys.listApiKeys('TENANT_SYSTEM_JWT', true);
     * ```
     */
    async listApiKeys(type, includeRevoked = false, options) {
        try {
            return await this.apiKeyApi.getTenantApiKeys(type, includeRevoked);
        }
        catch (error) {
            console.error('[ApiKeyManager] Failed to list API keys:', error);
            throw error;
        }
    }
    /**
     * Create a new API key with specified type
     *
     * **Admin Only**: Requires tenant admin authentication
     * **Security Warning**: The returned API key is only shown once - store it securely
     *
     * @param req API key creation request containing type, name, and optional configuration
     * @returns Created API key with the actual key value
     *
     * @example
     * ```typescript
     * // Create a JWT token
     * const jwtKey = await sdk.apiKeys.createApiKey('TENANT_SYSTEM_JWT', 'Frontend App');
     * console.log('Store this JWT securely:', jwtKey.privateKey);
     * ```
     */
    async createApiKey(req) {
        try {
            return await this.apiKeyApi.createApiKey(req);
        }
        catch (error) {
            console.error('[ApiKeyManager] Failed to create API key:', error);
            throw error;
        }
    }
    /**
     * Permanently revoke an API key
     *
     * **Admin Only**: Requires tenant admin authentication
     * **Warning**: This operation cannot be undone. The API key will be permanently disabled.
     *
     * @param apiKeyId Unique identifier of the API key to revoke
     * @returns API key verification details with revocation status
     *
     * @example
     * ```typescript
     * try {
     *   // List keys to find the one to revoke
     *   const apiKeys = await sdk.apiKeys.listApiKeys();
     *   const oldKey = apiKeys.find(key => key.name === 'Old Frontend App');
     *
     *   if (oldKey) {
     *     const result = await sdk.apiKeys.revokeApiKey(oldKey.id);
     *     console.log('Revocation successful:', result.success);
     *     if (result.apiKey) {
     *       console.log('Revoked key:', result.apiKey.name);
     *     }
     *   }
     * } catch (error) {
     *   console.error('Failed to revoke API key:', error.message);
     * }
     * ```
     */
    async revokeApiKey(apiKeyId) {
        try {
            const result = await this.apiKeyApi.revokeApiKey(apiKeyId);
            return result;
        }
        catch (error) {
            console.error('[ApiKeyManager] Failed to revoke API key:', error);
            throw error;
        }
    }
}

/**
 * Analytics Manager - Clean, high-level interface for analytics operations
 *
 * Provides comprehensive analytics and business intelligence capabilities for the
 * loyalty ecosystem including transaction analysis, user behavior tracking, campaign
 * performance metrics, and business insights. Enables data-driven decision making
 * with real-time and historical analytics across all system domains.
 *
 * @group Managers
 * @category Analytics & Reporting
 *
 * @example Basic Transaction Analytics
 * ```typescript
 * // Get transaction analytics for the last 30 days
 * const analytics = await sdk.analytics.getTransactionAnalytics({
 *   startDate: '2024-01-01',
 *   endDate: '2024-01-31',
 *   groupBy: 'day',
 *   metrics: ['volume', 'count', 'averageValue']
 * });
 *
 * console.log('Transaction Trends:');
 * console.log(`Total Volume: $${analytics.totalVolume}`);
 * console.log(`Total Transactions: ${analytics.totalCount}`);
 * console.log(`Average Transaction: $${analytics.averageValue}`);
 * ```
 *
 * @example Business Performance Analysis
 * ```typescript
 * // Analyze performance by business partner
 * const businessAnalytics = await sdk.analytics.getTransactionAnalytics({
 *   startDate: '2024-01-01',
 *   endDate: '2024-12-31',
 *   groupBy: 'business',
 *   includeDetails: true
 * });
 *
 * console.log('Top Performing Partners:');
 * businessAnalytics.businessBreakdown?.forEach((business, index) => {
 *   console.log(`${index + 1}. ${business.name}`);
 *   console.log(`   Volume: $${business.volume}`);
 *   console.log(`   Transactions: ${business.count}`);
 * });
 * ```
 *
 * @example Advanced Analytics
 * ```typescript
 * // Get comprehensive analytics dashboard data
 * const analyticsService = sdk.analytics.getAnalyticsService();
 *
 * // Get multiple analytics in parallel
 * const [userMetrics, campaignMetrics, tokenMetrics] = await Promise.all([
 *   analyticsService.getUserEngagementMetrics(),
 *   analyticsService.getCampaignPerformance(),
 *   analyticsService.getTokenDistributionAnalytics()
 * ]);
 * ```
 */
class AnalyticsManager {
    constructor(apiClient) {
        this.apiClient = apiClient;
        const analyticsApi = new AnalyticsApi(apiClient);
        this.analyticsService = new AnalyticsService(analyticsApi);
    }
    /**
     * Get transaction analytics
     *
     * Retrieves comprehensive transaction analytics with flexible filtering,
     * grouping, and metric selection. Provides insights into transaction volumes,
     * trends, business performance, and user behavior patterns across the
     * loyalty ecosystem.
     *
     * @param request - Analytics request parameters including date range, filters, and metrics
     * @returns Promise resolving to transaction analytics data with metrics and breakdowns
     * @throws {PersApiError} When analytics data is unavailable or parameters are invalid
     *
     * @example Volume and Trend Analysis
     * ```typescript
     * try {
     *   const analytics = await sdk.analytics.getTransactionAnalytics({
     *     startDate: '2024-01-01T00:00:00Z',
     *     endDate: '2024-01-31T23:59:59Z',
     *     groupBy: 'day',
     *     metrics: ['volume', 'count', 'averageValue', 'tokens'],
     *     includeComparisons: true  // Compare with previous period
     *   });
     *
     *   console.log('Transaction Analytics Summary:');
     *   console.log(`Total Volume: $${analytics.totalVolume.toLocaleString()}`);
     *   console.log(`Total Transactions: ${analytics.totalCount.toLocaleString()}`);
     *   console.log(`Average Transaction: $${analytics.averageValue.toFixed(2)}`);
     *   console.log(`Tokens Distributed: ${analytics.totalTokens.toLocaleString()}`);
     *
     *   if (analytics.periodComparison) {
     *     const volumeChange = analytics.periodComparison.volumeChangePercent;
     *     const countChange = analytics.periodComparison.countChangePercent;
     *
     *     console.log('\nPeriod-over-Period Changes:');
     *     console.log(`Volume: ${volumeChange > 0 ? '+' : ''}${volumeChange}%`);
     *     console.log(`Count: ${countChange > 0 ? '+' : ''}${countChange}%`);
     *   }
     *
     *   // Display daily trend data
     *   if (analytics.dailyBreakdown) {
     *     console.log('\nDaily Trends:');
     *     analytics.dailyBreakdown.forEach(day => {
     *       console.log(`${day.date}: $${day.volume} (${day.count} transactions)`);
     *     });
     *   }
     *
     * } catch (error) {
     *   console.log('Analytics retrieval failed:', error.message);
     * }
     * ```
     *
     * @example Business Partner Analysis
     * ```typescript
     * // Analyze transaction performance by business partner
     * const businessAnalytics = await sdk.analytics.getTransactionAnalytics({
     *   startDate: '2024-01-01',
     *   endDate: '2024-03-31',
     *   groupBy: 'business',
     *   metrics: ['volume', 'count', 'averageValue', 'customerCount'],
     *   includeDetails: true,
     *   minimumTransactionCount: 10  // Only include businesses with 10+ transactions
     * });
     *
     * console.log('Business Partner Performance:');
     *
     * if (businessAnalytics.businessBreakdown) {
     *   // Sort by volume descending
     *   const sortedBusinesses = businessAnalytics.businessBreakdown
     *     .sort((a, b) => b.volume - a.volume);
     *
     *   console.log('\nTop 10 Partners by Volume:');
     *   sortedBusinesses.slice(0, 10).forEach((business, index) => {
     *     console.log(`\n${index + 1}. ${business.name}`);
     *     console.log(`   Volume: $${business.volume.toLocaleString()}`);
     *     console.log(`   Transactions: ${business.count}`);
     *     console.log(`   Avg Transaction: $${business.averageValue.toFixed(2)}`);
     *     console.log(`   Unique Customers: ${business.customerCount}`);
     *     console.log(`   Customer LTV: $${(business.volume / business.customerCount).toFixed(2)}`);
     *   });
     *
     *   // Identify growth opportunities
     *   const lowVolumeHighFrequency = sortedBusinesses.filter(b =>
     *     b.count > 50 && b.averageValue < 25
     *   );
     *
     *   if (lowVolumeHighFrequency.length > 0) {
     *     console.log('\nGrowth Opportunities (High Frequency, Low Value):');
     *     lowVolumeHighFrequency.forEach(business => {
     *       console.log(`${business.name}: ${business.count} transactions, $${business.averageValue} avg`);
     *     });
     *   }
     * }
     * ```
     *
     * @example Seasonal and Trend Analysis
     * ```typescript
     * // Analyze seasonal patterns and trends
     * const seasonalAnalytics = await sdk.analytics.getTransactionAnalytics({
     *   startDate: '2023-01-01',
     *   endDate: '2024-12-31',
     *   groupBy: 'month',
     *   metrics: ['volume', 'count'],
     *   includeSeasonalAnalysis: true,
     *   includeTrendLine: true
     * });
     *
     * console.log('Seasonal Analysis:');
     *
     * if (seasonalAnalytics.monthlyBreakdown) {
     *   const monthlyData = seasonalAnalytics.monthlyBreakdown;
     *
     *   // Find peak months
     *   const peakVolumeMonth = monthlyData.reduce((max, current) =>
     *     current.volume > max.volume ? current : max
     *   );
     *
     *   const peakCountMonth = monthlyData.reduce((max, current) =>
     *     current.count > max.count ? current : max
     *   );
     *
     *   console.log(`Peak Volume Month: ${peakVolumeMonth.month} ($${peakVolumeMonth.volume})`);
     *   console.log(`Peak Transaction Month: ${peakCountMonth.month} (${peakCountMonth.count})`);
     *
     *   // Calculate quarterly performance
     *   const quarters = {
     *     Q1: monthlyData.slice(0, 3).reduce((sum, m) => sum + m.volume, 0),
     *     Q2: monthlyData.slice(3, 6).reduce((sum, m) => sum + m.volume, 0),
     *     Q3: monthlyData.slice(6, 9).reduce((sum, m) => sum + m.volume, 0),
     *     Q4: monthlyData.slice(9, 12).reduce((sum, m) => sum + m.volume, 0)
     *   };
     *
     *   console.log('\nQuarterly Performance:');
     *   Object.entries(quarters).forEach(([quarter, volume]) => {
     *     console.log(`${quarter}: $${volume.toLocaleString()}`);
     *   });
     * }
     *
     * if (seasonalAnalytics.trendAnalysis) {
     *   console.log('\nTrend Analysis:');
     *   console.log(`Growth Rate: ${seasonalAnalytics.trendAnalysis.growthRate}%/month`);
     *   console.log(`Trend Direction: ${seasonalAnalytics.trendAnalysis.direction}`);
     *   console.log(`Seasonality Score: ${seasonalAnalytics.trendAnalysis.seasonalityScore}`);
     * }
     * ```
     *
     * @example Real-time Dashboard Metrics
     * ```typescript
     * // Get real-time metrics for dashboard display
     * const realTimeMetrics = await sdk.analytics.getTransactionAnalytics({
     *   startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Last 24 hours
     *   endDate: new Date().toISOString(),
     *   groupBy: 'hour',
     *   metrics: ['volume', 'count', 'tokens'],
     *   includeRealTimeMetrics: true
     * });
     *
     * console.log('⚡ Real-time Metrics (Last 24 Hours):');
     * console.log(`Current Volume: $${realTimeMetrics.totalVolume}`);
     * console.log(`Active Users: ${realTimeMetrics.activeUserCount}`);
     * console.log(`Transactions/Hour: ${realTimeMetrics.transactionsPerHour}`);
     *
     * if (realTimeMetrics.hourlyBreakdown) {
     *   // Show last 6 hours
     *   const lastSixHours = realTimeMetrics.hourlyBreakdown.slice(-6);
     *   console.log('\n� Last 6 Hours:');
     *   lastSixHours.forEach(hour => {
     *     console.log(`${hour.hour}:00 - $${hour.volume} (${hour.count} txns)`);
     *   });
     * }
     *
     * // Update dashboard in real-time
     * const updateDashboard = () => {
     *   document.getElementById('totalVolume').textContent = `$${realTimeMetrics.totalVolume}`;
     *   document.getElementById('transactionCount').textContent = realTimeMetrics.totalCount;
     *   document.getElementById('activeUsers').textContent = realTimeMetrics.activeUserCount;
     * };
     *
     * updateDashboard();
     * ```
     */
    async getTransactionAnalytics(request) {
        return this.analyticsService.getTransactionAnalytics(request);
    }
    /**
     * Get campaign claim analytics with aggregation
     *
     * Retrieves aggregated analytics for campaign claims with flexible grouping,
     * filtering, and metric selection. Provides insights into campaign performance,
     * claim patterns, and user engagement across the loyalty ecosystem.
     *
     * @param request - Analytics request with filters, groupBy, and metrics
     * @returns Promise resolving to campaign claim analytics data
     *
     * @example Claims per campaign
     * ```typescript
     * const analytics = await sdk.analytics.getCampaignClaimAnalytics({
     *   filters: { status: 'COMPLETED' },
     *   groupBy: ['campaignId'],
     *   metrics: ['count'],
     *   sortBy: 'count',
     *   sortOrder: 'DESC',
     *   limit: 10
     * });
     *
     * console.log('Top campaigns:', analytics.results);
     * ```
     */
    async getCampaignClaimAnalytics(request) {
        return this.analyticsService.getCampaignClaimAnalytics(request);
    }
    /**
     * Get redemption redeem analytics with aggregation
     *
     * Retrieves aggregated redemption redeem data with flexible grouping and metrics.
     * Perfect for charts, dashboards, and redeem pattern analysis across redemptions,
     * businesses, and time periods.
     *
     * @param request - Analytics request with filters, groupBy, and metrics
     * @returns Promise resolving to redemption redeem analytics data
     *
     * @example Redeems per redemption
     * ```typescript
     * const analytics = await sdk.analytics.getRedemptionRedeemAnalytics({
     *   groupBy: ['redemptionId'],
     *   metrics: ['count'],
     *   sortBy: 'count',
     *   sortOrder: 'DESC',
     *   limit: 10
     * });
     *
     * console.log('Top redemptions by redeems:', analytics.results);
     * ```
     *
     * @example Redeems by status over time
     * ```typescript
     * const analytics = await sdk.analytics.getRedemptionRedeemAnalytics({
     *   groupBy: ['month', 'status'],
     *   metrics: ['count'],
     *   sortBy: 'month',
     *   sortOrder: 'DESC',
     *   startDate: new Date('2026-01-01'),
     *   endDate: new Date('2026-12-31')
     * });
     * ```
     *
     * @example Redeems by business
     * ```typescript
     * const analytics = await sdk.analytics.getRedemptionRedeemAnalytics({
     *   filters: { status: 'COMPLETED' },
     *   groupBy: ['businessId'],
     *   metrics: ['count'],
     *   sortBy: 'count',
     *   sortOrder: 'DESC'
     * });
     * ```
     */
    async getRedemptionRedeemAnalytics(request) {
        return this.analyticsService.getRedemptionRedeemAnalytics(request);
    }
    /**
     * Get user analytics with engagement metrics
     *
     * Retrieves aggregated user statistics including engagement rate, active users,
     * transaction metrics, and per-active-user averages. Provides comprehensive
     * insights into user behavior, engagement patterns, and platform adoption metrics.
     *
     * NEW: Added per-active-user metrics (averageTransactionsPerActiveUser, etc.) which
     * show concentrated engagement among active users - more useful than diluted all-user averages.
     *
     * Request structure matches TransactionAnalytics and CampaignClaimAnalytics:
     * - Use filters object for business-specific scoping
     * - startDate/endDate at root level for date range filtering
     *
     * @param request - Analytics request with optional filters and date range
     * @returns Promise resolving to user analytics data with per-user and per-active-user metrics
     *
     * @example Basic user analytics (compare per-user vs per-active-user)
     * ```typescript
     * const analytics = await sdk.analytics.getUserAnalytics({});
     * console.log(`Total users: ${analytics.totalUsers}`);
     * console.log(`Active users: ${analytics.activeUsers} (${analytics.engagementRate.toFixed(1)}%)`);
     *
     * // Per-user averages (includes inactive users - lower numbers)
     * console.log(`\\nPer-User Averages (all users):`);
     * console.log(`  Transactions: ${analytics.averageTransactionsPerUser.toFixed(2)}`);
     * console.log(`  Claims: ${analytics.averageClaimsPerUser.toFixed(2)}`);
     * console.log(`  Redemptions: ${analytics.averageRedemptionsPerUser.toFixed(2)}`);
     *
     * // Per-active-user averages (only engaged users - higher, more meaningful numbers)
     * console.log(`\\nPer-Active-User Averages (engaged users only):`);
     * console.log(`  Transactions: ${analytics.averageTransactionsPerActiveUser.toFixed(2)}`);
     * console.log(`  Claims: ${analytics.averageClaimsPerActiveUser.toFixed(2)}`);
     * console.log(`  Redemptions: ${analytics.averageRedemptionsPerActiveUser.toFixed(2)}`);
     * ```
     *
     * @example Monthly user analytics with date range
     * ```typescript
     * const analytics = await sdk.analytics.getUserAnalytics({
     *   startDate: new Date('2026-02-01'),
     *   endDate: new Date('2026-02-28')
     * });
     *
     * console.log('February 2026 User Metrics:');
     * console.log(`Total users: ${analytics.totalUsers}`);
     * console.log(`Active users: ${analytics.activeUsers}`);
     * console.log(`New users: ${analytics.newUsers}`);
     * console.log(`Engagement rate: ${analytics.engagementRate.toFixed(2)}%`);
     * console.log(`Total transaction volume: $${analytics.totalTransactionVolume.toLocaleString()}`);
     * console.log(`Avg transaction amount: $${analytics.averageTransactionAmount.toFixed(2)}`);
     * console.log(`\\nFilters applied: ${analytics.metadata.dateRange?.startDate} to ${analytics.metadata.dateRange?.endDate}`);
     * ```
     *
     * @example Business-specific user analytics with filters
     * ```typescript
     * const analytics = await sdk.analytics.getUserAnalytics({
     *   filters: { businessId: 'business-123' },
     *   startDate: new Date('2026-01-01'),
     *   endDate: new Date('2026-12-31')
     * });
     *
     * console.log(`Business customer metrics for 2026:`);
     * console.log(`Active customers: ${analytics.activeUsers} / ${analytics.totalUsers}`);
     * console.log(`Customer engagement: ${analytics.engagementRate.toFixed(1)}%`);
     * console.log(`Avg spend per customer: $${analytics.averageTransactionAmount.toFixed(2)}`);
     * console.log(`Avg spend per active customer: $${(analytics.totalTransactionVolume / analytics.activeUsers).toFixed(2)}`);
     * console.log(`Avg claims per active customer: ${analytics.averageClaimsPerActiveUser.toFixed(2)}`);
     * ```
     */
    async getUserAnalytics(request = {}) {
        return this.analyticsService.getUserAnalytics(request);
    }
    /**
     * Get user transaction ranking with enriched user data
     *
     * Retrieves ranked list of users with full user details (email, externalUserId)
     * and transaction metrics. Data enrichment happens via efficient SQL JOINs + UNION
     * (handles legacy transactions). Ideal for leaderboards, engagement analysis, and
     * identifying power users for targeted campaigns.
     *
     * Use Cases:
     * - Admin leaderboards showing top users by activity
     * - User engagement analysis with full user context
     * - Identifying power users for campaigns
     * - Customer segmentation by transaction behavior
     *
     * @param request - Ranking request with filters, sorting, and limit
     * @returns Promise resolving to ranked user list with transaction metrics
     *
     * @example Top 50 users by transaction count
     * ```typescript
     * const ranking = await sdk.analytics.getUserRanking({
     *   sortBy: 'totalTransactions',
     *   sortOrder: 'DESC',
     *   limit: 50
     * });
     *
     * console.log(`Top ${ranking.totalUsers} users:`);
     * ranking.results.forEach((user, index) => {
     *   console.log(`#${index + 1}: ${user.email || user.externalUserId}`);
     *   console.log(`  Transactions: ${user.totalTransactions}`);
     *   console.log(`  Token spent: ${user.tokenSpent}`);
     * });
     * ```
     *
     * @example Top users by STAMP spending
     * ```typescript
     * const ranking = await sdk.analytics.getUserRanking({
     *   filters: { tokenType: 'STAMP' },
     *   sortBy: 'tokenSpent',
     *   sortOrder: 'DESC',
     *   limit: 20
     * });
     *
     * console.log('Top 20 STAMP spenders:');
     * ranking.results.forEach((user, index) => {
     *   const identifier = user.email || user.externalUserId || user.userId;
     *   console.log(`${index + 1}. ${identifier} - ${user.tokenSpent} STAMP`);
     * });
     * ```
     */
    async getUserRanking(request = {}) {
        return this.analyticsService.getUserRanking(request);
    }
    /**
     * Get business transaction ranking with enriched business data
     *
     * Returns ranked list of businesses with full business details and transaction metrics.
     *
     * @param request - Ranking request with filters, sorting, and limit
     * @returns Promise resolving to ranked business list
     */
    async getBusinessRanking(request = {}) {
        return this.analyticsService.getBusinessRanking(request);
    }
    /**
     * Get monthly user retention analytics
     *
     * Returns monthly retention data with user metrics and retention rates.
     *
     * @param request - Retention request with monthsBack and filters
     * @returns Promise resolving to monthly retention data
     */
    async getRetentionAnalytics(request = {}) {
        return this.analyticsService.getRetentionAnalytics(request);
    }
    /**
     * Get tag usage analytics across entities
     *
     * Aggregates tag usage across all taggable entities (campaigns, redemptions,
     * businesses, token metadata, user status types). Perfect for:
     * - Tag autocomplete/suggestions
     * - Tag management dashboards
     * - Usage analytics
     *
     * @param request - Optional filters for entity types
     * @returns Promise resolving to aggregated tag usage data
     *
     * @example Get all tags for autocomplete
     * ```typescript
     * const allTags = await sdk.analytics.getTagAnalytics();
     * const suggestions = allTags.tags.map(t => t.tag);
     * ```
     *
     * @example Get tags only from campaigns
     * ```typescript
     * import { TaggableEntityType } from '@explorins/pers-sdk';
     *
     * const campaignTags = await sdk.analytics.getTagAnalytics({
     *   entityTypes: [TaggableEntityType.CAMPAIGN]
     * });
     * ```
     */
    async getTagAnalytics(request = {}) {
        return this.analyticsService.getTagAnalytics(request);
    }
    /**
     * Get the full analytics service for advanced operations
     *
     * Provides access to the complete AnalyticsService instance for advanced analytics
     * operations, custom metrics, predictive analytics, cohort analysis, and
     * operations not covered by the high-level manager methods.
     *
     * @returns AnalyticsService instance with full API access
     *
     * @example Advanced Analytics Operations
     * ```typescript
     * const analyticsService = sdk.analytics.getAnalyticsService();
     *
     * // Access user engagement analytics
     * const engagementMetrics = await analyticsService.getUserEngagementMetrics({
     *   timeframe: 'last-90-days',
     *   includeSegmentation: true
     * });
     *
     * // Access campaign performance analytics
     * const campaignMetrics = await analyticsService.getCampaignPerformance({
     *   campaignIds: ['summer-2024', 'holiday-2024'],
     *   includeConversionFunnel: true
     * });
     *
     * // Access token distribution analytics
     * const tokenAnalytics = await analyticsService.getTokenDistributionAnalytics({
     *   tokenTypes: ['CREDIT', 'REWARD'],
     *   includePredictions: true
     * });
     *
     * // Access cohort analysis
     * const cohorts = await analyticsService.getCohortAnalysis({
     *     cohortType: 'monthly',
     *     startDate: '2024-01-01',
     *     metrics: ['retention', 'revenue', 'activity']
     * });
     *
     * // Access predictive analytics
     * const predictions = await analyticsService.getPredictiveAnalytics({
     *   model: 'customer-lifetime-value',
     *   horizon: '12-months'
     * });
     *
     * // Access real-time streaming analytics
     * const realTimeStream = analyticsService.getRealtimeMetrics();
     * realTimeStream.on('update', (metrics) => {
     *   console.log('Real-time update:', metrics);
     * });
     *
     * // Access custom analytics queries
     * const customAnalytics = await analyticsService.executeCustomQuery({
     *   query: 'business-performance-by-region',
     *   parameters: { region: 'north-america', timeframe: 'Q1-2024' }
     * });
     *
     * console.log('Advanced analytics data retrieved');
     * ```
     */
    getAnalyticsService() {
        return this.analyticsService;
    }
}

/**
 * Donation Manager - Clean, high-level interface for donation operations
 *
 * Provides a simplified API for common donation tasks while maintaining
 * access to the full donation SDK for advanced use cases.
 */
class DonationManager {
    constructor(apiClient) {
        this.apiClient = apiClient;
        const donationApi = new DonationApi(apiClient);
        this.donationService = new DonationService(donationApi);
    }
    /**
     * Get available donation types
     *
     * @returns Promise resolving to paginated donation types
     */
    async getDonationTypes(options) {
        return this.donationService.getAllDonationTypes(options);
    }
    /**
     * Get the full donation service for advanced operations
     *
     * @returns DonationService instance
     */
    getDonationService() {
        return this.donationService;
    }
}

/**
 * TriggerSource Manager - Clean, high-level interface for trigger source operations
 *
 * Manages trigger sources which are physical or digital activation points for campaigns:
 * - QR_CODE: Scannable QR codes
 * - NFC_TAG: NFC tap points
 * - GPS_GEOFENCE: Location-based triggers
 * - API_WEBHOOK: External system integration
 * - TRANSACTION: Purchase/payment based triggers
 *
 * Trigger sources are independent entities that can be created, managed, and then
 * assigned to campaigns. This separation allows reuse and flexible campaign configuration.
 *
 * @group Managers
 * @category TriggerSource Management
 *
 * @example Basic TriggerSource Operations
 * ```typescript
 * // Get all QR code trigger sources
 * const qrSources = await sdk.triggerSources.getAll({ type: 'QR_CODE' });
 *
 * // Create a new QR code trigger source
 * const source = await sdk.triggerSources.create({
 *   type: 'QR_CODE',
 *   name: 'Store Entrance QR',
 *   businessId: 'business-123'
 * });
 *
 * // Assign to campaign (via CampaignManager)
 * await sdk.campaigns.assignTriggerSource('campaign-456', source.id);
 * ```
 */
class TriggerSourceManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
        const triggerSourceApi = new TriggerSourceApi(apiClient);
        this.triggerSourceService = new TriggerSourceService(triggerSourceApi);
    }
    /**
     * Get trigger sources with optional filters and pagination
     *
     * Retrieves trigger sources (QR codes, NFC tags, GPS geofences, webhooks, etc.)
     * that can be used to activate campaigns. Supports filtering by type, business,
     * campaign association, and active status.
     *
     * @param options - Filter and pagination options
     * @returns Promise resolving to paginated trigger sources
     *
     * @example
     * ```typescript
     * // Get all QR code trigger sources
     * const qrSources = await sdk.triggerSources.getAll({ type: 'QR_CODE' });
     *
     * // Get trigger sources for a specific business
     * const businessSources = await sdk.triggerSources.getAll({
     *   businessId: 'business-123',
     *   active: true,
     *   page: 1,
     *   limit: 20
     * });
     *
     * // Get trigger sources assigned to a campaign
     * const campaignSources = await sdk.triggerSources.getAll({
     *   campaignId: 'campaign-456'
     * });
     * ```
     */
    async getAll(options) {
        return this.triggerSourceService.getTriggerSources(options);
    }
    /**
     * Get trigger source by ID
     *
     * Retrieves detailed information for a specific trigger source including
     * its type, location, metadata, and usage analytics.
     *
     * @param triggerSourceId - UUID of the trigger source
     * @returns Promise resolving to trigger source details
     * @throws {PersApiError} When trigger source with specified ID is not found
     *
     * @example
     * ```typescript
     * const source = await sdk.triggerSources.getById('source-123');
     *
     * console.log('Trigger Source:', source.name);
     * console.log('Type:', source.type);
     * console.log('Active:', source.isActive);
     *
     * if (source.coordsLatitude && source.coordsLongitude) {
     *   console.log('Location:', source.coordsLatitude, source.coordsLongitude);
     * }
     * ```
     */
    async getById(triggerSourceId) {
        return this.triggerSourceService.getTriggerSourceById(triggerSourceId);
    }
    /**
     * Admin: Create a new trigger source
     *
     * Creates a new trigger source (QR code, NFC tag, GPS geofence, webhook, or
     * transaction-based trigger) that can be assigned to campaigns. Requires
     * administrator privileges.
     *
     * @param triggerSource - Trigger source creation data
     * @returns Promise resolving to created trigger source
     * @throws {PersApiError} When not authenticated as admin or validation fails
     *
     * @example
     * ```typescript
     * // Create a QR code trigger source
     * const qrSource = await sdk.triggerSources.create({
     *   type: 'QR_CODE',
     *   name: 'Store Entrance QR',
     *   description: 'QR code at main entrance',
     *   businessId: 'business-123',
     *   coordsLatitude: 47.6062,
     *   coordsLongitude: -122.3321
     * });
     *
     * // Create an NFC tag trigger source
     * const nfcSource = await sdk.triggerSources.create({
     *   type: 'NFC_TAG',
     *   name: 'Product Display NFC',
     *   metadata: { productId: 'SKU-12345' }
     * });
     *
     * // Create a GPS geofence trigger source
     * const geoSource = await sdk.triggerSources.create({
     *   type: 'GPS_GEOFENCE',
     *   name: 'Store Area',
     *   coordsLatitude: 47.6062,
     *   coordsLongitude: -122.3321,
     *   metadata: { radiusMeters: 100 }
     * });
     * ```
     */
    async create(triggerSource) {
        const result = await this.triggerSourceService.createTriggerSource(triggerSource);
        this.events?.emitSuccess({
            domain: 'trigger-source',
            type: 'trigger_source_created',
            userMessage: 'Trigger source created successfully',
            details: { triggerSourceId: result.id, type: result.type }
        });
        return result;
    }
    /**
     * Admin: Update a trigger source
     *
     * Updates an existing trigger source's configuration. All fields are optional;
     * only provided fields will be updated. Requires administrator privileges.
     *
     * @param triggerSourceId - UUID of the trigger source to update
     * @param triggerSource - Updated trigger source data (partial)
     * @returns Promise resolving to updated trigger source
     * @throws {PersApiError} When not authenticated as admin or trigger source not found
     *
     * @example
     * ```typescript
     * // Update trigger source name and location
     * const updated = await sdk.triggerSources.update('source-123', {
     *   name: 'Updated Store Entrance QR',
     *   description: 'New description',
     *   coordsLatitude: 47.6063,
     *   coordsLongitude: -122.3322
     * });
     * ```
     */
    async update(triggerSourceId, triggerSource) {
        const result = await this.triggerSourceService.updateTriggerSource(triggerSourceId, triggerSource);
        this.events?.emitSuccess({
            domain: 'trigger-source',
            type: 'trigger_source_updated',
            userMessage: 'Trigger source updated successfully',
            details: { triggerSourceId }
        });
        return result;
    }
    /**
     * Admin: Delete a trigger source
     *
     * Soft deletes a trigger source, making it inactive. The trigger source will
     * be removed from any campaigns it was assigned to. Requires administrator
     * privileges.
     *
     * @param triggerSourceId - UUID of the trigger source to delete
     * @returns Promise resolving to success status
     * @throws {PersApiError} When not authenticated as admin or trigger source not found
     *
     * @example
     * ```typescript
     * const success = await sdk.triggerSources.delete('source-123');
     * console.log('Trigger source deleted:', success);
     * ```
     */
    async delete(triggerSourceId) {
        const result = await this.triggerSourceService.deleteTriggerSource(triggerSourceId);
        this.events?.emitSuccess({
            domain: 'trigger-source',
            type: 'trigger_source_deleted',
            userMessage: 'Trigger source deleted successfully',
            details: { triggerSourceId }
        });
        return result;
    }
    /**
     * Get the full trigger source service for advanced operations
     *
     * @returns TriggerSourceService instance with full API access
     */
    getTriggerSourceService() {
        return this.triggerSourceService;
    }
}

/**
 * Webhook API - Low-level API client for webhook operations
 *
 * Backend routes:
 * - Admin CRUD: /hooks (requires tenant auth)
 * - Proxy/Trigger: /hooks/:projectKey/:hookId (public, identified by projectKey)
 * - Callback: /hooks/:projectKey/executions/:executionId/callback (public)
 *
 * @internal Use WebhookService or WebhookManager for higher-level operations
 */
class WebhookApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/hooks';
    }
    // ================================
    // ADMIN: Webhook Configuration CRUD
    // All require tenant auth (handled by apiClient)
    // ================================
    /**
     * List all webhooks (Admin)
     * GET /hooks
     */
    async listWebhooks(options) {
        const params = new URLSearchParams();
        if (options?.active !== undefined)
            params.append('active', String(options.active));
        if (options?.page)
            params.append('page', String(options.page));
        if (options?.limit)
            params.append('limit', String(options.limit));
        if (options?.search)
            params.append('search', options.search);
        const queryString = params.toString();
        const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
        return this.apiClient.get(url);
    }
    /**
     * Get webhook by ID (Admin)
     * GET /hooks/:hookId
     */
    async getWebhookById(webhookId) {
        return this.apiClient.get(`${this.basePath}/${webhookId}`);
    }
    /**
     * Create a new webhook (Admin)
     * POST /hooks
     */
    async createWebhook(webhook) {
        return this.apiClient.post(this.basePath, webhook);
    }
    /**
     * Update a webhook (Admin)
     * PUT /hooks/:hookId
     */
    async updateWebhook(webhookId, webhook) {
        return this.apiClient.put(`${this.basePath}/${webhookId}`, webhook);
    }
    /**
     * Delete a webhook (Admin)
     * DELETE /hooks/:hookId
     */
    async deleteWebhook(webhookId) {
        return this.apiClient.delete(`${this.basePath}/${webhookId}`);
    }
    // ================================
    // Webhook Triggering via Proxy
    // Route: /hooks/:projectKey/:hookId
    // Public endpoint identified by projectKey in URL
    // ================================
    /**
     * Trigger a webhook programmatically
     *
     * Uses the proxy endpoint which requires projectKey in the URL path.
     * The projectKey is obtained from SDK config.
     *
     * ALL /hooks/:projectKey/:hookId
     */
    async triggerWebhook(request, projectKey) {
        const { hookId, method = 'POST', body, queryParams, waitForCallback, callbackTimeoutMs } = request;
        // Build query string from queryParams
        const params = new URLSearchParams();
        if (queryParams) {
            Object.entries(queryParams).forEach(([key, value]) => params.append(key, value));
        }
        if (waitForCallback)
            params.append('waitForCallback', 'true');
        if (callbackTimeoutMs)
            params.append('callbackTimeoutMs', String(callbackTimeoutMs));
        const queryString = params.toString();
        // Use proxy path with projectKey
        const proxyPath = `${this.basePath}/${projectKey}/${hookId}`;
        const url = queryString ? `${proxyPath}?${queryString}` : proxyPath;
        // Use appropriate HTTP method - backend returns WebhookTriggerResponseDTO
        switch (method) {
            case 'GET':
                return this.apiClient.get(url);
            case 'PUT':
                return this.apiClient.put(url, body);
            case 'DELETE':
                return this.apiClient.delete(url);
            case 'POST':
            default:
                return this.apiClient.post(url, body);
        }
    }
    // ================================
    // Execution History (Admin)
    // GET /hooks/executions
    // ================================
    /**
     * List webhook executions (Admin)
     * GET /hooks/executions
     */
    async listExecutions(options) {
        const params = new URLSearchParams();
        if (options?.webhookId)
            params.append('hookId', options.webhookId);
        if (options?.status)
            params.append('status', options.status);
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        if (options?.page)
            params.append('page', String(options.page));
        if (options?.limit)
            params.append('limit', String(options.limit));
        const queryString = params.toString();
        const url = queryString ? `${this.basePath}/executions?${queryString}` : `${this.basePath}/executions`;
        return this.apiClient.get(url);
    }
    /**
     * Get execution by ID (Admin)
     * Note: This endpoint doesn't exist in current backend - would need to filter by ID
     */
    async getExecutionById(executionId) {
        // Filter executions by ID since direct endpoint doesn't exist
        const result = await this.listExecutions({ page: 1, limit: 1 });
        const execution = result.data.find(e => e.id === executionId);
        if (!execution) {
            throw new Error(`Execution ${executionId} not found`);
        }
        return execution;
    }
    // ================================
    // Callback (Public - called by external systems)
    // POST /hooks/:projectKey/executions/:executionId/callback
    // ================================
    /**
     * Send callback result (for testing or manual callback trigger)
     * This is normally called by external systems like n8n
     *
     * POST /hooks/:projectKey/executions/:executionId/callback
     */
    async sendCallback(executionId, callback, projectKey) {
        return this.apiClient.post(`${this.basePath}/${projectKey}/executions/${executionId}/callback`, callback);
    }
}

/**
 * Webhook Service - Business logic layer for webhook operations
 *
 * Provides higher-level operations on top of WebhookApi including:
 * - Validation and error handling
 * - Convenience methods for common patterns
 * - Async workflow support with callbacks
 *
 * Note: Trigger operations require projectKey which is obtained from SDK config.
 *
 * @group Services
 * @category Webhook
 */
class WebhookService {
    constructor(webhookApi, projectKey) {
        this.webhookApi = webhookApi;
        this.projectKey = projectKey;
    }
    // ================================
    // Admin: Webhook Configuration
    // ================================
    /**
     * Get all webhooks with optional filters
     */
    async getWebhooks(options) {
        return this.webhookApi.listWebhooks(options);
    }
    /**
     * Get active webhooks only
     */
    async getActiveWebhooks() {
        return this.webhookApi.listWebhooks({ active: true });
    }
    /**
     * Get webhook by ID
     */
    async getWebhookById(webhookId) {
        return this.webhookApi.getWebhookById(webhookId);
    }
    /**
     * Create a new webhook
     */
    async createWebhook(webhook) {
        // Validate required fields
        if (!webhook.name?.trim()) {
            throw new Error('Webhook name is required');
        }
        if (!webhook.targetUrl?.trim()) {
            throw new Error('Target URL is required');
        }
        // Validate URL format
        try {
            new URL(webhook.targetUrl);
        }
        catch {
            throw new Error('Invalid target URL format');
        }
        return this.webhookApi.createWebhook(webhook);
    }
    /**
     * Update a webhook
     */
    async updateWebhook(webhookId, webhook) {
        // Validate URL if provided (cast to access optional property from PartialType)
        const update = webhook;
        if (update.targetUrl) {
            try {
                new URL(update.targetUrl);
            }
            catch {
                throw new Error('Invalid target URL format');
            }
        }
        return this.webhookApi.updateWebhook(webhookId, webhook);
    }
    /**
     * Enable a webhook
     */
    async enableWebhook(webhookId) {
        return this.webhookApi.updateWebhook(webhookId, { isActive: true });
    }
    /**
     * Disable a webhook
     */
    async disableWebhook(webhookId) {
        return this.webhookApi.updateWebhook(webhookId, { isActive: false });
    }
    /**
     * Delete a webhook
     */
    async deleteWebhook(webhookId) {
        return this.webhookApi.deleteWebhook(webhookId);
    }
    // ================================
    // Webhook Triggering (via Proxy)
    // Uses /hooks/:projectKey/:hookId
    // ================================
    /**
     * Trigger a webhook with full control over request parameters
     */
    async triggerWebhook(request) {
        return this.webhookApi.triggerWebhook(request, this.projectKey);
    }
    /**
     * Simple POST trigger with JSON body
     */
    async post(hookId, body) {
        return this.webhookApi.triggerWebhook({
            hookId,
            method: WebhookMethod.POST,
            body
        }, this.projectKey);
    }
    /**
     * Simple GET trigger
     */
    async get(hookId, queryParams) {
        return this.webhookApi.triggerWebhook({
            hookId,
            method: WebhookMethod.GET,
            queryParams
        }, this.projectKey);
    }
    /**
     * Trigger and wait for async callback (for workflow systems like n8n)
     *
     * @param hookId - Webhook ID to trigger
     * @param body - Request body
     * @param timeoutMs - How long to wait for callback (default: 30000ms)
     */
    async triggerAndWait(hookId, body, timeoutMs = 30000) {
        return this.webhookApi.triggerWebhook({
            hookId,
            method: WebhookMethod.POST,
            body,
            waitForCallback: true,
            callbackTimeoutMs: timeoutMs
        }, this.projectKey);
    }
    // ================================
    // Execution History (Admin)
    // ================================
    /**
     * Get execution history with filters
     */
    async getExecutions(options) {
        return this.webhookApi.listExecutions(options);
    }
    /**
     * Get executions for a specific webhook
     */
    async getWebhookExecutions(webhookId, options) {
        return this.webhookApi.listExecutions({ ...options, webhookId });
    }
    /**
     * Get failed executions
     */
    async getFailedExecutions(options) {
        return this.webhookApi.listExecutions({ ...options, status: WebhookExecutionStatus.FAILED });
    }
    /**
     * Get execution details by ID
     */
    async getExecutionById(executionId) {
        return this.webhookApi.getExecutionById(executionId);
    }
    // ================================
    // Callback (for testing/manual triggers)
    // ================================
    /**
     * Send a callback result for an execution
     * Normally this is called by external systems like n8n
     */
    async sendCallback(executionId, callback) {
        return this.webhookApi.sendCallback(executionId, callback, this.projectKey);
    }
}

/**
 * Webhook Manager - Clean, high-level interface for webhook operations
 *
 * Webhooks enable integration with external systems (n8n, Zapier, custom backends):
 * - **Admin operations**: Create, configure, and manage webhook endpoints
 * - **Trigger operations**: Programmatically trigger webhooks with payloads
 * - **Async workflows**: Wait for callbacks from workflow systems
 * - **Monitoring**: Track execution history
 *
 * ## Security Model
 * - Admin endpoints require tenant authentication
 * - Trigger endpoints work with user/business/tenant auth
 * - Caller identity is passed to webhook target
 *
 * @group Managers
 * @category Webhook Management
 *
 * @example Basic Webhook Operations
 * ```typescript
 * // Admin: Create a webhook for order processing
 * const webhook = await sdk.webhooks.create({
 *   name: 'Process Order',
 *   targetUrl: 'https://n8n.example.com/webhook/orders',
 *   method: 'POST',
 *   headers: { 'X-Custom-Header': 'value' }
 * });
 *
 * // Trigger webhook with order data
 * const result = await sdk.webhooks.trigger(webhook.id, {
 *   orderId: 'order-123',
 *   items: [{ productId: 'prod-1', quantity: 2 }]
 * });
 *
 * console.log('Execution ID:', result.executionId);
 * ```
 *
 * @example Async Workflow with Callback
 * ```typescript
 * // Trigger and wait for workflow completion (up to 30s)
 * const result = await sdk.webhooks.triggerAndWait(
 *   'ai-processing-webhook',
 *   { prompt: 'Generate report for Q1' },
 *   30000
 * );
 *
 * if (result.status === 'COMPLETED') {
 *   console.log('AI Response:', result.body);
 * }
 * ```
 */
class WebhookManager {
    constructor(apiClient, projectKey, events) {
        this.apiClient = apiClient;
        this.projectKey = projectKey;
        this.events = events;
        const webhookApi = new WebhookApi(apiClient);
        this.webhookService = new WebhookService(webhookApi, projectKey);
    }
    // ================================
    // Admin: Webhook Configuration
    // ================================
    /**
     * Get all webhooks with optional filters (Admin)
     *
     * @param options - Filter and pagination options
     * @returns Promise resolving to paginated webhooks
     *
     * @example
     * ```typescript
     * // Get all webhooks
     * const webhooks = await sdk.webhooks.getAll();
     *
     * // Get only active webhooks
     * const active = await sdk.webhooks.getAll({ active: true });
     *
     * // Search by name
     * const found = await sdk.webhooks.getAll({ search: 'order' });
     * ```
     */
    async getAll(options) {
        return this.webhookService.getWebhooks(options);
    }
    /**
     * Get active webhooks only (Admin)
     */
    async getActive() {
        return this.webhookService.getActiveWebhooks();
    }
    /**
     * Get webhook by ID (Admin)
     *
     * @param webhookId - UUID of the webhook
     * @returns Promise resolving to webhook details
     */
    async getById(webhookId) {
        return this.webhookService.getWebhookById(webhookId);
    }
    /**
     * Create a new webhook (Admin)
     *
     * @param webhook - Webhook configuration
     * @returns Promise resolving to created webhook
     *
     * @example
     * ```typescript
     * const webhook = await sdk.webhooks.create({
     *   name: 'Order Notifications',
     *   targetUrl: 'https://api.example.com/webhooks/orders',
     *   method: 'POST',
     *   headers: {
     *     'Authorization': 'Bearer secret-token'
     *   },
     *   rateLimitPerMinute: 60
     * });
     * ```
     */
    async create(webhook) {
        const result = await this.webhookService.createWebhook(webhook);
        this.events?.emitSuccess({
            domain: 'webhook',
            type: 'webhook_created',
            userMessage: 'Webhook created successfully',
            details: { webhookId: result.id, name: result.name }
        });
        return result;
    }
    /**
     * Update a webhook (Admin)
     *
     * @param webhookId - UUID of the webhook to update
     * @param webhook - Updated configuration (partial)
     * @returns Promise resolving to updated webhook
     */
    async update(webhookId, webhook) {
        const result = await this.webhookService.updateWebhook(webhookId, webhook);
        this.events?.emitSuccess({
            domain: 'webhook',
            type: 'webhook_updated',
            userMessage: 'Webhook updated successfully',
            details: { webhookId }
        });
        return result;
    }
    /**
     * Enable a webhook (Admin)
     */
    async enable(webhookId) {
        return this.webhookService.enableWebhook(webhookId);
    }
    /**
     * Disable a webhook (Admin)
     */
    async disable(webhookId) {
        return this.webhookService.disableWebhook(webhookId);
    }
    /**
     * Delete a webhook (Admin)
     *
     * @param webhookId - UUID of the webhook to delete
     * @returns Promise resolving to deletion confirmation
     */
    async delete(webhookId) {
        const result = await this.webhookService.deleteWebhook(webhookId);
        this.events?.emitSuccess({
            domain: 'webhook',
            type: 'webhook_deleted',
            userMessage: 'Webhook deleted successfully',
            details: { webhookId }
        });
        return result;
    }
    // ================================
    // Webhook Triggering
    // ================================
    /**
     * Trigger a webhook with GET method
     *
     * Sends a GET request through the webhook proxy. Returns the full response
     * including execution metadata and raw data from the target.
     *
     * @param hookId - Webhook ID to trigger
     * @param queryParams - Optional query parameters
     * @returns Promise resolving to trigger response with metadata and data
     *
     * @example
     * ```typescript
     * // Fetch data from external API via webhook proxy
     * const result = await sdk.webhooks.get('user-directory-webhook');
     * console.log('Success:', result.success);
     * console.log('Data:', result.data); // Raw data from target
     * ```
     */
    async get(hookId, queryParams) {
        return this.webhookService.get(hookId, queryParams);
    }
    /**
     * Trigger a webhook with POST method
     *
     * Sends data to the configured webhook target. The caller's identity
     * (user/business/tenant) is included in the request context.
     *
     * @param hookId - Webhook ID to trigger
     * @param body - Payload to send
     * @returns Promise resolving to trigger response with execution metadata
     *
     * @example
     * ```typescript
     * const result = await sdk.webhooks.trigger('order-webhook', {
     *   orderId: 'order-123',
     *   action: 'created',
     *   items: [...]
     * });
     *
     * console.log('Success:', result.success);
     * console.log('Execution ID:', result.executionId);
     * console.log('Response:', result.data);
     * ```
     */
    async trigger(hookId, body) {
        const result = await this.webhookService.post(hookId, body);
        this.events?.emitSuccess({
            domain: 'webhook',
            type: 'webhook_triggered',
            userMessage: 'Webhook triggered',
            details: { hookId, executionId: result.executionId }
        });
        return result;
    }
    /**
     * Trigger a webhook and wait for async callback
     *
     * Use this for workflow systems (n8n, Zapier) that need time to process
     * and return results via callback. The SDK will wait for the callback
     * up to the specified timeout.
     *
     * @param hookId - Webhook ID to trigger
     * @param body - Payload to send
     * @param timeoutMs - Max time to wait for callback (default: 30s)
     * @returns Promise resolving when callback received or timeout
     *
     * @example
     * ```typescript
     * // Trigger AI workflow and wait for result
     * const result = await sdk.webhooks.triggerAndWait(
     *   'ai-analysis-webhook',
     *   { documentId: 'doc-123', analysisType: 'sentiment' },
     *   60000 // Wait up to 60 seconds
     * );
     *
     * if (result.success) {
     *   console.log('Analysis:', result.data);
     * }
     * ```
     */
    async triggerAndWait(hookId, body, timeoutMs = 30000) {
        return this.webhookService.triggerAndWait(hookId, body, timeoutMs);
    }
    // ================================
    // Execution History & Monitoring
    // ================================
    /**
     * Get webhook execution history (Admin)
     *
     * @param options - Filter and pagination options
     * @returns Promise resolving to paginated executions
     *
     * @example
     * ```typescript
     * // Get recent failed executions
     * const failed = await sdk.webhooks.getExecutions({ status: 'FAILED' });
     *
     * // Get executions for specific webhook
     * const executions = await sdk.webhooks.getExecutions({
     *   webhookId: 'webhook-123',
     *   dateFrom: '2024-01-01'
     * });
     * ```
     */
    async getExecutions(options) {
        return this.webhookService.getExecutions(options);
    }
    /**
     * Get execution details by ID (Admin)
     */
    async getExecutionById(executionId) {
        return this.webhookService.getExecutionById(executionId);
    }
    /**
     * Get the full webhook service for advanced operations
     *
     * @returns WebhookService instance with full API access
     */
    getWebhookService() {
        return this.webhookService;
    }
}

/**
 * PERS Events Client
 *
 * Lightweight WebSocket client for real-time blockchain event streaming.
 * Connects to the PERS WS Relay server to receive events for user's wallets.
 *
 * ## v1.2.0 Subscription Model
 *
 * JWT is used for authentication only. After connecting, you must explicitly
 * subscribe to wallets (user SDK) or chains (admin dashboard).
 *
 * @example User SDK - Subscribe to specific wallets
 * ```typescript
 * const client = new PersEventsClient({
 *   wsUrl: 'wss://events.pers.ninja',
 *   autoReconnect: true
 * });
 *
 * await client.connect(jwtToken);
 *
 * // Subscribe to user's wallets
 * await client.subscribeWallets([
 *   { address: '0x123...', chainId: 39123 }
 * ]);
 *
 * client.on('Transfer', (event) => {
 *   console.log(`Received ${event.data.value} tokens`);
 * });
 * ```
 *
 * @example Admin Dashboard - Subscribe to all events on chains
 * ```typescript
 * await client.connect(adminJwtToken);
 *
 * // Subscribe to all events on specific chains
 * await client.subscribeChains([39123, 137]);
 *
 * client.on('*', (event) => {
 *   console.log(`Chain ${event.chainId}: ${event.type}`);
 * });
 * ```
 */
const DEFAULT_CONFIG = {
    autoReconnect: true,
    maxReconnectAttempts: 10,
    reconnectDelay: 1000,
    connectionTimeout: 30000,
    debug: false,
    tokenRefresher: undefined,
};
class PersEventsClient {
    constructor(config) {
        this.ws = null;
        this.state = 'disconnected';
        this.reconnectAttempts = 0;
        this.reconnectTimeout = null;
        this.token = null;
        // Event handlers by type
        this.handlers = new Map();
        this.stateHandlers = new Set();
        // Connection info from server
        this.connectionInfo = null;
        // Current subscription state
        this.subscriptionState = { wallets: [], chains: [], activeChains: [] };
        // Pending subscription promise resolver
        this.pendingSubscription = null;
        // Subscriptions to restore on reconnect
        this.savedSubscriptions = { wallets: [], chains: [] };
        this.config = { ...DEFAULT_CONFIG, ...config };
    }
    /**
     * Connect to the WS relay server
     * @param token - JWT token for authentication (wallets no longer required in JWT)
     */
    async connect(token) {
        if (this.state === 'connected' || this.state === 'connecting') {
            this.log('Already connected or connecting');
            return;
        }
        this.token = token;
        this.setState('connecting');
        return new Promise((resolve, reject) => {
            // Connection timeout
            const connectionTimeout = setTimeout(() => {
                this.log('Connection timeout');
                this.cleanup();
                this.setState('error');
                reject(new Error(`Connection timeout after ${this.config.connectionTimeout}ms`));
            }, this.config.connectionTimeout);
            const clearTimeoutAndResolve = () => {
                clearTimeout(connectionTimeout);
                resolve();
            };
            const clearTimeoutAndReject = (err) => {
                clearTimeout(connectionTimeout);
                reject(err);
            };
            try {
                const url = new URL(this.config.wsUrl);
                url.searchParams.set('token', token);
                this.ws = new WebSocket(url.toString());
                this.ws.onopen = () => {
                    // Wait for server 'connected' message
                };
                this.ws.onmessage = (event) => {
                    this.handleMessage(event.data, clearTimeoutAndResolve);
                };
                this.ws.onerror = (error) => {
                    this.log('WebSocket error:', error);
                    this.setState('error');
                    clearTimeoutAndReject(new Error('Connection failed'));
                };
                this.ws.onclose = (event) => {
                    this.log(`WebSocket closed: ${event.code} ${event.reason}`);
                    // Only reject if we were still connecting
                    if (this.state === 'connecting') {
                        clearTimeoutAndReject(new Error(`Connection closed during handshake: ${event.code} ${event.reason}`));
                    }
                    this.handleDisconnect();
                };
            }
            catch (err) {
                this.log('Error creating WebSocket:', err);
                this.setState('error');
                clearTimeoutAndReject(err instanceof Error ? err : new Error(String(err)));
            }
        });
    }
    /**
     * Disconnect from the server
     */
    disconnect() {
        this.config.autoReconnect = false; // Prevent reconnect
        this.cleanup();
        this.setState('disconnected');
    }
    // ─────────────────────────────────────────────────────────────────────────────
    // Subscription Methods (v1.2.0)
    // ─────────────────────────────────────────────────────────────────────────────
    /**
     * Subscribe to wallet events (User SDK)
     *
     * Receives events only for the specified wallet addresses.
     * Can be called multiple times to add more wallets.
     *
     * @param wallets - Array of wallet info (address + chainId)
     * @returns Promise that resolves when subscription is confirmed
     */
    async subscribeWallets(wallets) {
        return this.sendSubscription({ type: 'subscribe', wallets });
    }
    /**
     * Subscribe to chain events (Admin Dashboard)
     *
     * Receives ALL events on the specified chains.
     * Use for admin dashboards that need to monitor all activity.
     *
     * @param chains - Array of chain IDs to subscribe to
     * @returns Promise that resolves when subscription is confirmed
     */
    async subscribeChains(chains) {
        return this.sendSubscription({ type: 'subscribe', chains });
    }
    /**
     * Unsubscribe from wallet events
     *
     * @param wallets - Array of wallet info to unsubscribe from
     * @returns Promise that resolves when unsubscription is confirmed
     */
    async unsubscribeWallets(wallets) {
        return this.sendSubscription({ type: 'unsubscribe', wallets });
    }
    /**
     * Unsubscribe from chain events
     *
     * @param chains - Array of chain IDs to unsubscribe from
     * @returns Promise that resolves when unsubscription is confirmed
     */
    async unsubscribeChains(chains) {
        return this.sendSubscription({ type: 'unsubscribe', chains });
    }
    /**
     * Get current subscription state
     */
    getSubscriptionState() {
        return { ...this.subscriptionState };
    }
    // ─────────────────────────────────────────────────────────────────────────────
    // Event Handlers
    // ─────────────────────────────────────────────────────────────────────────────
    /**
     * Subscribe to blockchain events
     * @param eventType - Event type to listen for, or '*' for all events
     * @param handler - Event handler function
     * @returns Unsubscribe function
     */
    on(eventType, handler) {
        if (!this.handlers.has(eventType)) {
            this.handlers.set(eventType, new Set());
        }
        this.handlers.get(eventType).add(handler);
        return () => {
            this.handlers.get(eventType)?.delete(handler);
        };
    }
    /**
     * Subscribe to connection state changes
     */
    onStateChange(handler) {
        this.stateHandlers.add(handler);
        return () => this.stateHandlers.delete(handler);
    }
    /**
     * Get current connection state
     */
    getState() {
        return this.state;
    }
    /**
     * Get connection info (userId, initial wallets, activeChains)
     */
    getConnectionInfo() {
        return this.connectionInfo;
    }
    // ─────────────────────────────────────────────────────────────────────────────
    // Private methods
    // ─────────────────────────────────────────────────────────────────────────────
    async sendSubscription(message) {
        if (this.state !== 'connected' || !this.ws) {
            throw new Error('Not connected to server');
        }
        // Save subscriptions for reconnect
        if (message.type === 'subscribe') {
            if (message.wallets) {
                this.savedSubscriptions.wallets = [
                    ...this.savedSubscriptions.wallets,
                    ...message.wallets.filter(w => !this.savedSubscriptions.wallets.some(sw => sw.address === w.address && sw.chainId === w.chainId))
                ];
            }
            if (message.chains) {
                this.savedSubscriptions.chains = [
                    ...new Set([...this.savedSubscriptions.chains, ...message.chains])
                ];
            }
        }
        else if (message.type === 'unsubscribe') {
            if (message.wallets) {
                this.savedSubscriptions.wallets = this.savedSubscriptions.wallets.filter(sw => !message.wallets.some(w => w.address === sw.address && w.chainId === sw.chainId));
            }
            if (message.chains) {
                this.savedSubscriptions.chains = this.savedSubscriptions.chains.filter(c => !message.chains.includes(c));
            }
        }
        return new Promise((resolve, reject) => {
            // Set up timeout
            const timeout = setTimeout(() => {
                this.pendingSubscription = null;
                reject(new Error('Subscription timeout'));
            }, 10000);
            this.pendingSubscription = {
                resolve: () => {
                    clearTimeout(timeout);
                    this.pendingSubscription = null;
                    resolve(this.subscriptionState);
                },
                reject: (err) => {
                    clearTimeout(timeout);
                    this.pendingSubscription = null;
                    reject(err);
                }
            };
            this.ws.send(JSON.stringify(message));
            this.log('Sent subscription message:', message);
        });
    }
    handleMessage(data, onConnected) {
        try {
            const message = JSON.parse(data);
            switch (message.type) {
                case 'connected':
                    this.connectionInfo = message.payload;
                    this.reconnectAttempts = 0;
                    this.setState('connected');
                    this.log('Connected:', message.payload);
                    onConnected?.();
                    // Restore subscriptions on reconnect
                    this.restoreSubscriptions();
                    break;
                case 'subscribed':
                    this.subscriptionState = message.payload;
                    this.log('Subscription confirmed:', message.payload);
                    this.pendingSubscription?.resolve();
                    break;
                case 'unsubscribed':
                    this.subscriptionState = message.payload;
                    this.log('Unsubscription confirmed:', message.payload);
                    this.pendingSubscription?.resolve();
                    break;
                case 'event':
                    this.routeEvent(message.payload);
                    break;
                case 'ping':
                    // Respond to server ping with pong
                    this.sendPong();
                    break;
                case 'error':
                    this.log('Server error:', message.payload);
                    this.pendingSubscription?.reject(new Error(message.payload.message));
                    break;
            }
        }
        catch (err) {
            this.log('Failed to parse message:', err);
        }
    }
    sendPong() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'pong' }));
            this.log('Sent pong');
        }
    }
    async restoreSubscriptions() {
        // Restore wallet subscriptions
        if (this.savedSubscriptions.wallets.length > 0) {
            this.log('Restoring wallet subscriptions:', this.savedSubscriptions.wallets);
            try {
                await this.subscribeWallets(this.savedSubscriptions.wallets);
            }
            catch (err) {
                this.log('Failed to restore wallet subscriptions:', err);
            }
        }
        // Restore chain subscriptions
        if (this.savedSubscriptions.chains.length > 0) {
            this.log('Restoring chain subscriptions:', this.savedSubscriptions.chains);
            try {
                await this.subscribeChains(this.savedSubscriptions.chains);
            }
            catch (err) {
                this.log('Failed to restore chain subscriptions:', err);
            }
        }
    }
    routeEvent(event) {
        this.log('Event received:', event.type, event);
        // Call specific type handlers
        const typeHandlers = this.handlers.get(event.type);
        typeHandlers?.forEach(handler => {
            try {
                handler(event);
            }
            catch (err) {
                console.error('[PERS-Events] Handler error:', err);
            }
        });
        // Call wildcard handlers
        const wildcardHandlers = this.handlers.get('*');
        wildcardHandlers?.forEach(handler => {
            try {
                handler(event);
            }
            catch (err) {
                console.error('[PERS-Events] Handler error:', err);
            }
        });
    }
    handleDisconnect() {
        this.cleanup();
        this.setState('disconnected');
        if (this.config.autoReconnect && this.token) {
            if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
                this.attemptReconnect();
            }
            else {
                this.log(`Max reconnect attempts (${this.config.maxReconnectAttempts}) reached. Call connect() manually to retry.`);
                // Reset counter so manual connect() will work
                this.reconnectAttempts = 0;
            }
        }
    }
    attemptReconnect() {
        const delay = Math.min(this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000 // Max 30 second delay
        );
        this.reconnectAttempts++;
        this.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);
        this.setState('reconnecting');
        this.reconnectTimeout = setTimeout(async () => {
            try {
                // Try to get fresh token if tokenRefresher is provided
                let tokenToUse = this.token;
                if (this.config.tokenRefresher) {
                    try {
                        this.log('🔄 Refreshing token before reconnect...');
                        tokenToUse = await this.config.tokenRefresher();
                        this.token = tokenToUse; // Update stored token
                    }
                    catch (refreshErr) {
                        this.log('⚠️ Token refresh failed, using existing token:', refreshErr);
                    }
                }
                if (tokenToUse) {
                    this.log(`🔄 Attempting reconnection...`);
                    await this.connect(tokenToUse);
                }
            }
            catch (err) {
                this.log('❌ Reconnect failed:', err);
            }
        }, delay);
    }
    cleanup() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.ws) {
            this.ws.onopen = null;
            this.ws.onmessage = null;
            this.ws.onerror = null;
            this.ws.onclose = null;
            if (this.ws.readyState === WebSocket.OPEN) {
                this.ws.close();
            }
            this.ws = null;
        }
    }
    setState(state) {
        if (this.state !== state) {
            this.state = state;
            this.stateHandlers.forEach(handler => handler(state));
        }
    }
    log(...args) {
        if (this.config.debug) {
            console.log('[PERS-Events]', ...args);
        }
    }
}
/**
 * Create a PERS Events client instance
 */
function createPersEventsClient(config) {
    return new PersEventsClient(config);
}

/**
 * Wallet Events Manager - Real-time blockchain events for user's wallets
 *
 * Provides automatic connection management and event routing integrated
 * with the SDK's authentication flow and event system.
 *
 * Events are routed through the SDK's PersEventEmitter, so you can either:
 * 1. Subscribe via `sdk.walletEvents.on()` for wallet-specific handling
 * 2. Subscribe via `sdk.events.subscribe()` for unified event stream
 *
 * ## v1.2.0 - Subscription Model
 *
 * After connecting, you must explicitly subscribe to wallets or chains:
 * - User SDK: `subscribeWallets([{ address, chainId }])`
 * - Admin Dashboard: `subscribeChains([chainId1, chainId2])`
 *
 * For convenience, use `sdk.connectWalletEvents()` which auto-subscribes
 * based on auth type.
 *
 * @example Manual subscription
 * ```typescript
 * await sdk.walletEvents.connect();
 * await sdk.walletEvents.subscribeWallets([
 *   { address: user.wallets[0].address, chainId: 39123 }
 * ]);
 *
 * sdk.walletEvents.on('Transfer', (event) => {
 *   console.log(`Received ${event.data.value} tokens`);
 * });
 * ```
 *
 * @example Auto-subscription (recommended)
 * ```typescript
 * // Auto-subscribes based on auth type (user/business/admin)
 * await sdk.connectWalletEvents();
 * ```
 */
/**
 * Wallet Events Manager - Manages real-time blockchain event subscriptions
 *
 * Low-level WS client wrapper. For auto-subscription based on auth type,
 * use `sdk.connectWalletEvents()` which handles subscription automatically.
 */
class WalletEventsManager {
    constructor(apiClient, eventEmitter, config) {
        this.apiClient = apiClient;
        this.eventEmitter = eventEmitter;
        this.client = null;
        this.pendingHandlers = [];
        this.unsubscribes = [];
        // Track if WS gave up reconnecting (so we can retry on token refresh)
        this.reconnectGaveUp = false;
        this.lastSubscriptions = { wallets: [], chains: [] };
        this.config = {
            autoReconnect: true,
            connectionTimeout: 30000,
            debug: false,
            ...config,
        };
        // Subscribe to auth events to retry WS connection when tokens are refreshed
        this.authEventUnsubscribe = this.eventEmitter.subscribe((event) => {
            if (event.type === 'token_refreshed' && this.reconnectGaveUp) {
                // Tokens were refreshed and WS had given up - retry connection
                this.retryAfterTokenRefresh();
            }
        }, { domains: ['authentication'], levels: ['success'] });
    }
    /**
     * Connect to real-time wallet events
     *
     * Establishes WebSocket connection to the WS relay server.
     * After connecting, call subscribeWallets() or subscribeChains() to start
     * receiving events.
     *
     * For auto-subscription, use `sdk.connectWalletEvents()` instead.
     */
    async connect() {
        // Already connected?
        if (this.client?.getState() === 'connected') {
            return;
        }
        const sdkConfig = this.apiClient.getConfig();
        const authProvider = sdkConfig.authProvider;
        if (!authProvider) {
            throw new Error('Not authenticated. Call sdk.auth.login() first.');
        }
        const token = await authProvider.getToken();
        if (!token) {
            throw new Error('No authentication token available. Call sdk.auth.login() first.');
        }
        // Resolve wsUrl: config override > SDK config > environment default
        const wsUrl = this.config.wsUrl
            || sdkConfig.walletEventsWsUrl
            || buildWalletEventsWsUrl(sdkConfig.environment);
        // Create token refresher that ensures valid token before reconnecting
        // This triggers actual token refresh if expired, not just retrieval
        const tokenRefresher = async () => {
            // Ensure token is refreshed if expired (calls refresh API if needed)
            await this.apiClient.ensureValidToken();
            const freshToken = await authProvider.getToken();
            if (!freshToken) {
                throw new Error('Failed to refresh token');
            }
            return freshToken;
        };
        this.client = new PersEventsClient({
            wsUrl,
            autoReconnect: this.config.autoReconnect,
            connectionTimeout: this.config.connectionTimeout,
            debug: this.config.debug,
            tokenRefresher,
        });
        await this.client.connect(token);
        // Reset gave-up flag on successful connection
        this.reconnectGaveUp = false;
        // Track previous state to detect reconnections and give-ups
        let previousState = 'connected';
        // Clean up previous state change listener (prevent duplicate listeners)
        this.stateChangeUnsubscribe?.();
        // Listen for state changes
        this.stateChangeUnsubscribe = this.client.onStateChange((state) => {
            if (state === 'disconnected' && previousState === 'reconnecting') {
                // Transitioned from reconnecting to disconnected = exhausted all attempts
                this.reconnectGaveUp = true;
            }
            else if (state === 'connected') {
                this.reconnectGaveUp = false;
                // Emit reconnected event so apps can refresh state (catch missed events)
                if (previousState === 'reconnecting') {
                    this.eventEmitter.emitSuccess({
                        type: 'wallet_reconnected',
                        domain: 'wallet',
                        userMessage: 'Wallet events reconnected',
                        details: { previousState }
                    });
                }
            }
            previousState = state;
        });
        // Clear previous unsubscribes on new connection (prevent memory leak)
        this.unsubscribes.forEach(unsub => unsub());
        this.unsubscribes = [];
        // Route all events through PersEventEmitter for unified stream
        // Note: Cast needed because web3-types uses string for event.type while pers-shared uses strict union
        this.client.on('*', (event) => this.emitToPersEvents(event));
        // Re-attach any handlers registered before connect
        for (const { type, handler } of this.pendingHandlers) {
            this.unsubscribes.push(this.client.on(type, handler));
        }
        this.pendingHandlers = [];
    }
    /**
     * Disconnect from real-time events
     */
    disconnect() {
        // Clear gave-up flag to prevent auto-reconnect after intentional disconnect
        this.reconnectGaveUp = false;
        this.stateChangeUnsubscribe?.();
        this.stateChangeUnsubscribe = undefined;
        this.unsubscribes.forEach(unsub => unsub());
        this.unsubscribes = [];
        this.client?.disconnect();
        this.client = null;
    }
    /**
     * Full cleanup - disconnect and remove all subscriptions including auth listener
     * Call this when completely done with the manager (e.g., SDK disposal)
     */
    destroy() {
        this.disconnect();
        this.authEventUnsubscribe?.();
        this.authEventUnsubscribe = undefined;
        this.reconnectGaveUp = false;
        this.lastSubscriptions = { wallets: [], chains: [] };
    }
    // ─────────────────────────────────────────────────────────────────────────────
    // Subscription Methods (v1.2.0)
    // ─────────────────────────────────────────────────────────────────────────────
    /**
     * Subscribe to wallet events
     *
     * Receives events only for the specified wallet addresses.
     * Use this for user-facing SDK where you want to monitor specific wallets.
     *
     * @param wallets - Array of wallet info (address + chainId)
     * @returns Promise that resolves when subscription is confirmed
     *
     * @example
     * ```typescript
     * await sdk.walletEvents.connect();
     * await sdk.walletEvents.subscribeWallets([
     *   { address: '0x123...', chainId: 39123 }
     * ]);
     * ```
     */
    async subscribeWallets(wallets) {
        if (!this.client) {
            throw new Error('Not connected. Call connect() first.');
        }
        // Save subscriptions for auto-resubscribe on reconnect
        this.lastSubscriptions.wallets = wallets;
        const result = await this.client.subscribeWallets(wallets);
        return result;
    }
    /**
     * Subscribe to chain events (Admin Dashboard)
     *
     * Receives ALL events on the specified chains.
     * Use for admin dashboards that need to monitor all activity.
     *
     * @param chains - Array of chain IDs to subscribe to
     * @returns Promise that resolves when subscription is confirmed
     *
     * @example
     * ```typescript
     * await sdk.walletEvents.connect();
     * await sdk.walletEvents.subscribeChains([39123, 137]);
     * ```
     */
    async subscribeChains(chains) {
        if (!this.client) {
            throw new Error('Not connected. Call connect() first.');
        }
        // Save subscriptions for auto-resubscribe on reconnect
        this.lastSubscriptions.chains = chains;
        const result = await this.client.subscribeChains(chains);
        return result;
    }
    /**
     * Unsubscribe from wallet events
     *
     * @param wallets - Array of wallet info to unsubscribe from
     * @returns Promise that resolves when unsubscription is confirmed
     */
    async unsubscribeWallets(wallets) {
        if (!this.client) {
            throw new Error('Not connected. Call connect() first.');
        }
        return this.client.unsubscribeWallets(wallets);
    }
    /**
     * Unsubscribe from chain events
     *
     * @param chains - Array of chain IDs to unsubscribe from
     * @returns Promise that resolves when unsubscription is confirmed
     */
    async unsubscribeChains(chains) {
        if (!this.client) {
            throw new Error('Not connected. Call connect() first.');
        }
        return this.client.unsubscribeChains(chains);
    }
    /**
     * Get current subscription state
     */
    getSubscriptionState() {
        return this.client?.getSubscriptionState() ?? { wallets: [], chains: [], activeChains: [] };
    }
    // ─────────────────────────────────────────────────────────────────────────────
    // Event Handlers
    // ─────────────────────────────────────────────────────────────────────────────
    /**
     * Subscribe to blockchain events
     *
     * @param eventType - Event type ('Transfer', 'Approval', etc.) or '*' for all
     * @param handler - Callback function when event is received
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsub = sdk.walletEvents.on('Transfer', (event) => {
     *   console.log(`Transfer: ${event.data.from} -> ${event.data.to}`);
     * });
     *
     * // Later: stop listening
     * unsub();
     * ```
     */
    on(eventType, handler) {
        if (this.client) {
            const unsub = this.client.on(eventType, handler);
            this.unsubscribes.push(unsub);
            return unsub;
        }
        else {
            // Store for when connect() is called
            this.pendingHandlers.push({ type: eventType, handler });
            return () => {
                const idx = this.pendingHandlers.findIndex(h => h.handler === handler);
                if (idx !== -1)
                    this.pendingHandlers.splice(idx, 1);
            };
        }
    }
    /**
     * Subscribe to connection state changes
     */
    onStateChange(handler) {
        if (this.client) {
            return this.client.onStateChange(handler);
        }
        return () => { };
    }
    /**
     * Get current connection state
     */
    getState() {
        return this.client?.getState() ?? 'disconnected';
    }
    /**
     * Check if connected
     */
    isConnected() {
        return this.getState() === 'connected';
    }
    /**
     * Check if WS reconnection gave up (exhausted all attempts)
     * Used to determine if we should retry on token refresh
     */
    hasReconnectGivenUp() {
        return this.reconnectGaveUp;
    }
    /**
     * Retry connection after token refresh
     * Called when auth tokens are refreshed and WS had previously given up
     * Automatically resubscribes to previous subscriptions
     */
    async retryAfterTokenRefresh() {
        if (!this.reconnectGaveUp) {
            return; // Not in gave-up state, nothing to retry
        }
        try {
            await this.connect();
            // Restore previous subscriptions
            if (this.lastSubscriptions.wallets.length > 0) {
                await this.subscribeWallets(this.lastSubscriptions.wallets);
            }
            if (this.lastSubscriptions.chains.length > 0) {
                await this.subscribeChains(this.lastSubscriptions.chains);
            }
        }
        catch (error) {
            console.warn('[WalletEventsManager] Retry after token refresh failed:', error);
        }
    }
    /**
     * Get connection info (wallets, active chains)
     */
    getConnectionInfo() {
        return this.client?.getConnectionInfo();
    }
    // ─────────────────────────────────────────────────────────────────────────────
    // Private Methods
    // ─────────────────────────────────────────────────────────────────────────────
    /**
     * Build a descriptive message for blockchain events
     * Neutral tone - frontend will handle user-facing presentation
     */
    buildUserMessage(event) {
        const { type, data } = event;
        // Use string for switch since event types may extend beyond strict BlockchainEventType union
        const eventType = type;
        const from = data.from?.toLowerCase();
        const to = data.to?.toLowerCase();
        const shortFrom = from ? `${from.slice(0, 6)}...${from.slice(-4)}` : 'unknown';
        const shortTo = to ? `${to.slice(0, 6)}...${to.slice(-4)}` : 'unknown';
        // Get user's wallets to determine direction
        const subscriptionState = this.getSubscriptionState();
        const userWallets = subscriptionState.wallets.map(w => w.address.toLowerCase());
        const isIncoming = to && userWallets.includes(to);
        const isOutgoing = from && userWallets.includes(from);
        const isMint = from === '0x0000000000000000000000000000000000000000';
        const isBurn = to === '0x0000000000000000000000000000000000000000';
        switch (eventType) {
            case 'Transfer':
                if (isMint) {
                    return `Token minted to ${shortTo}`;
                }
                else if (isBurn) {
                    return `Token burned from ${shortFrom}`;
                }
                else if (isIncoming) {
                    return `Transfer received from ${shortFrom}`;
                }
                else if (isOutgoing) {
                    return `Transfer sent to ${shortTo}`;
                }
                return `Transfer from ${shortFrom} to ${shortTo}`;
            case 'Approval':
                return `Approval from ${shortFrom}`;
            case 'SmartWalletCreated':
                return `Smart wallet created: ${shortTo}`;
            case 'TransactionExecuted':
                return `Transaction executed by ${shortFrom}`;
            case 'OwnershipTransferred':
                return `Ownership transferred from ${shortFrom} to ${shortTo}`;
            default:
                return `${eventType} event`;
        }
    }
    /**
     * Route blockchain event to PersEventEmitter for unified event stream
     */
    emitToPersEvents(event) {
        const userMessage = this.buildUserMessage(event);
        this.eventEmitter.emitSuccess({
            type: `wallet_${event.type.toLowerCase()}`,
            domain: 'wallet',
            userMessage,
            details: {
                chainId: event.chainId,
                txHash: event.txHash,
                blockNumber: event.blockNumber,
                timestamp: event.timestamp,
                contractAddress: event.contractAddress,
                ...event.data,
            },
        });
    }
}

/**
 * Custom Field Definitions API
 *
 * Low-level API for custom field definition CRUD operations.
 * Used internally by CustomFieldDefinitionManager.
 *
 * @internal
 */
class CustomFieldApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/custom-field-definitions';
    }
    /**
     * List all custom field definitions
     * Backend returns non-paginated array sorted by sortOrder
     */
    async getDefinitions(entityType) {
        const params = new URLSearchParams();
        if (entityType) {
            params.append('entityType', entityType);
        }
        const query = params.toString();
        const url = query ? `${this.basePath}?${query}` : this.basePath;
        return this.apiClient.get(url);
    }
    /**
     * Get a single custom field definition by ID
     */
    async getDefinition(id) {
        return this.apiClient.get(`${this.basePath}/${id}`);
    }
    /**
     * Create a new custom field definition
     */
    async createDefinition(data) {
        return this.apiClient.post(this.basePath, data);
    }
    /**
     * Update an existing custom field definition
     */
    async updateDefinition(id, data) {
        return this.apiClient.put(`${this.basePath}/${id}`, data);
    }
    /**
     * Delete a custom field definition
     */
    async deleteDefinition(id) {
        return this.apiClient.delete(`${this.basePath}/${id}`);
    }
    /**
     * Get usage count for a field definition
     */
    async getUsages(id) {
        return this.apiClient.get(`${this.basePath}/${id}/usages`);
    }
    /**
     * Resolve dynamic select options for a field
     */
    async resolveSelectOptions(id) {
        return this.apiClient.get(`${this.basePath}/${id}/options`);
    }
}

/**
 * Custom Field Definition Manager - Manage tenant-specific custom fields
 *
 * Provides CRUD operations for custom field definitions that extend the built-in
 * user profile fields. Custom fields are defined per-tenant and can be used for
 * additional user data collection, redemption requirements, and form validation.
 *
 * @group Managers
 * @category Custom Fields
 *
 * @example Basic Operations
 * ```typescript
 * // List all user custom fields
 * const fields = await sdk.customFields.getDefinitions();
 *
 * // Create a new custom field
 * const field = await sdk.customFields.createDefinition({
 *   key: 'employee_id',
 *   label: 'Employee ID',
 *   fieldType: 'text',
 *   validation: { required: true, pattern: '^E[0-9]{5}$' }
 * });
 *
 * // Update a field
 * await sdk.customFields.updateDefinition(field.id, {
 *   label: 'Company Employee ID'
 * });
 *
 * // Delete a field
 * await sdk.customFields.deleteDefinition(field.id);
 * ```
 *
 * @example Validation
 * ```typescript
 * // Validate user custom data against definitions
 * const definitions = await sdk.customFields.getDefinitions();
 * const errors = sdk.customFields.validateUserData(
 *   { employee_id: 'INVALID' },
 *   definitions
 * );
 * if (errors.length > 0) {
 *   console.log('Validation errors:', errors);
 * }
 * ```
 */
class CustomFieldDefinitionManager {
    constructor(apiClient, events) {
        this.events = events;
        this.api = new CustomFieldApi(apiClient);
    }
    /**
     * List all custom field definitions for the tenant
     *
     * Retrieves all custom field definitions, optionally filtered by entity type.
     * Results are sorted by sortOrder (ascending).
     *
     * @param options - Query options including entity type filter
     * @returns Array of custom field definitions
     *
     * @example List All User Fields
     * ```typescript
     * const fields = await sdk.customFields.getDefinitions();
     * console.log(`Found ${fields.length} custom fields`);
     *
     * fields.forEach(field => {
     *   console.log(`${field.key}: ${field.label} (${field.fieldType})`);
     * });
     * ```
     *
     * @example Filter by Entity Type
     * ```typescript
     * // Get only business custom fields
     * const businessFields = await sdk.customFields.getDefinitions({
     *   entityType: 'business'
     * });
     * ```
     */
    async getDefinitions(options) {
        return this.api.getDefinitions(options?.entityType);
    }
    /**
     * Get a single custom field definition by ID
     *
     * @param id - The UUID of the custom field definition
     * @returns The custom field definition
     * @throws {PersApiError} When definition not found (404)
     *
     * @example
     * ```typescript
     * try {
     *   const field = await sdk.customFields.getDefinition('uuid-here');
     *   console.log('Field:', field.label);
     * } catch (error) {
     *   if (error.statusCode === 404) {
     *     console.log('Field not found');
     *   }
     * }
     * ```
     */
    async getDefinition(id) {
        return this.api.getDefinition(id);
    }
    /**
     * Create a new custom field definition
     *
     * Creates a new custom field for the tenant. The field key must be unique
     * within the tenant and entity type combination.
     *
     * @param data - The field definition data
     * @returns The created custom field definition
     * @throws {PersApiError} When key already exists (409) or validation fails (400)
     *
     * @example Text Field with Pattern
     * ```typescript
     * const employeeIdField = await sdk.customFields.createDefinition({
     *   key: 'employee_id',
     *   label: 'Employee ID',
     *   description: 'Your company employee ID (E + 5 digits)',
     *   fieldType: 'text',
     *   validation: {
     *     required: true,
     *     pattern: '^E[0-9]{5}$',
     *     patternMessage: 'Must be E followed by 5 digits'
     *   },
     *   sortOrder: 1
     * });
     * ```
     *
     * @example Date Field with Comparison
     * ```typescript
     * const checkOutField = await sdk.customFields.createDefinition({
     *   key: 'check_out',
     *   label: 'Check-out Date',
     *   fieldType: 'date',
     *   validation: {
     *     required: true,
     *     comparisons: [
     *       { field: 'check_in', operator: '>', message: 'Must be after check-in' }
     *     ]
     *   },
     *   sortOrder: 2
     * });
     * ```
     *
     * @example Select Field with Static Options
     * ```typescript
     * const departmentField = await sdk.customFields.createDefinition({
     *   key: 'department',
     *   label: 'Department',
     *   fieldType: 'select',
     *   selectOptions: [
     *     { value: 'engineering', label: 'Engineering' },
     *     { value: 'sales', label: 'Sales' },
     *     { value: 'hr', label: 'Human Resources' }
     *   ],
     *   validation: { required: true }
     * });
     * ```
     *
     * @example Select Field with Dynamic Options
     * ```typescript
     * const hotelField = await sdk.customFields.createDefinition({
     *   key: 'preferred_hotel',
     *   label: 'Preferred Hotel',
     *   fieldType: 'select',
     *   selectOptionsSource: {
     *     entity: 'business',
     *     valueField: 'id',
     *     labelField: 'name',
     *     filter: { tags: ['hotel'], isActive: true }
     *   },
     *   validation: { required: true }
     * });
     * ```
     */
    async createDefinition(data) {
        const result = await this.api.createDefinition(data);
        // Using 'user' domain until pers-shared adds 'customField' domain
        this.events?.emitSuccess({
            domain: 'user',
            type: 'definition_created',
            userMessage: `Custom field "${result.label}" created`,
            details: { id: result.id, key: result.key }
        });
        return result;
    }
    /**
     * Update an existing custom field definition
     *
     * Updates a custom field definition. Note that key and entityType cannot
     * be changed after creation.
     *
     * @param id - The UUID of the custom field definition to update
     * @param data - The fields to update (partial update supported)
     * @returns The updated custom field definition
     * @throws {PersApiError} When definition not found (404) or validation fails (400)
     *
     * @example Update Label and Validation
     * ```typescript
     * const updated = await sdk.customFields.updateDefinition('uuid-here', {
     *   label: 'Employee ID (Required)',
     *   validation: {
     *     required: true,
     *     minLength: 6,
     *     maxLength: 6
     *   }
     * });
     * ```
     *
     * @example Update Sort Order
     * ```typescript
     * await sdk.customFields.updateDefinition('uuid-here', {
     *   sortOrder: 5
     * });
     * ```
     */
    async updateDefinition(id, data) {
        const result = await this.api.updateDefinition(id, data);
        // Using 'user' domain until pers-shared adds 'customField' domain
        this.events?.emitSuccess({
            domain: 'user',
            type: 'definition_updated',
            userMessage: `Custom field "${result.label}" updated`,
            details: { id: result.id, key: result.key }
        });
        return result;
    }
    /**
     * Delete a custom field definition (soft delete)
     *
     * Soft deletes a custom field definition. Existing user data in customData
     * is preserved, but the field will no longer appear in forms or validation.
     *
     * ⚠️ Consider the impact on existing user data before deleting.
     *
     * @param id - The UUID of the custom field definition to delete
     * @throws {PersApiError} When definition not found (404)
     *
     * @example
     * ```typescript
     * try {
     *   await sdk.customFields.deleteDefinition('uuid-here');
     *   console.log('Field deleted');
     * } catch (error) {
     *   console.log('Failed to delete:', error.message);
     * }
     * ```
     */
    async deleteDefinition(id) {
        await this.api.deleteDefinition(id);
        // Using 'user' domain until pers-shared adds 'customField' domain
        this.events?.emitSuccess({
            domain: 'user',
            type: 'definition_deleted',
            userMessage: 'Custom field deleted',
            details: { id }
        });
    }
    /**
     * Resolve dynamic select options for a field
     *
     * For fields with selectOptionsSource (dynamic options from entities),
     * this resolves the actual options by querying the source entity.
     *
     * @param id - The UUID of the custom field definition
     * @returns Array of select options
     *
     * @example
     * ```typescript
     * const field = await sdk.customFields.getDefinition('hotel-field-id');
     *
     * if (field.selectOptionsSource) {
     *   // Options come from entity - need to resolve
     *   const options = await sdk.customFields.resolveSelectOptions(field.id);
     *   console.log('Available hotels:', options);
     * } else {
     *   // Static options
     *   console.log('Options:', field.selectOptions);
     * }
     * ```
     */
    async resolveSelectOptions(id) {
        const response = await this.api.resolveSelectOptions(id);
        return response.options;
    }
    /**
     * Validate user custom data against field definitions
     *
     * Client-side validation using the same rules as the backend. Use this
     * to validate form data before submission.
     *
     * @param data - User's custom data (key-value pairs)
     * @param definitions - Custom field definitions to validate against
     * @returns Array of validation errors (empty if valid)
     *
     * @example Form Validation
     * ```typescript
     * const definitions = await sdk.customFields.getDefinitions();
     * const formData = {
     *   employee_id: 'E123', // Missing a digit
     *   department: 'engineering'
     * };
     *
     * const errors = sdk.customFields.validateUserData(formData, definitions);
     * if (errors.length > 0) {
     *   errors.forEach(err => {
     *     console.log(`${err.field}: ${err.message}`);
     *   });
     *   return; // Don't submit
     * }
     *
     * // No errors, safe to submit
     * await sdk.users.updateCurrentUser({ customData: formData });
     * ```
     */
    validateUserData(data, definitions) {
        // Convert CustomFieldDefinitionDTO to FieldDefinition for validation
        const fieldDefinitions = definitions.map(def => ({
            key: def.key,
            fieldType: def.fieldType,
            label: def.label,
            validation: def.validation ?? undefined,
        }));
        return validateAllFields(data, fieldDefinitions);
    }
    /**
     * Validate a single field value
     *
     * Validates a single value against a field's rules. Useful for
     * real-time validation as the user types.
     *
     * @param value - The value to validate
     * @param definition - The field definition
     * @returns Validation error or null if valid
     *
     * @example Real-time Validation
     * ```typescript
     * const employeeIdField = definitions.find(d => d.key === 'employee_id');
     *
     * const handleBlur = (value: string) => {
     *   const error = sdk.customFields.validateFieldValue(value, employeeIdField);
     *   if (error) {
     *     setFieldError('employee_id', error.message);
     *   } else {
     *     clearFieldError('employee_id');
     *   }
     * };
     * ```
     */
    validateFieldValue(value, definition) {
        const error = validateFieldValue(value, definition.validation ?? null, definition.fieldType);
        if (error) {
            // Add field key to error
            return { ...error, field: definition.key };
        }
        return null;
    }
}

/**
 * Low-level API for booking operations
 *
 * Provides direct HTTP calls to the booking endpoints.
 * For most use cases, prefer using BookingManager or BookingService.
 *
 * @internal
 */
class BookingApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/bookings';
    }
    /**
     * List all bookings with optional filters
     *
     * @param options - Filter options (userId, businessId, status)
     * @returns Promise resolving to array of bookings
     */
    async getAll(options) {
        const params = new URLSearchParams();
        if (options?.userId) {
            params.append('userId', options.userId);
        }
        if (options?.businessId) {
            params.append('businessId', options.businessId);
        }
        if (options?.status) {
            params.append('status', options.status);
        }
        const queryString = params.toString();
        const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
        return this.apiClient.get(url);
    }
    /**
     * Get a single booking by ID
     *
     * @param id - Booking UUID
     * @returns Promise resolving to booking data
     */
    async getById(id) {
        return this.apiClient.get(`${this.basePath}/${id}`);
    }
    /**
     * Create a new booking
     *
     * @param data - Booking creation data
     * @returns Promise resolving to created booking
     */
    async create(data) {
        return this.apiClient.post(this.basePath, data);
    }
    /**
     * Update an existing booking
     *
     * @param id - Booking UUID
     * @param data - Booking update data
     * @returns Promise resolving to updated booking
     */
    async update(id, data) {
        return this.apiClient.put(`${this.basePath}/${id}`, data);
    }
    /**
     * Delete a booking
     *
     * @param id - Booking UUID
     * @returns Promise resolving to success status
     */
    async delete(id) {
        return this.apiClient.delete(`${this.basePath}/${id}`);
    }
    // ==========================================
    // CSV EXPORT
    // ==========================================
    /**
     * ADMIN: Export bookings to CSV
     *
     * Returns a CSV file with booking data for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo in ISO format)
     * @returns CSV blob
     */
    async exportCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `${this.basePath}/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
}

/**
 * Booking Service - Business logic layer for booking operations
 *
 * Provides booking management functionality with validation and
 * business rule enforcement.
 *
 * @internal
 */
class BookingService {
    constructor(bookingApi) {
        this.bookingApi = bookingApi;
    }
    /**
     * Get all bookings with optional filters
     *
     * @param options - Filter options
     * @returns Promise resolving to array of bookings
     */
    async getAll(options) {
        return this.bookingApi.getAll(options);
    }
    /**
     * Get bookings for a specific user
     *
     * @param userId - User ID to filter by
     * @param status - Optional status filter
     * @returns Promise resolving to user's bookings
     */
    async getByUser(userId, status) {
        return this.bookingApi.getAll({ userId, status });
    }
    /**
     * Get bookings for a specific business
     *
     * @param businessId - Business ID to filter by
     * @param status - Optional status filter
     * @returns Promise resolving to business's bookings
     */
    async getByBusiness(businessId, status) {
        return this.bookingApi.getAll({ businessId, status });
    }
    /**
     * Get a single booking by ID
     *
     * @param id - Booking UUID
     * @returns Promise resolving to booking data
     */
    async getById(id) {
        return this.bookingApi.getById(id);
    }
    /**
     * Create a new booking
     *
     * @param data - Booking creation data
     * @returns Promise resolving to created booking
     */
    async create(data) {
        // Validate check-out is after check-in
        if (data.checkInDate && data.checkOutDate) {
            const checkIn = new Date(data.checkInDate);
            const checkOut = new Date(data.checkOutDate);
            if (checkOut <= checkIn) {
                throw new Error('Check-out date must be after check-in date');
            }
        }
        return this.bookingApi.create(data);
    }
    /**
     * Update an existing booking
     *
     * @param id - Booking UUID
     * @param data - Booking update data
     * @returns Promise resolving to updated booking
     */
    async update(id, data) {
        // Validate dates if both provided
        if (data.checkInDate && data.checkOutDate) {
            const checkIn = new Date(data.checkInDate);
            const checkOut = new Date(data.checkOutDate);
            if (checkOut <= checkIn) {
                throw new Error('Check-out date must be after check-in date');
            }
        }
        return this.bookingApi.update(id, data);
    }
    /**
     * Delete a booking
     *
     * @param id - Booking UUID
     * @returns Promise resolving to success status
     */
    async delete(id) {
        return this.bookingApi.delete(id);
    }
    /**
     * Check if a user has an active or future booking
     *
     * Useful for eligibility checks on redemptions.
     *
     * @param userId - User ID to check
     * @param options - Validation options (status filter, business filter)
     * @returns Promise resolving to true if user has valid booking
     */
    async hasValidBooking(userId, options) {
        const queryOptions = {
            userId,
            businessId: options?.businessId
        };
        // If a specific status is requested, use it
        if (options?.status && options.status !== 'active_future') {
            queryOptions.status = options.status === 'any' ? undefined : options.status;
            const bookings = await this.bookingApi.getAll(queryOptions);
            return bookings.length > 0;
        }
        // Default: check active+future (active_future or undefined)
        const activeBookings = await this.bookingApi.getAll({ ...queryOptions, status: 'active' });
        if (activeBookings.length > 0)
            return true;
        const futureBookings = await this.bookingApi.getAll({ ...queryOptions, status: 'future' });
        return futureBookings.length > 0;
    }
}

/**
 * Booking Manager - High-level interface for booking operations
 *
 * Provides a simplified API for managing bookings (hotel stays, reservations, etc.)
 * Used for tracking user stays and eligibility checks on redemptions.
 *
 * @group Managers
 * @category Booking Management
 *
 * @example List Bookings
 * ```typescript
 * // Get all bookings
 * const allBookings = await sdk.bookings.getAll();
 *
 * // Filter by user
 * const userBookings = await sdk.bookings.getAll({ userId: 'user-123' });
 *
 * // Filter by status
 * const activeBookings = await sdk.bookings.getAll({ status: 'active' });
 * ```
 *
 * @example Create Booking
 * ```typescript
 * const booking = await sdk.bookings.create({
 *   userId: 'user-123',
 *   locationName: 'Grand Hotel',
 *   checkInDate: '2026-06-01',
 *   checkOutDate: '2026-06-05',
 *   guests: 2
 * });
 * ```
 *
 * @example Check Eligibility
 * ```typescript
 * // Check if user has valid (active or future) booking
 * const hasBooking = await sdk.bookings.hasValidBooking('user-123');
 * if (hasBooking) {
 *   // User is eligible for booking-required redemption
 * }
 * ```
 */
class BookingManager {
    constructor(apiClient, events) {
        this.apiClient = apiClient;
        this.events = events;
        const bookingApi = new BookingApi(apiClient);
        this.bookingService = new BookingService(bookingApi);
    }
    /**
     * Get all bookings with optional filters
     *
     * @param options - Filter options (userId, businessId, status)
     * @returns Promise resolving to array of bookings
     *
     * @example Basic Usage
     * ```typescript
     * // All bookings
     * const bookings = await sdk.bookings.getAll();
     *
     * // Filter by user
     * const userBookings = await sdk.bookings.getAll({ userId: 'user-123' });
     *
     * // Filter by status
     * const activeBookings = await sdk.bookings.getAll({ status: 'active' });
     * const futureBookings = await sdk.bookings.getAll({ status: 'future' });
     * const pastBookings = await sdk.bookings.getAll({ status: 'past' });
     * ```
     */
    async getAll(options) {
        return this.bookingService.getAll(options);
    }
    /**
     * Get bookings for a specific user
     *
     * Convenience method for filtering bookings by user ID.
     *
     * @param userId - User ID to filter by
     * @param status - Optional status filter
     * @returns Promise resolving to user's bookings
     */
    async getByUser(userId, status) {
        return this.bookingService.getByUser(userId, status);
    }
    /**
     * Get bookings for a specific business
     *
     * Convenience method for filtering bookings by business ID.
     *
     * @param businessId - Business ID to filter by
     * @param status - Optional status filter
     * @returns Promise resolving to business's bookings
     */
    async getByBusiness(businessId, status) {
        return this.bookingService.getByBusiness(businessId, status);
    }
    /**
     * Get a single booking by ID
     *
     * @param id - Booking UUID
     * @returns Promise resolving to booking data
     * @throws {PersApiError} When booking not found
     */
    async getById(id) {
        return this.bookingService.getById(id);
    }
    /**
     * Create a new booking
     *
     * Creates a booking for a user at a specific location. Used for tracking
     * hotel stays, reservations, etc. for eligibility checks.
     *
     * @param data - Booking creation data
     * @returns Promise resolving to created booking
     * @throws {Error} When check-out date is not after check-in date
     *
     * @example Create Hotel Booking
     * ```typescript
     * const booking = await sdk.bookings.create({
     *   userId: 'user-123',
     *   locationName: 'Grand Hotel',
     *   locationType: 'hotel',
     *   checkInDate: '2026-06-01',
     *   checkOutDate: '2026-06-05',
     *   confirmationNumber: 'CONF123',
     *   guests: 2,
     *   notes: 'Ocean view room'
     * });
     * ```
     */
    async create(data) {
        const result = await this.bookingService.create(data);
        // TODO: Add event emission when 'booking' domain is added to pers-shared
        // this.events?.emitSuccess({ domain: 'booking', type: 'booking_created', ... });
        return result;
    }
    /**
     * Update an existing booking
     *
     * @param id - Booking UUID
     * @param data - Booking update data (partial update supported)
     * @returns Promise resolving to updated booking
     * @throws {PersApiError} When booking not found
     * @throws {Error} When check-out date is not after check-in date
     *
     * @example Update Booking Dates
     * ```typescript
     * const updated = await sdk.bookings.update('booking-123', {
     *   checkInDate: '2026-06-02',
     *   checkOutDate: '2026-06-07'
     * });
     * ```
     */
    async update(id, data) {
        const result = await this.bookingService.update(id, data);
        // TODO: Add event emission when 'booking' domain is added to pers-shared
        // this.events?.emitSuccess({ domain: 'booking', type: 'booking_updated', ... });
        return result;
    }
    /**
     * Delete a booking
     *
     * @param id - Booking UUID
     * @returns Promise resolving to success status
     * @throws {PersApiError} When booking not found
     */
    async delete(id) {
        const result = await this.bookingService.delete(id);
        // TODO: Add event emission when 'booking' domain is added to pers-shared
        // this.events?.emitSuccess({ domain: 'booking', type: 'booking_deleted', ... });
        return result;
    }
    /**
     * Check if a user has a valid (active or future) booking
     *
     * Useful for eligibility checks on redemptions that require a valid booking.
     *
     * @param userId - User ID to check
     * @param options - Validation options (status filter, business filter)
     * @returns Promise resolving to true if user has valid booking
     *
     * @example Eligibility Check
     * ```typescript
     * const hasBooking = await sdk.bookings.hasValidBooking('user-123');
     *
     * if (!hasBooking) {
     *   console.log('User must have an active or upcoming booking');
     * }
     *
     * // Check for active booking only
     * const hasActive = await sdk.bookings.hasValidBooking('user-123', { status: 'active' });
     *
     * // Check for booking at specific business
     * const hasHotelBooking = await sdk.bookings.hasValidBooking('user-123', {
     *   businessId: 'hotel-123'
     * });
     * ```
     */
    async hasValidBooking(userId, options) {
        return this.bookingService.hasValidBooking(userId, options);
    }
    /**
     * Get the booking service for advanced operations
     *
     * @returns BookingService instance with full API access
     */
    getBookingService() {
        return this.bookingService;
    }
}

/**
 * @fileoverview PERS SDK - Platform-agnostic TypeScript SDK with High-Level Managers
 *
 * @module @explorins/pers-sdk
 * @version 1.6.4
 * @author Explorins
 * @license MIT
 */
/**
 * PERS SDK - Main SDK class with domain managers
 *
 * Main SDK interface providing clean, high-level managers for common operations
 * while maintaining full access to the underlying API client and domain services.
 *
 * The SDK follows a layered architecture:
 * - **Managers**: High-level, intuitive APIs for common operations
 * - **Domain Services**: Full-featured access for advanced use cases
 * - **API Client**: Direct REST API access for custom operations
 *
 * @group Core
 * @category SDK
 *
 * @example Basic Setup
 * ```typescript
 * import { PersSDK } from '@explorins/pers-sdk';
 * import { BrowserFetchClientAdapter } from '@explorins/pers-sdk/platform-adapters';
 *
 * const sdk = new PersSDK(new BrowserFetchClientAdapter(), {
 *   environment: 'production',
 *   apiProjectKey: 'your-project-key'
 * });
 * ```
 *
 * @example Authentication Flow
 * ```typescript
 * // Login with external JWT
 * await sdk.auth.loginWithToken(firebaseJWT, 'user');
 *
 * // Check authentication
 * if (await sdk.auth.isAuthenticated()) {
 *   const user = await sdk.auth.getCurrentUser();
 *   console.log('Welcome,', user.name);
 * }
 * ```
 *
 * @example Business Operations
 * ```typescript
 * // Get active businesses
 * const businesses = await sdk.businesses.getActiveBusinesses();
 *
 * // Get business details
 * const business = await sdk.businesses.getBusinessById(businessId);
 * ```
 *
 * @example Token Operations
 * ```typescript
 * // Get user's token balances
 * const tokens = await sdk.tokens.getTokens();
 *
 * // Get active credit token
 * const creditToken = await sdk.tokens.getActiveCreditToken();
 * ```
 *
 * @since 1.3.0 - Manager pattern architecture
 */
class PersSDK {
    /**
     * Creates a new PERS SDK instance
     *
     * Initializes all domain managers and sets up the API client with the provided
     * HTTP client adapter and configuration.
     *
     * @param httpClient - Platform-specific HTTP client implementation
     * @param config - SDK configuration options
     *
     * @example Browser Setup
     * ```typescript
     * import { BrowserFetchClientAdapter } from '@explorins/pers-sdk/platform-adapters';
     *
     * const sdk = new PersSDK(new BrowserFetchClientAdapter(), {
     *   environment: 'production',
     *   apiProjectKey: 'your-project-key'
     * });
     * ```
     *
     * @example Node.js Setup
     * ```typescript
     * import { NodeHttpClientAdapter } from '@explorins/pers-sdk/platform-adapters';
     *
     * const sdk = new PersSDK(new NodeHttpClientAdapter(), {
     *   environment: 'production',
     *   apiProjectKey: 'your-project-key',
     *   baseUrl: 'https://api.yourpers.com'
     * });
     * ```
     *
     * @example Angular Setup
     * ```typescript
     * import { AngularHttpClientAdapter } from '@explorins/pers-sdk/platform-adapters';
     *
     * constructor(private http: HttpClient) {
     *   this.sdk = new PersSDK(new AngularHttpClientAdapter(this.http), {
     *     environment: 'production',
     *     apiProjectKey: 'your-project-key'
     *   });
     * }
     * ```
     */
    constructor(httpClient, config) {
        this.apiClient = new PersApiClient(httpClient, config);
        // Initialize event emitter and wire to API client for error events
        this._events = new PersEventEmitter();
        this.apiClient.setEvents(this._events);
        // Auto-connect to wallet events on successful login (if enabled)
        this.setupWalletEventsAutoConnect();
        // Auto-restore session from stored tokens (if enabled, default: true)
        this.setupAutoRestoreSession();
        // Dev-mode tracking validation (helps developers ensure proper attribution)
        this.validateTrackingInDev();
    }
    /**
     * Validate tracking configuration in development mode
     * @internal
     */
    validateTrackingInDev() {
        // Only run in development (check common env vars)
        const isDev = (typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'development') ||
            (typeof window !== 'undefined' && window.__DEV__);
        if (!isDev)
            return;
        // Delay check to allow time for setDataSource/setPlatform calls
        setTimeout(() => {
            const result = this.apiClient.validateTracking();
            if (!result.isValid) {
                console.warn('[PERS SDK] Tracking configuration warnings:');
                result.warnings.forEach(w => console.warn(`  ⚠️ ${w}`));
                console.warn('[PERS SDK] Call sdk.setDataSource() and sdk.setPlatform() for better analytics.');
            }
        }, 2000);
    }
    /**
     * Setup automatic session restoration from stored tokens
     * @internal
     */
    setupAutoRestoreSession() {
        const config = this.apiClient.getConfig();
        // Check if autoRestoreSession is enabled (default: true)
        if (config.autoRestoreSession === false) {
            return;
        }
        // Fire-and-forget: restore session asynchronously
        // Events will be emitted on success/failure
        this.restoreSession().catch(() => {
            // Error already emitted via session_restoration_failed event
        });
    }
    /**
     * Setup auto-connect for wallet events on authentication
     * @internal
     */
    setupWalletEventsAutoConnect() {
        const config = this.apiClient.getConfig();
        // Check if captureWalletEvents is enabled (default: true)
        if (config.captureWalletEvents === false) {
            return;
        }
        // Listen for login success events (both fresh login and session restoration)
        this._events.subscribe((event) => {
            if (event.level === 'success' && (event.type === 'login_success' || event.type === 'session_restored')) {
                // Auto-connect and subscribe to wallet events (fire and forget, log errors)
                this.connectWalletEvents().catch((err) => {
                    console.warn('[PersSDK] Failed to auto-connect wallet events:', err.message);
                });
            }
            // Disconnect on logout
            if (event.level === 'success' && event.type === 'logout_success') {
                this.walletEvents.disconnect();
            }
        });
    }
    /**
     * Connect to wallet events and auto-subscribe based on auth type
     *
     * Connects to the WebSocket relay and automatically subscribes to relevant
     * blockchain events based on the current authentication type:
     * - **USER**: Subscribes to all user's wallets
     * - **BUSINESS**: Subscribes to all business's wallets
     * - **TENANT**: Subscribes to all chains where tokens are deployed
     *
     * This method is called automatically on login when `captureWalletEvents` is enabled.
     * Call manually if you need to reconnect or refresh subscriptions.
     *
     * @example Manual connection
     * ```typescript
     * await sdk.connectWalletEvents();
     * ```
     */
    async connectWalletEvents() {
        await this.walletEvents.connect();
        // Get authType from auth provider (where it's stored during login)
        const authProvider = this.apiClient.getConfig().authProvider;
        const authType = authProvider?.getAuthType ? await authProvider.getAuthType() : undefined;
        switch (authType) {
            case AccountOwnerType.USER: {
                const user = await this.users.getCurrentUser();
                const wallets = user.wallets?.map(w => ({ address: w.address, chainId: w.chainId })) || [];
                if (wallets.length > 0) {
                    await this.walletEvents.subscribeWallets(wallets);
                }
                break;
            }
            case AccountOwnerType.BUSINESS: {
                const business = await this.auth.getCurrentBusiness();
                const wallets = business.wallets?.map(w => ({ address: w.address, chainId: w.chainId })) || [];
                if (wallets.length > 0) {
                    await this.walletEvents.subscribeWallets(wallets);
                }
                break;
            }
            case AccountOwnerType.TENANT: {
                const tokens = await this.tokens.getTokens();
                const chains = [...new Set(tokens.data.map(t => t.chainId))];
                if (chains.length > 0) {
                    await this.walletEvents.subscribeChains(chains);
                }
                break;
            }
        }
    }
    /**
     * Restore user session from stored tokens
     *
     * **Note:** This method is called automatically on SDK initialization when
     * `autoRestoreSession` is enabled (default: true). You only need to call this
     * manually if you disabled auto-restore or need to force a session refresh.
     *
     * Validates stored tokens and fetches user data to restore the session.
     * Emits `session_restored` event on success, `session_restoration_failed` on error.
     *
     * **Important:** Only works for USER and BUSINESS accounts. Admin/tenant
     * accounts don't support user data fetching, so this will return null for them
     * (though their tokens remain valid for API calls).
     *
     * @returns Promise resolving to User data if session restored, null if no session or admin account
     * @throws {PersError} If token validation or user fetch fails
     *
     * @example
     * ```typescript
     * // Manual restore (only needed if autoRestoreSession: false)
     * const user = await sdk.restoreSession();
     * if (user) {
     *   console.log('Welcome back,', user.name);
     * }
     * ```
     */
    async restoreSession() {
        const hasToken = await this.auth.hasValidAuth();
        if (!hasToken) {
            return null;
        }
        // Check auth type - only restore session for user and business accounts
        const authProvider = this.apiClient.getConfig().authProvider;
        const authType = authProvider?.getAuthType ? await authProvider.getAuthType() : AccountOwnerType.USER;
        if (authType === AccountOwnerType.TENANT) {
            // Ensure token is valid (refresh if expired) before fetching admin
            try {
                await this.auth.ensureValidToken();
                // Fetch current admin data
                const adminData = await this.auth.getCurrentAdmin();
                // Emit session restored for tenant admin
                this._events.emitSuccess({
                    type: 'session_restored',
                    domain: 'authentication',
                    userMessage: 'Admin session restored successfully',
                    details: { admin: adminData }
                });
                return null;
            }
            catch (error) {
                await this.auth.clearAuth();
                this._events.emitError({
                    type: 'session_restoration_failed',
                    domain: 'authentication',
                    userMessage: 'Admin session restoration failed',
                    code: 'SESSION_INVALID',
                    details: { error }
                });
                throw error;
            }
        }
        try {
            // Ensure token is valid (refresh if expired) before fetching user
            await this.auth.ensureValidToken();
            // For BUSINESS auth, only fetch business context (no access to /users/me)
            if (authType === AccountOwnerType.BUSINESS) {
                const businessData = await this.auth.getCurrentBusiness();
                // Emit event with business data (no user data for business auth)
                this._events.emitSuccess({
                    type: 'session_restored',
                    domain: 'authentication',
                    userMessage: 'Business session restored successfully',
                    details: { business: businessData }
                });
                return null; // No user data for business auth
            }
            // For USER auth, fetch user data
            const userData = await this.auth.getCurrentUser();
            // Emit event with user data
            this._events.emitSuccess({
                type: 'session_restored',
                domain: 'authentication',
                userMessage: 'Session restored successfully',
                details: { user: userData }
            });
            return userData;
        }
        catch (error) {
            // Tokens exist but are invalid (expired, revoked, etc.)
            await this.auth.clearAuth();
            // Emit restoration failed event
            this._events.emitError({
                type: 'session_restoration_failed',
                domain: 'authentication',
                userMessage: 'Session restoration failed',
                code: 'SESSION_INVALID',
                details: { error }
            });
            throw error;
        }
    }
    /**
     * Event emitter - Subscribe to SDK-wide events
     *
     * Provides a platform-agnostic event system for subscribing to transaction,
     * authentication, campaign, and system events. Use this to display
     * notifications, update UI, or trigger side effects in your application.
     *
     * All events have a `userMessage` field ready for display.
     *
     * @returns PersEventEmitter instance
     *
     * @example Basic Usage - Show All Notifications
     * ```typescript
     * const unsubscribe = sdk.events.subscribe((event) => {
     *   // userMessage is always present and UI-ready
     *   showNotification(event.userMessage, event.level);
     * });
     *
     * // Later: cleanup
     * unsubscribe();
     * ```
     *
     * @example Filter by Domain and Level
     * ```typescript
     * sdk.events.subscribe((event) => {
     *   if (event.level === 'success' && event.domain === 'transaction') {
     *     playSuccessSound();
     *     confetti();
     *   }
     *
     *   if (event.level === 'error') {
     *     logToSentry(event);
     *   }
     * });
     * ```
     *
     * @example One-time Event
     * ```typescript
     * // Auto-unsubscribe after first event
     * sdk.events.once((event) => {
     *   console.log('First event received:', event.type);
     * });
     * ```
     *
     * @see {@link PersEventEmitter} for detailed documentation
     */
    get events() {
        return this._events;
    }
    /**
     * Authentication manager - High-level authentication operations
     *
     * Provides methods for user login, logout, token management, and authentication
     * status checking. Supports both user and admin authentication flows.
     *
     * @returns AuthManager instance
     *
     * @example Basic Authentication
     * ```typescript
     * // Login with external JWT (Firebase, Auth0, etc.)
     * await sdk.auth.loginWithToken(firebaseJWT, 'user');
     *
     * // Check authentication status
     * if (await sdk.auth.isAuthenticated()) {
     *   const user = await sdk.auth.getCurrentUser();
     *   console.log('Welcome,', user.name);
     * }
     *
     * // Logout
     * await sdk.auth.clearAuth();
     * ```
     *
     * @example Admin Authentication
     * ```typescript
     * // Admin login
     * await sdk.auth.loginAdmin(adminJWT);
     *
     * // Check if valid auth exists (synchronous)
     * if (sdk.auth.hasValidAuth()) {
     *   console.log('Authentication tokens found');
     * }
     * ```
     *
     * @see {@link AuthManager} for detailed documentation
     */
    get auth() {
        if (!this._auth) {
            this._auth = new AuthManager(this.apiClient, this._events);
        }
        return this._auth;
    }
    /**
     * User manager - High-level user operations
     *
     * @example
     * ```typescript
     * const user = await sdk.user.getCurrentUser();
     * await sdk.user.updateCurrentUser(userData);
     * const users = await sdk.user.getAllUsersPublic();
     * ```
     */
    get users() {
        if (!this._users) {
            this._users = new UserManager(this.apiClient, this._events);
        }
        return this._users;
    }
    /**
     * User Status manager - High-level user status operations
     *
     * @example
     * ```typescript
     * const statusTypes = await sdk.userStatus.getUserStatusTypes();
     * const earnedStatus = await sdk.userStatus.getEarnedUserStatus();
     * await sdk.userStatus.createUserStatusType(statusData);
     * ```
     */
    get userStatus() {
        if (!this._userStatus) {
            this._userStatus = new UserStatusManager(this.apiClient);
        }
        return this._userStatus;
    }
    /**
     * Token manager - High-level token operations
     *
     * @example
     * ```typescript
     * const tokens = await sdk.tokens.getTokens();
     * const creditToken = await sdk.tokens.getActiveCreditToken();
     * const rewards = await sdk.tokens.getRewardTokens();
     * ```
     */
    get tokens() {
        if (!this._tokens) {
            this._tokens = new TokenManager(this.apiClient, this._events);
        }
        return this._tokens;
    }
    /**
     * Business manager - High-level business operations
     *
     * @example
     * ```typescript
     * const businesses = await sdk.business.getActiveBusinesses();
     * const business = await sdk.business.getBusinessById(id);
     * const types = await sdk.business.getBusinessTypes();
     * ```
     */
    get businesses() {
        if (!this._businesses) {
            this._businesses = new BusinessManager(this.apiClient, this._events);
        }
        return this._businesses;
    }
    /**
     * Campaign manager - High-level campaign operations
     *
     * @example
     * ```typescript
     * const campaigns = await sdk.campaigns.getActiveCampaigns();
     * await sdk.campaigns.claimCampaign(claimData);
     * const claims = await sdk.campaigns.getUserClaims();
     * ```
     */
    get campaigns() {
        if (!this._campaigns) {
            this._campaigns = new CampaignManager(this.apiClient, this._events);
        }
        return this._campaigns;
    }
    /**
     * Redemption manager - High-level redemption operations
     *
     * @example
     * ```typescript
     * const redemptions = await sdk.redemptions.getActiveRedemptions();
     * await sdk.redemptions.redeem(redemptionId);
     * const history = await sdk.redemptions.getUserRedemptions();
     * ```
     */
    get redemptions() {
        if (!this._redemptions) {
            this._redemptions = new RedemptionManager(this.apiClient, this._events);
        }
        return this._redemptions;
    }
    /**
     * Transaction manager - High-level transaction operations
     *
     * @example
     * ```typescript
     * const transaction = await sdk.transactions.getTransactionById(id);
     * await sdk.transactions.createTransaction(txData);
     * const history = await sdk.transactions.getUserTransactionHistory('all');
     * ```
     */
    get transactions() {
        if (!this._transactions) {
            this._transactions = new TransactionManager(this.apiClient, this._events);
        }
        return this._transactions;
    }
    /**
     * Purchase manager - High-level purchase operations
     *
     * @example
     * ```typescript
     * const intent = await sdk.purchases.createPaymentIntent(100, 'usd', 'email@example.com', 'Purchase');
     * const tokens = await sdk.purchases.getActivePurchaseTokens();
     * const purchases = await sdk.purchases.getAllUserPurchases();
     * ```
     */
    get purchases() {
        if (!this._purchases) {
            this._purchases = new PurchaseManager(this.apiClient);
        }
        return this._purchases;
    }
    /**
     * File manager - High-level file operations
     *
     * @example
     * ```typescript
     * const uploadUrl = await sdk.files.getSignedPutUrl('entity-123', 'token', 'jpg');
     * const accessUrl = await sdk.files.getSignedGetUrl('entity-123', 'token');
     * const optimizedUrl = await sdk.files.optimizeMedia(originalUrl, 800, 600);
     * ```
     */
    get files() {
        if (!this._files) {
            this._files = new FileManager(this.apiClient);
        }
        return this._files;
    }
    /**
     * Tenant manager - High-level tenant operations
     *
     * @example
     * ```typescript
     * const tenant = await sdk.tenant.getTenantInfo();
     * const config = await sdk.tenant.getClientConfig();
     * const admins = await sdk.tenant.getAdmins();
     * ```
     */
    get tenants() {
        if (!this._tenants) {
            this._tenants = new TenantManager(this.apiClient);
        }
        return this._tenants;
    }
    /**
     * API Key manager - High-level API key management operations (Admin Only)
     *
     * Provides methods for creating, listing, and revoking API keys for the tenant.
     * All operations require tenant admin authentication.
     *
     * @returns ApiKeyManager instance
     *
     * @example API Key Operations
     * ```typescript
     * // Create a JWT token for frontend authentication
     * const jwtToken = await sdk.apiKeys.createJwtToken('Frontend App');
     * console.log('Store this JWT securely:', jwtToken.key);
     *
     * // List all API keys
     * const apiKeys = await sdk.apiKeys.listApiKeys();
     *
     * // Revoke an old API key
     * await sdk.apiKeys.revokeApiKey('old-key-id');
     * ```
     */
    get apiKeys() {
        if (!this._apiKeys) {
            this._apiKeys = new ApiKeyManager(this.apiClient);
        }
        return this._apiKeys;
    }
    /**
     * Analytics manager - High-level analytics operations
     *
     * @example
     * ```typescript
     * const analytics = await sdk.analytics.getTransactionAnalytics(request);
     * ```
     */
    get analytics() {
        if (!this._analytics) {
            this._analytics = new AnalyticsManager(this.apiClient);
        }
        return this._analytics;
    }
    /**
     * Donation manager - High-level donation operations
     *
     * @example
     * ```typescript
     * const types = await sdk.donations.getDonationTypes();
     * ```
     */
    get donations() {
        if (!this._donations) {
            this._donations = new DonationManager(this.apiClient);
        }
        return this._donations;
    }
    /**
     * TriggerSource manager - High-level trigger source operations (Admin Only)
     *
     * Provides CRUD operations for managing trigger sources (QR codes, NFC tags,
     * GPS geofences, API webhooks). TriggerSources are standalone entities that
     * can be assigned to campaigns via the campaigns manager.
     *
     * @returns TriggerSourceManager instance
     *
     * @example TriggerSource Operations
     * ```typescript
     * // Create a QR code trigger source
     * const qrSource = await sdk.triggerSources.create({
     *   name: 'Store Entrance QR',
     *   type: 'QR_CODE',
     *   description: 'QR code at main entrance'
     * });
     *
     * // Get all trigger sources
     * const sources = await sdk.triggerSources.getAll();
     *
     * // Update trigger source
     * await sdk.triggerSources.update(sourceId, { name: 'Updated Name' });
     *
     * // Assign to campaign (via campaigns manager)
     * await sdk.campaigns.assignTriggerSource(campaignId, qrSource.id);
     * ```
     */
    get triggerSources() {
        if (!this._triggerSources) {
            this._triggerSources = new TriggerSourceManager(this.apiClient, this._events);
        }
        return this._triggerSources;
    }
    /**
     * Webhook manager - High-level webhook operations
     *
     * Provides methods for creating webhooks, triggering them programmatically,
     * and monitoring execution history. Supports async workflows with callbacks.
     *
     * @returns WebhookManager instance
     *
     * @example Webhook Operations
     * ```typescript
     * // Admin: Create a webhook
     * const webhook = await sdk.webhooks.create({
     *   name: 'Order Processing',
     *   targetUrl: 'https://n8n.example.com/webhook/orders',
     *   method: 'POST'
     * });
     *
     * // Trigger webhook with payload
     * const result = await sdk.webhooks.trigger(webhook.id, {
     *   orderId: 'order-123',
     *   action: 'created'
     * });
     *
     * // Trigger and wait for async workflow completion
     * const asyncResult = await sdk.webhooks.triggerAndWait(
     *   'ai-webhook',
     *   { prompt: 'Analyze this data' },
     *   30000  // Wait up to 30s
     * );
     * ```
     *
     * @see {@link WebhookManager} for detailed documentation
     */
    get webhooks() {
        if (!this._webhooks) {
            const projectKey = this.apiClient.getConfig().apiProjectKey || '';
            this._webhooks = new WebhookManager(this.apiClient, projectKey, this._events);
        }
        return this._webhooks;
    }
    /**
     * Custom Field Definition Manager - Manage tenant-specific custom fields
     *
     * Provides CRUD operations for custom field definitions that extend the built-in
     * user profile fields. Custom fields are tenant-specific and support validation.
     *
     * @returns CustomFieldDefinitionManager instance
     *
     * @example Basic Operations
     * ```typescript
     * // List all custom fields
     * const fields = await sdk.customFields.getDefinitions();
     *
     * // Create a new field
     * const field = await sdk.customFields.createDefinition({
     *   key: 'employee_id',
     *   label: 'Employee ID',
     *   fieldType: 'text',
     *   validation: { required: true }
     * });
     *
     * // Validate user data
     * const errors = sdk.customFields.validateUserData(formData, fields.data);
     * ```
     */
    get customFields() {
        if (!this._customFields) {
            this._customFields = new CustomFieldDefinitionManager(this.apiClient, this._events);
        }
        return this._customFields;
    }
    /**
     * Booking manager - High-level booking operations
     *
     * Provides methods for managing user bookings (hotel stays, reservations, etc.)
     * Used for eligibility checks on redemptions that require valid bookings.
     *
     * @returns BookingManager instance
     *
     * @example List User Bookings
     * ```typescript
     * // Get all bookings for a user
     * const bookings = await sdk.bookings.getByUser('user-123');
     *
     * // Filter by status
     * const activeBookings = await sdk.bookings.getAll({ status: 'active' });
     * ```
     *
     * @example Create Booking
     * ```typescript
     * const booking = await sdk.bookings.create({
     *   userId: 'user-123',
     *   locationName: 'Grand Hotel',
     *   checkInDate: '2026-06-01',
     *   checkOutDate: '2026-06-05'
     * });
     * ```
     *
     * @example Eligibility Check
     * ```typescript
     * const hasBooking = await sdk.bookings.hasValidBooking('user-123');
     * if (hasBooking) {
     *   // User is eligible for booking-required redemption
     * }
     * ```
     */
    get bookings() {
        if (!this._bookings) {
            this._bookings = new BookingManager(this.apiClient, this._events);
        }
        return this._bookings;
    }
    /**
     * Wallet Events Manager - Real-time blockchain events for user's wallets
     *
     * Provides real-time WebSocket connection to receive blockchain events
     * for the user's wallets (transfers, approvals, NFT mints, etc.).
     *
     * Events are also routed through `sdk.events` for unified event handling.
     *
     * **Important:** Requires `walletEventsWsUrl` configuration and authentication.
     *
     * @returns WalletEventsManager instance
     *
     * @example Basic Usage
     * ```typescript
     * // Configure SDK with events URL
     * sdk.configureWalletEvents({ wsUrl: 'wss://events.pers.ninja' });
     *
     * // Connect after authentication
     * await sdk.auth.loginWithToken(jwt, 'user');
     * await sdk.walletEvents.connect();
     *
     * // Listen for token transfers
     * sdk.walletEvents.on('Transfer', (event) => {
     *   if (event.data.to === myWallet) {
     *     showNotification(`Received ${event.data.value} tokens!`);
     *   }
     * });
     * ```
     *
     * @example Unified Event Stream
     * ```typescript
     * // Wallet events also flow through sdk.events
     * sdk.events.subscribe((event) => {
     *   if (event.domain === 'wallet') {
     *     console.log('Wallet event:', event.type);
     *   }
     * });
     * ```
     *
     * @see {@link WalletEventsManager} for detailed documentation
     */
    get walletEvents() {
        if (!this._walletEvents) {
            this._walletEvents = new WalletEventsManager(this.apiClient, this._events, this.walletEventsConfig);
        }
        return this._walletEvents;
    }
    /**
     * Configure wallet events (call before accessing walletEvents)
     *
     * @param config - Events configuration including wsUrl
     */
    configureWalletEvents(config) {
        this.walletEventsConfig = config;
        // Reset manager so it picks up new config
        this._walletEvents = undefined;
    }
    // ==========================================
    // DATA SOURCE TRACKING
    // ==========================================
    /**
     * Set data source tracking for analytics attribution
     *
     * Use this to track where users and actions originate from.
     * Web apps typically only need medium/campaign/source (channel is auto-detected).
     * Native apps must set channel: 'mobile'.
     *
     * @param dataSource - Data source configuration
     *
     * @example Web app with UTM params
     * ```typescript
     * sdk.setDataSource({
     *   medium: 'email',
     *   campaign: 'summer_2026',
     *   source: 'mailchimp',
     * });
     * ```
     *
     * @example Native app
     * ```typescript
     * sdk.setDataSource({
     *   channel: 'mobile',
     *   medium: 'push',
     *   campaign: 'retention_30d',
     * });
     * ```
     */
    setDataSource(dataSource) {
        this.apiClient.setDataSource(dataSource);
    }
    /**
     * Update data source (merges with existing values)
     *
     * @param dataSource - Partial data source to merge
     *
     * @example Update campaign after deep link
     * ```typescript
     * sdk.updateDataSource({ campaign: 'new_campaign' });
     * ```
     */
    updateDataSource(dataSource) {
        this.apiClient.updateDataSource(dataSource);
    }
    /**
     * Set platform info for device analytics
     *
     * Native apps should call this on initialization with device info.
     * Web apps can optionally send browser/OS info.
     *
     * @param platform - Platform information
     *
     * @example Native app
     * ```typescript
     * sdk.setPlatform({
     *   os: 'iOS',
     *   osVersion: '17.4',
     *   app: 'MyApp',
     *   appVersion: '2.3.1',
     *   deviceType: 'phone',
     * });
     * ```
     */
    setPlatform(platform) {
        this.apiClient.setPlatform(platform);
    }
    /**
     * Gets the API client for direct PERS API requests
     *
     * Use this for advanced operations not covered by the managers.
     * The returned client handles authentication, token refresh, and error handling automatically.
     *
     * @returns Configured PersApiClient instance
     *
     * @example
     * ```typescript
     * const apiClient = sdk.api();
     * const customData = await apiClient.get<CustomType>('/custom-endpoint');
     * await apiClient.post('/custom-endpoint', customData);
     * ```
     */
    api() {
        return this.apiClient;
    }
    /**
     * Checks if SDK is configured for production environment
     *
     * @returns True if environment is 'production', false otherwise
     */
    isProduction() {
        return this.apiClient.getConfig().environment === 'production';
    }
    /**
     * Checks if SDK has an authentication provider configured
     *
     * Note: This only checks if an auth provider exists, not if tokens are valid.
     * For token validity checking, use auth.hasValidAuth() (async) or auth.isAuthenticated() (async).
     *
     * @returns True if authentication provider is configured, false otherwise
     */
    isInitialized() {
        return !!this.apiClient.getConfig().authProvider;
    }
}
/**
 * Factory function for creating PERS SDK
 *
 * @param httpClient Platform-specific HTTP client implementation
 * @param config SDK configuration options
 * @returns PERS SDK instance
 */
function createPersSDK(httpClient, config) {
    return new PersSDK(httpClient, config);
}

export { createPersEventsClient as $, AuthStatus as A, BusinessManager as B, CampaignManager as C, DefaultAuthProvider as D, TriggerSourceManager as E, FATAL_AUTH_CODES as F, WebhookManager as G, WalletEventsManager as H, IndexedDBTokenStorage as I, CustomFieldDefinitionManager as J, BookingManager as K, LocalStorageTokenStorage as L, MemoryTokenStorage as M, FileApi as N, FileService as O, PersSDK as P, ApiKeyApi as Q, RedemptionManager as R, SDK_NAME as S, TrackingManager as T, UserManager as U, WebhookApi as V, WebDPoPCryptoProvider as W, WebhookService as X, BookingApi as Y, BookingService as Z, PersEventsClient as _, AuthTokenManager as a, AUTH_STORAGE_KEYS as b, createPersSDK as c, DPOP_STORAGE_KEYS as d, SDK_VERSION as e, SDK_USER_AGENT as f, PersApiClient as g, DEFAULT_PERS_CONFIG as h, buildApiRoot as i, buildWalletEventsWsUrl as j, StaticJwtAuthProvider as k, AuthApi as l, mergeWithDefaults as m, isFatalAuthErrorInMessage as n, AuthService as o, DPoPManager as p, PersEventEmitter as q, AuthManager as r, UserStatusManager as s, TokenManager as t, TransactionManager as u, PurchaseManager as v, FileManager as w, ApiKeyManager as x, AnalyticsManager as y, DonationManager as z };
//# sourceMappingURL=pers-sdk-BEF_pjl0.js.map
